import Bluebird from 'bluebird';
import equal from 'fast-deep-equal';
import escapeRegExp from 'lodash.escaperegexp';
import { action, computed, makeObservable, observable, override, runInAction, toJS } from 'mobx';
import moment from 'moment';
import { stringify as toQueryStringParams } from 'query-string';
import { v4 as uuidgen } from 'uuid';
import { NotesSortOptionValue } from '../models';
import { getDisplayName } from '../models/NameUtils';
import * as VmUtils from './Utils';
import * as Api from './sdk';
import {
	BaseObservablePageCollectionController,
	FilteredPageCollectionController,
	ObservableCollection,
	ObservablePageCollectionController,
	PagedViewModel,
} from './viewModels/Collections';
import {
	AttachmentsToBeUploadedViewModel,
	AttachmentsViewModel,
	FileAttachmentViewModel,
	FileWithExtensionsType,
} from './viewModels/Files';
import { TemplatesViewModel } from './viewModels/Templates';
import {
	ObservablePageCollectionControllerOld,
	UserSessionContext,
	UserViewModel,
	ViewModel,
	getEmptyPageControllerResolvedPromise,
} from './viewModels/index';

export interface IRichContentEditorStateAttributes {
	document?: {
		characterCount?: number;
		wordCount?: number;
	};
	selection?: {
		characterCount?: number;
		wordCount?: number;
	};
}

export interface IRichContentEditorState {
	attributes?: IRichContentEditorStateAttributes;
	getPlainTextPreview(truncate?: boolean, terminator?: string): string;
	getRawRichTextContent(): Api.IRawRichTextContentState;
	hasContent(): boolean;
	reset(): void;
}

export class UserWithStatsViewModel extends UserViewModel<Api.IUserWithStats> {
	constructor(userSession: UserSessionContext, user: Api.IUserWithStats) {
		super(userSession, user);

		makeObservable(this, {
			stats: computed,
			loadUserStats: action,
			updateTaggingGameActivities: action,
		});
	}

	public get stats() {
		return this.mUser.stats;
	}

	public loadUserStats = () => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IUserStats>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUserStats>) => {
					runInAction(() => {
						if (opResult.success) {
							const userStats: Api.IUserWithStats = {
								...(this.mUser || {}),
								stats: opResult.value,
							};
							this.mSetUser(userStats);
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserStats>(
					this.composeApiUrl({ urlPath: 'user/stats' }),
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public updateTaggingGameActivities = (taggingGameActivities: Api.ITaggingGameActivities) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IUserStats>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IUserStats>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mUser.stats = result.value;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserStats>(
					'user/stats/taggingGame',
					'POST',
					taggingGameActivities,
					onFinish,
					onFinish
				);
			});
		}
		return null;
	};
}

export type AutoCompleteResultFilter<T> = (result: T, query: string) => boolean;

export interface IAutoCompleteOptions<T> {
	apiParams?: Api.IDictionary;
	apiPath: string;
	inputDelay?: number;
	localFilter?: AutoCompleteResultFilter<T>;
	pageSize?: number;
	searchParamName: string;
}

/** Encapsulates logic needed to do autocomplete searches. */
export class AutoCompleteViewModel<T = any> extends Api.ImpersonationBroker {
	protected mSearchQuery: string;
	private mTimeoutHandle: any;
	protected mSearchPromise: Bluebird<Api.IOperationResult<Api.IPagedCollection<T>>>;
	protected mSearchResults: T[];
	protected mTotalCount: number;
	protected mHasMorePages: boolean;
	protected mOptions: IAutoCompleteOptions<T>;
	protected mUserSession: UserSessionContext;
	protected mDefaultApiParams: Api.IDictionary<any>;

	/**
	 * @param dataSource Provides the specific models
	 * @param filter Allows for a final filter before updating
	 */
	constructor(userSession: UserSessionContext, options: IAutoCompleteOptions<T>) {
		super();

		makeObservable<
			AutoCompleteViewModel<T>,
			'mSearchQuery' | 'mTimeoutHandle' | 'mSearchPromise' | 'mSearchResults' | 'mTotalCount' | 'mHasMorePages'
		>(this, {
			mSearchQuery: observable,
			mTimeoutHandle: observable.ref,
			mSearchPromise: observable.ref,
			mSearchResults: observable.ref,
			mTotalCount: observable,
			mHasMorePages: observable,
			totalCount: computed,
			searchResults: computed,
			isFetchingResults: computed,
			isBusy: computed,
			reset: action,
			addParam: action,
			setSearchQuery: action,
		});

		this.mGetResultsWithQuery = this.mGetResultsWithQuery.bind(this);
		this.mGetOpResult = this.mGetOpResult.bind(this);
		this.mReset = this.mReset.bind(this);
		this.mOptions = options;
		this.mUserSession = userSession;
		this.mDefaultApiParams = {
			enforcePageSize: true,
			...(options.apiParams || {}),
			pageSize: options.pageSize > 0 ? options.pageSize : 5,
		};
		this.mReset();
	}

	public get totalCount() {
		return this.mTotalCount || 0;
	}

	public get searchResults() {
		const results = this.mSearchResults || [];
		if (this.canFilterLocally) {
			return results.filter(x => {
				return this.mOptions.localFilter(x, this.mSearchQuery);
			});
		}
		return results;
	}

	public get isFetchingResults() {
		return !!this.mSearchPromise;
	}

	public get isBusy() {
		return this.isFetchingResults || !!this.mTimeoutHandle;
	}

	protected get canFilterLocally() {
		return !this.mHasMorePages && this.mOptions.localFilter && !!this.mSearchQuery;
	}

	public reset = () => {
		this.mReset();
	};

	public addParam = (param: Api.IDictionary) => {
		this.mDefaultApiParams = {
			...this.mDefaultApiParams,
			...param,
		};
	};

	public setSearchQuery = (
		query: string,
		delay = this.mOptions.inputDelay >= 0 ? this.mOptions.inputDelay : 0,
		searchWithNoQuery?: boolean,
		customParams?: Api.IDictionary<any>
	) => {
		if (searchWithNoQuery) {
			this.mGetResultsWithQuery('');
			return;
		}

		const trimmedQuery = (query || '').trim();
		if (trimmedQuery) {
			// check to see if we can filter locally
			if (!this.mTimeoutHandle && !this.mSearchPromise && this.canFilterLocally) {
				// Is this a continuation?
				const beginsWithRegExp = new RegExp(`^${escapeRegExp(this.mSearchQuery)}`, 'i');
				if (beginsWithRegExp.test(trimmedQuery)) {
					// just set mSearchQuery and let the computed getter filter locally
					this.mSearchQuery = trimmedQuery;
					return;
				}
			}

			this.mClearTimeoutHandle();
			this.mCancelCurrentSearch();

			const performSearch = () => {
				this.mGetResultsWithQuery(trimmedQuery, customParams);
			};

			if (delay > 0) {
				this.mTimeoutHandle = setTimeout(() => {
					runInAction(() => {
						this.mClearTimeoutHandle();
						performSearch();
					});
				}, delay);
			} else {
				performSearch();
			}
		} else {
			this.mReset();
		}
	};

	protected mCancelCurrentSearch = () => {
		if (this.mSearchPromise) {
			this.mSearchPromise.cancel();
			this.mSearchPromise = null;
		}
	};

	protected mClearTimeoutHandle = () => {
		if (this.mTimeoutHandle) {
			clearTimeout(this.mTimeoutHandle);
			this.mTimeoutHandle = null;
		}
	};

	protected mGetResultsWithQuery(query: string, customParams?: Api.IDictionary<any>) {
		const promise = new Bluebird<Api.IOperationResult<Api.IPagedCollection<T>>>(async (resolve, reject, onCancel) => {
			let canceled = false;
			if (onCancel) {
				onCancel(() => {
					canceled = true;
				});
			}

			const computedParams = {
				...this.mDefaultApiParams,
				...(customParams || {}),
				[this.mOptions.searchParamName]: query || '',
			};
			VmUtils.removeEmptyKvpFromObject(computedParams);

			try {
				const opResult = await this.mGetOpResult(computedParams);
				runInAction(() => {
					if (!canceled) {
						this.mSearchPromise = null;
						this.mClearTimeoutHandle();
						if (opResult.success) {
							this.mSearchQuery = query;
							this.mHasMorePages = !!opResult.value.pageToken;
							this.mTotalCount = opResult.value.totalCount;

							const isValue = typeof opResult?.value === 'object';
							const isValuesInValue = typeof opResult?.value?.values === 'object';

							let data = null;

							if (isValue && !isValuesInValue) {
								data = opResult?.value;
							}

							if (isValue && isValuesInValue) {
								data = opResult?.value?.values;
							}

							this.mSearchResults = data as [];

							resolve(opResult);
						} else {
							this.mReset();
							reject(opResult);
						}
					}
				});
			} catch (err) {
				if (!canceled) {
					runInAction(() => {
						this.mReset();
						reject(Api.asApiError(err));
					});
				}
			}
		});

		this.mSearchPromise = promise;
		return promise;
	}

	protected mGetOpResult(computedParams?: Api.IDictionary<any>) {
		return this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IPagedCollection<T>>(
			this.composeApiUrl({ queryParams: computedParams, urlPath: this.mOptions.apiPath }),
			'GET'
		);
	}

	protected mReset() {
		this.mCancelCurrentSearch();
		this.mClearTimeoutHandle();
		this.mSearchQuery = null;
		this.mTotalCount = 0;
	}
}

export type ResourceAutoCompleteModel =
	| Api.IUser
	| Api.ICompany
	| Api.IContact
	| Api.IHandleAutoCompleteResult
	| Api.IFacebookPageLookupResult
	| string
	| Api.IAccountTag;

export enum ResourceAutoCompleteViewModelType {
	AccountTag = 'accountTag',
	Company = 'company',
	Contact = 'contact',
	Entity = 'entity',
	EventRegistrationSurvey = 'eventRegistrationSurvey',
	LineOfBusiness = 'lineOfBusiness',
	Manager = 'manager',
	PhoneCallCategory = 'phoneCallCategory',
	Role = 'role',
	Tag = 'tag',
	Team = 'team',
	User = 'user',
	Vertical = 'Vertical',
	VerticalV2 = 'VerticalV2',
	DailyActivityTeam = 'DailyActivityTeam',
	FacebookPages = 'facebookPages',
}

export interface IResourceAutoCompleteOptions<T extends ResourceAutoCompleteModel = ResourceAutoCompleteModel> {
	/**
	 * This filter is always applied to the entire collection of search results, not just after local filtering is
	 * triggered.
	 */
	filter?: AutoCompleteResultFilter<T>;
	inputDelay?: number;
	localFilter?: AutoCompleteResultFilter<T>;
	pageSize?: number;
	type: ResourceAutoCompleteViewModelType;
}

export class ResourceAutoCompleteViewModel<
	T extends ResourceAutoCompleteModel = ResourceAutoCompleteModel,
> extends AutoCompleteViewModel<T> {
	protected mExtendedOptions: IResourceAutoCompleteOptions<T>;
	protected mAllResults: T[];
	constructor(userSession: UserSessionContext, options: IResourceAutoCompleteOptions<T>) {
		let apiPath = `${options.type}/autocomplete`;

		if (options.type === ResourceAutoCompleteViewModelType.AccountTag) {
			apiPath = 'tag/autocomplete/v2';
		}
		let searchParamName = 'fragment';
		let apiParams: Api.IDictionary = null;

		if (
			options.type === ResourceAutoCompleteViewModelType.Contact ||
			options.type === ResourceAutoCompleteViewModelType.Entity
		) {
			apiParams = {
				searchFields: 'nameParts',
				expand: 'HouseholdMembers',
			};
		}

		if (
			options.type === ResourceAutoCompleteViewModelType.Tag ||
			options.type === ResourceAutoCompleteViewModelType.AccountTag
		) {
			searchParamName = 'query';
		}

		if (options.type === ResourceAutoCompleteViewModelType.Team) {
			apiPath = 'LeadServed/team/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.Entity) {
			apiPath = 'autocomplete/byHandle';
		}

		if (options.type === ResourceAutoCompleteViewModelType.LineOfBusiness) {
			apiPath = 'keyfact/LineOfBusiness';
		}

		if (options.type === ResourceAutoCompleteViewModelType.Manager) {
			apiPath = 'LeadServed/manager/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.PhoneCallCategory) {
			searchParamName = 'query';
			apiPath = 'phonecall/category';
		}

		if (options.type === ResourceAutoCompleteViewModelType.Role) {
			apiPath = 'LeadServed/role/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.Vertical) {
			apiPath = 'aida/vertical/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.DailyActivityTeam) {
			apiPath = 'aida/teamsRoles/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.VerticalV2) {
			apiPath = 'LeadServed/vertical/autocomplete';
		}

		if (options.type === ResourceAutoCompleteViewModelType.EventRegistrationSurvey) {
			searchParamName = 'query';
			apiPath = 'survey/eventRegistration/autocomplete';
		}

		const extendedOptions: IResourceAutoCompleteOptions<T> = { ...options };

		const autocompleteOptions: IAutoCompleteOptions<T> = {
			apiParams,
			apiPath,
			inputDelay: extendedOptions.inputDelay >= 0 ? extendedOptions.inputDelay : 150,
			localFilter: extendedOptions.localFilter,
			pageSize: options.pageSize || 5,
			searchParamName,
		};
		super(userSession, autocompleteOptions);
		makeObservable<ResourceAutoCompleteViewModel<T>, 'searchResults'>(this, {
			searchResults: override,
		});
		this.mExtendedOptions = extendedOptions;
		this.mGetOpResult = this.mGetOpResult.bind(this);
		this.mReset = this.mReset.bind(this);
	}

	public get searchResults() {
		const results = this.mSearchResults || [];
		if (this.mExtendedOptions.filter || this.canFilterLocally) {
			return results.filter(x => {
				let include = this.mExtendedOptions.filter ? this.mExtendedOptions.filter(x, this.mSearchQuery) : true;
				if (include && this.canFilterLocally) {
					include = this.mExtendedOptions.localFilter(x, this.mSearchQuery);
				}
				return include;
			});
		}
		return results;
	}

	protected mGetOpResult(computedParams?: Api.IDictionary<any>) {
		if (this.mExtendedOptions.type === ResourceAutoCompleteViewModelType.LineOfBusiness) {
			// coerce the flat list into a paged result
			return new Promise<Api.IOperationResult<Api.IPagedCollection<T>>>(resolve => {
				const onOpResultSuccess = (result: Api.IOperationResult<string[]>) => {
					this.mAllResults = result.value as T[];
					const query = computedParams?.[this.mOptions.searchParamName] || '';
					const filter = query ? new RegExp(`^${escapeRegExp(query)}`, 'igm') : null;
					const values = (
						filter
							? result.value.filter(x => {
									filter.lastIndex = 0;
									return filter.test(x);
								})
							: result.value
					) as T[];
					resolve({
						...result,
						value: {
							totalCount: values.length,
							values,
						},
					});
				};
				let opResult: Api.IOperationResult<string[]> = null;
				if (this.mAllResults) {
					opResult = {
						success: true,
						value: this.mAllResults as string[],
					};
					onOpResultSuccess(opResult);
				} else {
					this.mUserSession.webServiceHelper
						.callWebServiceAsync<string[]>(this.composeApiUrl({ urlPath: this.mOptions.apiPath }), 'GET')
						.then((result: Api.IOperationResult<string[]>) => {
							if (result.success) {
								onOpResultSuccess(result);
							} else {
								resolve(result as Api.IOperationResultNoValue);
							}
						});
				}
			});
		}
		return super.mGetOpResult(computedParams);
	}

	protected mReset() {
		super.mReset();
		this.mAllResults = null;
	}
}

export type ToastMessageDuraton = 'short' | 'long';
export type ToastMessageType = 'textMessage' | 'successMessage' | 'custom' | 'errorMessage' | 'warningMessage';

export interface IToastMessage {
	duration?: ToastMessageDuraton | number;
	linkTitle?: string;
	message?: string;
	onHide?(): void;
	onShow?(): void;
	type?: ToastMessageType;
}

export interface IErrorMessage {
	messages?: string[];
	onClose?(): void;
	title?: string;
}

export class ErrorMessageViewModel {
	public currentErrorMessage?: IErrorMessage;
	private queue: IErrorMessage[];

	constructor() {
		makeObservable<ErrorMessageViewModel, 'queue'>(this, {
			currentErrorMessage: observable.ref,
			queue: observable.ref,
			pushApiError: action,
			push: action,
			dismiss: action,
			reset: action,
		});

		this.queue = [];
	}

	public pushApiError = (error: any, title?: string, onClose?: () => void) => {
		if (!error) {
			return;
		}
		let errorMessage = 'Unexpected error occurred';
		if (typeof error === 'object' && 'systemMessage' in error) {
			errorMessage = error.systemMessage;
		}

		if (error) {
			this.mPush({
				messages: [errorMessage],
				onClose,
				title,
			});
		}
	};

	public catchPromiseError = <TPromiseResult = any, TPromise extends Promise<TPromiseResult> = Promise<TPromiseResult>>(
		promise: TPromise,
		onWillShow?: boolean | (() => boolean),
		title?: string,
		onClose?: () => void
	) => {
		if (promise) {
			promise.catch(e => {
				if (e && onWillShow !== false) {
					const error = Api.asApiError(e);
					if (onWillShow === null || onWillShow === undefined || (typeof onWillShow === 'function' && onWillShow())) {
						this.pushApiError(error, title, onClose);
					}
				}
			});
		}
		return promise;
	};

	public push = (errorMessage?: IErrorMessage) => {
		if (errorMessage) {
			this.mPush(errorMessage);
		}
	};

	public dismiss = () => {
		this.currentErrorMessage = undefined;
		this.showNext();
	};

	public reset = () => {
		this.currentErrorMessage = undefined;
		this.queue = [];
	};

	private mPush = (errorMessage: IErrorMessage) => {
		const queue = this.queue.slice();
		queue.push(errorMessage);
		this.queue = queue;
		this.tryShowNext();
	};

	private tryShowNext = () => {
		if (!this.currentErrorMessage) {
			this.dismiss();
		}
	};

	private showNext = () => {
		this.currentErrorMessage = this.pop();
	};

	private pop = (): IErrorMessage => {
		if (this.queue.length > 0) {
			// get the first message
			const first = this.queue[0];

			// remove it from the queue and update the queue ref
			const queue = this.queue.slice();
			queue.splice(0, 1);
			this.queue = queue;

			return first;
		}
		return null;
	};
}

export enum ESelectionState {
	All = 'all',
	Some = 'some',
	None = 'none',
}

export enum TimelineEventType {
	SentEmailEvent = 'SentEmailEvent',
	ReceivedEmailEvent = 'ReceivedEmailEvent',
	ViewedEmailEvent = 'ViewedEmailEvent',
	RepliedEmailEvent = 'RepliedEmailEvent',
	ExternalSentEmailEvent = 'ExternalSentEmailEvent',
	HtmlNewsletterEvent = 'HtmlNewsletterEvent',
	MeetingEvent = 'MeetingEvent',
	MeetingScheduledEvent = 'MeetingScheduledEvent',
	DealCreatedEvent = 'DealCreatedEvent',
	DealUpdatedEvent = 'DealUpdatedEvent',
	NoteEvent = 'NoteEvent',
	SkipLeadEvent = 'SkipLeadEvent',
	ActionItemEvent = 'ActionItemEvent',
	ConversationThreadEvent = 'ConversationThreadEvent',
	PhoneCallEvent = 'PhoneCallEvent',
	PhoneCallCompletedEvent = 'PhoneCallCompletedEvent',
	UntrackedPhoneCallEvent = 'UntrackedPhoneCallEvent',
	FollowUpEvent = 'FollowUpEvent',
	CancelledFollowUpEvent = 'CancelledFollowUpEvent',
	RescheduledFollowUpEvent = 'RescheduledFollowUpEvent',
	SatisfactionSurveyResponseEvent = 'SatisfactionSurveyResponseEvent',
	CustomSurveyResponseEvent = 'CustomSurveyResponseEvent',
	DonationSurveyResponseEvent = 'DonationSurveyResponseEvent',
	EventRegistrationSurveyResponseEvent = 'EventRegistrationSurveyResponseEvent',
	HandwrittenCardOrderEvent = 'HandwrittenCardOrderEvent',
	FileRequestSurveyResponseEvent = 'FileRequestSurveyResponseEvent',
	FileSendEvent = 'FileSendEvent',
	FileDownloadEvent = 'FileDownloadEvent',
	FileDeleteEvent = 'FileDeleteEvent',
}

export class TimelineEventViewModel<T extends Api.ITimelineEvent = Api.ITimelineEvent> extends ViewModel {
	protected mEvent: T;

	constructor(userSession: UserSessionContext, event: T) {
		super(userSession);

		makeObservable<TimelineEventViewModel, 'mEvent'>(this, {
			mEvent: observable,
			resourceId: computed,
			scheduledMeeting: computed,
			id: computed,
			timestamp: computed,
			type: computed,
			title: computed,
			originalCreator: computed,
		});

		this.loading = false;
		this.mEvent = event;
	}

	public get resourceId() {
		return this.mEvent.resourceId;
	}

	public get scheduledMeeting() {
		return this.mEvent.scheduledMeeting;
	}

	public get id() {
		return this.mEvent.id;
	}

	public get timestamp() {
		return this.mEvent.timestamp;
	}

	public get type(): Api.TimelineEventTypes {
		return this.mEvent._type as Api.TimelineEventTypes;
	}

	public get title() {
		return this.mEvent.title;
	}

	public get originalCreator() {
		return this.mEvent.originalCreator;
	}

	toJs = () => {
		return this.mEvent;
	};
}

export class ActionItemEventViewModel<
	T extends Api.IActionItemEvent = Api.IActionItemEvent,
> extends TimelineEventViewModel<T> {
	actionItem: ActionItemViewModel;
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			actionItem: observable,
			viewmodel: computed,
		});

		this.actionItem = new ActionItemViewModel(userSession, event.actionItem);
	}

	public get viewmodel() {
		return this.actionItem;
	}
}

export class NoteEventViewModel<T extends Api.INoteEvent = Api.INoteEvent> extends TimelineEventViewModel<T> {
	note: NoteViewModel;
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			note: observable,
			viewmodel: computed,
		});

		this.note = new NoteViewModel(userSession, event.note);
	}

	public get viewmodel() {
		return this.note;
	}
}

export class PhoneCallCompletedEventViewModel<
	T extends Api.IPhoneCallCompletedEvent = Api.IPhoneCallCompletedEvent,
> extends NoteEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			phoneCall: computed,
		});
	}

	public get phoneCall() {
		return this.mEvent.phoneCall;
	}
}

export class HandwrittenCardOrderEventViewModel<
	T extends Api.IHandwrittenCardOrderEvent = Api.IHandwrittenCardOrderEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			cardContent: computed,
			cardSignature: computed,
		});
	}

	public get cardContent() {
		return this.mEvent.cardContent;
	}

	public get cardSignature() {
		return this.mEvent.cardSignature;
	}

	public get summary() {
		return this.mEvent.summary;
	}
}

export class UntrackedPhoneCallEventViewModel<
	T extends Api.INoteEvent = Api.INoteEvent,
> extends NoteEventViewModel<T> {}
export class DealCreatedEventViewModel<T extends Api.INoteEvent = Api.INoteEvent> extends NoteEventViewModel<T> {}
export class DealUpdatedEventViewModel<T extends Api.INoteEvent = Api.INoteEvent> extends NoteEventViewModel<T> {}
export class SkipLeadEventViewModel<T extends Api.INoteEvent = Api.INoteEvent> extends NoteEventViewModel<T> {}

export class FollowUpEventViewModel<
	T extends Api.IAbstractFollowUpEvent = Api.IAbstractFollowUpEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			followUpId: computed,
		});
	}

	public get followUpId() {
		return this.mEvent.followUpId;
	}
}

export class CancelledFollowUpEventViewModel<
	T extends Api.IAbstractFollowUpEvent = Api.IAbstractFollowUpEvent,
> extends TimelineEventViewModel<T> {}
export class RescheduledFollowUpEventViewModel<
	T extends Api.IAbstractFollowUpEvent = Api.IAbstractFollowUpEvent,
> extends TimelineEventViewModel<T> {}

export class ConversationThreadEventViewModel<
	T extends Api.IConversationThreadEvent = Api.IConversationThreadEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			lastMessages: computed,
		});
	}

	public get lastMessages() {
		return this.mEvent.lastMessages;
	}
}

export class SentEmailEventViewModel<
	T extends Api.ISentEmailEvent = Api.ISentEmailEvent,
> extends TimelineEventViewModel<T> {
	note: NoteViewModel;
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			note: observable,
			viewmodel: computed,
			openDate: computed,
		});

		this.note = new NoteViewModel(userSession, event.note);
	}

	public get viewmodel() {
		return this.note;
	}
	public get openDate() {
		return this.mEvent.openDate;
	}
}

export class ExternalSentEmailEventViewModel<
	T extends Api.IExternalSentEmailEvent = Api.IExternalSentEmailEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);
	}

	public get subject() {
		return this.mEvent.subject;
	}
}

export class HtmlNewsletterEventViewModel<
	T extends Api.IHtmlNewsletterEvent = Api.IHtmlNewsletterEvent,
> extends TimelineEventViewModel<T> {
	note: NoteViewModel;
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			note: observable,
			viewmodel: computed,
			openDate: computed,
		});

		this.note = new NoteViewModel(userSession, event.note);
	}

	public get viewmodel() {
		return this.note;
	}
	public get openDate() {
		return this.mEvent.openDate;
	}

	public get subject() {
		return this.mEvent.subject;
	}
}

export class PhoneCallEventViewModel<T extends Api.INoteEvent = Api.INoteEvent> extends TimelineEventViewModel<T> {
	phoneCall: NoteViewModel;
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			phoneCall: observable,
			viewmodel: computed,
		});

		this.phoneCall = new NoteViewModel(userSession, event.note);
	}

	public get viewmodel() {
		return this.phoneCall;
	}
}

export class MeetingEventViewModel<T extends Api.IMeetingEvent = Api.IMeetingEvent> extends TimelineEventViewModel<T> {}

export class SurveyResponseEventViewModel<
	TSurveyResponse extends Api.ISurveyResponse = Api.ISurveyResponse,
	T extends Api.ISurveyResponseEvent<TSurveyResponse> = Api.ISurveyResponseEvent<TSurveyResponse>,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			surveyName: computed,
			response: computed,
		});
	}

	public get surveyName() {
		return this.mEvent.surveyName;
	}

	public get response() {
		return this.mEvent.response;
	}
}

export class SatisfactionSurveyResponseEventViewModel extends SurveyResponseEventViewModel<
	Api.ISatisfactionSurveyResponse,
	Api.ISatisfactionSurveyResponseEvent
> {}

export class FileRequestSurveyResponseEventViewModel extends SurveyResponseEventViewModel<
	Api.IFileRequestSurveyResponse,
	Api.IFileRequestSurveyResponseEvent
> {}

export class FileSendEventViewModel<
	T extends Api.IFileSendEvent = Api.IFileSendEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			files: computed,
		});
	}

	public get files() {
		return this.mEvent.files;
	}
}

export class FileDownloadEventViewModel<
	T extends Api.IFileDownloadEvent = Api.IFileDownloadEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			files: computed,
		});
	}

	public get files() {
		return this.mEvent.files;
	}
}

export class FileDeleteEventViewModel<
	T extends Api.IFileDeleteEvent = Api.IFileDeleteEvent,
> extends TimelineEventViewModel<T> {
	constructor(userSession: UserSessionContext, event: T) {
		super(userSession, event);

		makeObservable(this, {
			file: computed,
		});
	}

	public get file() {
		return this.mEvent.file;
	}
}

export class RichContentViewModel<T extends Api.IRichContent = Api.IRichContent> extends ViewModel {
	protected creatorDisplayName: string;
	protected creatorDisplayNameShort: string;
	protected deleting: boolean;
	protected mLastModifiedDate: Date;
	protected mCreationDate: Date;
	protected saving: boolean;
	public dateFormat: string;
	protected mCompanyRefs: Api.IRichContentEntityReference<CompanyViewModel>[];
	protected mContactRefs: Api.IRichContentEntityReference<ContactViewModel>[];
	protected mMentionedEntities: Api.IRichContentEntityReference<EntityViewModel<Api.IEntity>>[];
	protected mRichContent: T;
	protected mUserRefs: Api.IRichContentEntityReference<Api.IUser>[];
	private mAttachments: FileAttachmentViewModel[];
	protected mUuid: string;

	constructor(userSession: UserSessionContext, richContent: T = {} as T) {
		super(userSession);

		makeObservable<
			RichContentViewModel<T>,
			| 'creatorDisplayName'
			| 'creatorDisplayNameShort'
			| 'deleting'
			| 'mLastModifiedDate'
			| 'mCreationDate'
			| 'saving'
			| 'mCompanyRefs'
			| 'mContactRefs'
			| 'mMentionedEntities'
			| 'mRichContent'
			| 'mUserRefs'
			| 'mAttachments'
		>(this, {
			creatorDisplayName: observable,
			creatorDisplayNameShort: observable,
			deleting: observable,
			mLastModifiedDate: observable,
			mCreationDate: observable,
			saving: observable,
			dateFormat: observable,
			mCompanyRefs: observable.ref,
			mContactRefs: observable.ref,
			mMentionedEntities: observable.ref,
			mRichContent: observable.ref,
			mUserRefs: observable.ref,
			mAttachments: observable.ref,
			uuid: computed,
			id: computed,
			plainTextContent: computed,
			context: computed,
			setContext: action,
			setContent: action,
			isPublic: computed,
			visibility: computed,
			creationDate: computed,
			lastModifiedDateAsDate: computed,
			lastModifiedDateText: computed,
			creator: computed,
			creatorId: computed,
			creatorName: computed,
			creatorNameShort: computed,
			isBusy: override,
			isSaving: computed,
			isDeleting: computed,
			canDelete: computed,
			canEdit: computed,
			rawContentState: computed,
			preview: computed,
			mentionedEntities: computed,
			contactReferences: computed,
			companyReferences: computed,
			userReferences: computed,
			attachments: computed,
			delete: action,
		});

		this.mUuid = uuidgen();
		this.dateFormat = 'MM/DD/YY';
		this.deleting = false;
		this.loading = false;
		this.mGetApiPath = this.mGetApiPath.bind(this);
		this.mSetRichContent = this.mSetRichContent.bind(this);
		this.mSetRichContent(richContent);
		this.saving = false;
	}

	/** Unique instance id (e.g. for use with react component keys) */
	public get uuid() {
		return this.mUuid;
	}

	public get id() {
		return this.mRichContent.id;
	}

	public get plainTextContent() {
		return this.mRichContent.plainTextContent;
	}

	public get context() {
		return this.mRichContent.context;
	}

	public setContext(context: Api.IRichContentContext) {
		this.mRichContent = { ...(this.mRichContent || {}), context } as T;
	}

	public setContent(content: Api.IRichContent) {
		this.mRichContent = { ...(content || {}) } as T;
	}

	public get isPublic() {
		return this.mRichContent.visibility === 'all';
	}

	public get visibility() {
		return this.mRichContent.visibility;
	}

	public get creationDate() {
		return this.mCreationDate;
	}

	public get lastModifiedDateAsDate() {
		return this.mLastModifiedDate;
	}

	public get lastModifiedDateText() {
		if (this.mRichContent && this.mRichContent.lastModifiedDate) {
			const lastModifiedMoment = moment(this.mLastModifiedDate);
			const now = moment();
			const startOfToday = now.startOf('day');
			if (startOfToday < lastModifiedMoment && lastModifiedMoment < now) {
				return 'EARLIER TODAY';
			}

			return lastModifiedMoment.format(this.dateFormat);
		}

		return null;
	}

	public get creator() {
		return this.mRichContent.creator;
	}

	public get creatorId() {
		return this.mRichContent.creatorId;
	}

	public get creatorName() {
		return this.creatorDisplayName;
	}

	public get creatorNameShort() {
		return this.creatorDisplayNameShort;
	}

	public get isBusy() {
		return this.deleting || this.loading || this.saving;
	}

	public get isSaving() {
		return this.saving;
	}

	public get isDeleting() {
		return this.deleting;
	}

	// eslint-disable-next-line @typescript-eslint/class-literal-property-style
	public get canDelete() {
		return true;
	}

	// eslint-disable-next-line @typescript-eslint/class-literal-property-style
	public get canEdit() {
		return true;
	}

	public get rawContentState() {
		return this.mRichContent.content;
	}

	public get preview() {
		return this.mRichContent.preview;
	}

	public get mentionedEntities() {
		return this.mMentionedEntities;
	}

	public get contactReferences() {
		return this.mContactRefs;
	}

	public get companyReferences() {
		return this.mCompanyRefs;
	}

	public get userReferences() {
		return this.mUserRefs;
	}

	public get attachments(): FileAttachmentViewModel[] {
		return this.mAttachments;
	}

	public toJs = () => {
		return this.mRichContent;
	};

	public load() {
		if (!this.isBusy) {
			this.loading = true;
			return new Promise<T>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<T>) => {
					runInAction(() => {
						this.loading = false;
						if (opResult.success) {
							this.mSetRichContent(opResult.value);
							resolve(opResult.value);
						} else {
							reject(opResult);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<T>(
					`${this.mGetApiPath()}/${this.mRichContent.id}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
		return null;
	}

	public delete = () => {
		if (this.deleting || !this.canDelete) {
			return;
		}

		this.deleting = true;
		const promise = new Promise<T>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<T>) => {
				runInAction(() => {
					this.deleting = false;
					if (opResult.success) {
						resolve(opResult.value);
					} else {
						reject(opResult);
					}
				});
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<T>(
				`${this.mGetApiPath()}/${this.mRichContent.id}`,
				'DELETE',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	/** Subclasses should implement this */
	protected mGetApiPath(): string {
		throw new Error('Not Implemented');
	}

	protected mSetRichContent(richContent?: T) {
		this.mRichContent = richContent;
		this.creatorDisplayName = null;
		this.creatorDisplayNameShort = null;
		this.mCompanyRefs = [];
		this.mContactRefs = [];
		this.mLastModifiedDate = null;
		this.mCreationDate = null;
		this.mMentionedEntities = [];
		this.mUserRefs = [];

		this.mAttachments = FileAttachmentViewModel.create(
			this.mUserSession,
			`${this.mGetApiPath()}/${this.id}`,
			richContent.attachments
		);

		if (this.mRichContent) {
			this.mLastModifiedDate = this.mRichContent.lastModifiedDate ? new Date(this.mRichContent.lastModifiedDate) : null;
			this.mCreationDate = this.mRichContent.creationDate ? new Date(this.mRichContent.creationDate) : null;
			this.creatorDisplayName = getDisplayName(this.mRichContent.creator);
			this.creatorDisplayNameShort = getDisplayName(this.mRichContent.creator, true);

			// convert referencedEntities to view models
			if (this.mRichContent.referencedEntities) {
				const refs = VmUtils.getReferenceEntityViewModels(this.mUserSession, this.mRichContent);
				this.mCompanyRefs = refs.companyRefs;
				this.mContactRefs = refs.contactRefs;
				this.mUserRefs = refs.userRefs;
				this.mMentionedEntities = [...refs.contactRefs, ...refs.companyRefs];
			}
		}
	}
}

export class NoteViewModel extends RichContentViewModel<Api.INote> {
	protected actionItemVms: ActionItemViewModel[];
	protected note: Api.INote;
	protected savePromise: Promise<Api.INote>;

	constructor(userSession: UserSessionContext, note?: Api.INote) {
		super(userSession, note);

		makeObservable<NoteViewModel, 'actionItemVms' | 'note'>(this, {
			actionItemVms: observable.ref,
			note: observable.ref,
			canDelete: override,
			canEdit: override,
			context: override,
			actionItems: computed,
			saveFromEmail: action,
			save: action,
		});

		this.mSetRichContent(note);
	}

	public get canDelete() {
		if (!this.note || !this.note.id || !this.note.creatorId || !this.note.permissions) {
			// this is the create note case
			return true;
		}

		return this.note.permissions.canDelete;
	}

	public get canEdit() {
		if (!this.note || !this.note.id || !this.note.creatorId || !this.note.permissions) {
			return true;
		}

		return this.note.permissions.canUpdate;
	}

	public get actionItems() {
		return this.actionItemVms;
	}

	public get context() {
		return this.note.context;
	}

	public get externalAssociations() {
		return this.note.externalAssociations;
	}

	public saveFromEmail = (note: Api.INote, newAttachments?: AttachmentsToBeUploadedViewModel) => {
		return this.save(note, newAttachments, 'note/fromEmail');
	};

	public save(note: Api.INote, newAttachments?: AttachmentsToBeUploadedViewModel, url?: string) {
		if (this.saving || this.savePromise) {
			return;
		}

		this.saving = true;
		const promise = new Promise<Api.INote>((resolve, reject) => {
			const updateExistingNote = this.note && !!this.note.id;
			const noteToSave = { ...note };
			if (updateExistingNote) {
				noteToSave.id = this.note.id;
			}

			// pull out email and attachments into form data if attachments are present
			let formData: FormData = null;
			if (newAttachments && newAttachments.count > 0) {
				formData = new FormData();
				formData.append('value', JSON.stringify(noteToSave));
				newAttachments.attachments.forEach(x => {
					if (!(x instanceof File) && (x as File).name) {
						// is a Blob that has been given a name
						// need to send name as additional arg
						formData.append('files', x, (x as File).name);
					} else {
						formData.append('files', x);
					}
				});
			}

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.INote>(
				url || `note${updateExistingNote ? `/${this.note.id}` : ''}`,
				updateExistingNote ? 'PUT' : 'POST',
				formData ? formData : noteToSave,
				opResult => {
					runInAction(() => {
						this.saving = false;
						this.savePromise = null;
						this.mSetRichContent(opResult.value);
						resolve(opResult.value);
					});
				},
				error => {
					runInAction(() => {
						this.saving = false;
						this.savePromise = null;
						reject(error);
					});
				}
			);
		});

		this.savePromise = promise;
		return promise;
	}

	public toNote = () => {
		return this.note;
	};

	public setContext(context: Api.INoteContext) {
		super.setContext(context);
		if (this.note) {
			this.note = { ...this.note, context };
		}
	}

	protected mGetApiPath() {
		return 'note';
	}

	protected mSetRichContent(note?: Api.INote) {
		this.note =
			!note || !note.id
				? {
						actionItems: [],
						referencedEntities: { companies: [], contacts: [], users: [] },
						visibility: VmUtils.getDefaultVisibility(this.mUserSession.user),
						...(note || {}), // could be partial note... e.g. recent meeting
					}
				: note;
		this.actionItemVms = (this.note.actionItems || []).map(x => new ActionItemViewModel(this.mUserSession, x));

		super.mSetRichContent(this.note);
	}
}

export class ActionItemViewModel extends RichContentViewModel<Api.IActionItem> {
	private completed: boolean;
	private mDueDate?: Date;
	private mKeepInTouchReference: KeepInTouchReferenceViewModel;
	private actionItem: Api.IActionItem;
	private associatedNoteRefs: Api.IRichContentEntityReference<EntityViewModel<Api.IEntity>>[];
	private savePromise: Promise<Api.IActionItem>;

	constructor(userSession: UserSessionContext, actionItem?: Api.IActionItem) {
		super(userSession, actionItem);

		makeObservable<
			ActionItemViewModel,
			'completed' | 'mDueDate' | 'mKeepInTouchReference' | 'actionItem' | 'associatedNoteRefs'
		>(this, {
			completed: observable,
			mDueDate: observable,
			mKeepInTouchReference: observable.ref,
			actionItem: observable.ref,
			associatedNoteRefs: observable.ref,
			associatedNoteModel: computed,
			associatedNotesReferencedEntities: computed,
			referencedContactsForSendMessage: computed,
			assignee: computed,
			automationId: computed,
			dueDate: computed,
			isCompleted: computed,
			keepInTouchReference: computed,
			isSuggestedKeepInTouchActionItem: computed,
			isKeepInTouchActionItem: computed,
			setActionItem: action,
			save: action,
			toggleComplete: action,
			snooze: action,
		});

		this.mSetRichContent(actionItem);
	}

	public get associatedNoteModel() {
		return this.actionItem.associatedNote;
	}

	public get associatedNotesReferencedEntities() {
		return this.associatedNoteRefs;
	}

	public get referencedContactsForSendMessage() {
		if (!this.actionItem) {
			return [];
		}

		const referencedContactsMapForSendMessage: Api.IDictionary<ContactViewModel> = {};

		// add contacts from non-content referenced contacts, content referenced contacts and associated note
		[...(this.mContactRefs || []), ...(this.mMentionedEntities || []), ...(this.associatedNoteRefs || [])]
			.filter(x => Api.getTypeForEntity(x.entity.toJs()) !== 'company')
			.map(x => x.entity as ContactViewModel)
			.forEach(x => {
				if (!referencedContactsMapForSendMessage[x.id]) {
					referencedContactsMapForSendMessage[x.id] = x;
				}
			});

		return Object.keys(referencedContactsMapForSendMessage).map(x => referencedContactsMapForSendMessage[x]);
	}

	public get assignee() {
		return this.actionItem ? this.actionItem.assignee : null;
	}

	public get automationId() {
		return this.actionItem?.automationId;
	}

	public get dueDate() {
		return this.mDueDate;
	}

	public get isCompleted() {
		return this.completed;
	}

	public get keepInTouchReference() {
		return this.mKeepInTouchReference;
	}

	public get isSuggestedKeepInTouchActionItem() {
		return this.mKeepInTouchReference && !this.mKeepInTouchReference.id;
	}

	public get isKeepInTouchActionItem() {
		return !!this.mKeepInTouchReference;
	}

	public setActionItem(actionItem: Api.IActionItem) {
		this.mSetRichContent(actionItem);
	}

	public toActionItem = (shallow = false) => {
		const actionItem = this.actionItem || {};
		const actionItemClone: Api.IActionItem = { ...actionItem };

		if (actionItem.assignee) {
			// create a copy because we might need to modify it
			actionItemClone.assignee = { ...actionItem.assignee };
		}

		if (actionItem.keepInTouchReference) {
			// create a copy because we might need to modify it
			actionItemClone.keepInTouchReference = {
				...actionItem.keepInTouchReference,
				contact:
					actionItem.keepInTouchReference && actionItem.keepInTouchReference.contact
						? {
								...actionItem.keepInTouchReference.contact,
							}
						: null,
			};
		}

		if (shallow) {
			if (actionItemClone.assignee) {
				actionItemClone.assignee = { id: actionItemClone.assignee.id };
			}

			if (actionItemClone.keepInTouchReference && actionItemClone.keepInTouchReference.contact) {
				actionItemClone.keepInTouchReference.contact = {
					id: actionItemClone.keepInTouchReference.contact.id,
				};
			}
		}

		return actionItemClone;
	};

	public save = (actionItemToSave: Api.IActionItem) => {
		if (!this.saving) {
			const promise = new Promise<Api.IActionItem>((resolve, reject) => {
				this.saving = true;
				const updatingActionItem = this.actionItem && this.actionItem.id;
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IActionItem>(
					`actionitem${updatingActionItem ? `/${this.actionItem.id}` : ''}`,
					updatingActionItem ? 'PUT' : 'POST',
					actionItemToSave,
					opResult => {
						runInAction(() => {
							this.mSetRichContent(opResult.value);
							this.saving = false;
							resolve(opResult.value);
						});
					},
					(error: Api.IOperationResultNoValue) => {
						runInAction(() => {
							this.saving = false;
							reject(error);
						});
					}
				);
			});
			this.savePromise = promise;
			return promise;
		} else {
			return this.savePromise;
		}
	};

	public toggleComplete = (complete?: boolean) => {
		if (!this.saving) {
			const promise = new Promise<Api.IActionItem>((resolve, reject) => {
				this.saving = true;
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IActionItem>(
					`actionitem/${this.actionItem.id}/${complete ? 'complete' : 'incomplete'}`,
					'POST',
					null,
					opResult => {
						runInAction(() => {
							// no need to call actionItem setter for one prop change
							this.completed = complete;
							this.actionItem.isCompleted = complete;
							this.saving = false;
							resolve(opResult.value);
						});
					},
					(error: Api.IOperationResultNoValue) => {
						runInAction(() => {
							this.saving = false;
							reject(error);
						});
					}
				);
			});
			return promise;
		} else {
			return null;
		}
	};

	/** Set the due date to today + numberOfDays */
	public snooze = (numberOfDays: number) => {
		if (!this.isBusy && numberOfDays > 0) {
			const nextDueDate = new Date();
			nextDueDate.setDate(nextDueDate.getDate() + numberOfDays);
			const actionItemModel = {
				...this.toActionItem(true),
				dueDate: moment(nextDueDate).toISOString(),
			};
			return this.save(actionItemModel);
		}

		return null;
	};

	protected mGetApiPath() {
		return 'actionitem';
	}

	// internal method w/o action
	protected mSetRichContent(actionItem?: Api.IActionItem) {
		this.actionItem =
			!actionItem || !actionItem.id
				? {
						referencedEntities: { companies: [], contacts: [], users: [] },
						visibility: VmUtils.getDefaultVisibility(this.mUserSession.user),
						...(actionItem || {}), // could be partial actionItem
					}
				: actionItem;
		this.associatedNoteRefs = [];
		this.completed = false;
		this.mDueDate = null;
		this.mKeepInTouchReference = null;

		if (actionItem) {
			this.mDueDate = actionItem.dueDate ? new Date(actionItem.dueDate) : null;
			this.completed = actionItem.isCompleted;

			// convert associated notes referencedEntities to view models
			if (actionItem.associatedNote && actionItem.associatedNote.referencedEntities) {
				const refs = VmUtils.getReferenceEntityViewModels(this.mUserSession, actionItem.associatedNote);
				this.associatedNoteRefs = [...refs.companyRefs, ...refs.contactRefs];
			}

			if (actionItem.keepInTouchReference) {
				this.mKeepInTouchReference = new KeepInTouchReferenceViewModel(
					this.mUserSession,
					actionItem.keepInTouchReference
				);
			}
		}

		super.mSetRichContent(this.actionItem);
	}
}

export class ActionItemAttachmentViewModel<TContentState extends IRichContentEditorState = IRichContentEditorState> {
	protected mActionItem: ActionItemViewModel;
	public assignee: Api.IUser;
	public contentState: TContentState;
	public dueDate: Date;

	constructor(actionItem?: ActionItemViewModel, contentState?: TContentState) {
		makeObservable<ActionItemAttachmentViewModel<TContentState>, 'mActionItem'>(this, {
			mActionItem: observable.ref,
			assignee: observable.ref,
			contentState: observable.ref,
			dueDate: observable.ref,
			actionItem: computed,
			hasContent: computed,
			setActionItem: action,
		});

		this.mSetActionItem(actionItem);
		if (contentState) {
			this.contentState = contentState;
		}
	}

	public get actionItem() {
		return this.mActionItem;
	}

	public get hasContent() {
		return this.contentState && this.contentState.hasContent();
	}

	public setActionItem = (actionItem: ActionItemViewModel) => {
		this.mSetActionItem(actionItem);
	};

	public toJs = (shallow = false) => {
		const originalActionItemModel = this.mActionItem ? this.mActionItem.toActionItem(shallow) : {};
		const actionItemModel: Api.IActionItem = {
			...originalActionItemModel,
		};

		// important: remove completion date and let isCompleted control the completion state. setting both is undefined
		delete actionItemModel.completionDate;

		// ensure referenced entities exists
		if (!originalActionItemModel || !actionItemModel.referencedEntities) {
			actionItemModel.referencedEntities = {
				companies: [],
				contacts: [],
				users: [],
			};
		}

		if (this.contentState) {
			actionItemModel.content = this.contentState.getRawRichTextContent();
		}

		if (this.assignee && this.assignee.id) {
			actionItemModel.assignee = shallow ? { id: this.assignee.id } : this.assignee;
		}

		if (this.dueDate) {
			actionItemModel.dueDate = moment(this.dueDate).toISOString();
		}

		return actionItemModel;
	};

	protected mSetActionItem = (actionItem?: ActionItemViewModel) => {
		this.mActionItem = actionItem;

		if (this.mActionItem) {
			this.assignee = this.mActionItem.assignee;
			this.dueDate = this.mActionItem.dueDate;
		} else {
			this.assignee = null;
			this.dueDate = null;
		}
	};
}

export class EntityViewModel<T extends Api.IEntity = Api.IEntity> extends ViewModel {
	protected deleting: boolean;
	protected displayName: string;
	protected saving: boolean;
	protected validEntity: boolean;
	protected entity: T;
	protected mLastModifiedDate: Date;
	protected mRichContentViewModel: EntityRichContentViewModel<T>;

	constructor(userSession: UserSessionContext, entity: T) {
		super(userSession);

		makeObservable<
			EntityViewModel<T>,
			'deleting' | 'displayName' | 'saving' | 'validEntity' | 'entity' | 'mLastModifiedDate' | 'mRichContentViewModel'
		>(this, {
			deleting: observable,
			displayName: observable,
			saving: observable,
			validEntity: observable,
			entity: observable.ref,
			mLastModifiedDate: observable.ref,
			mRichContentViewModel: observable.ref,
			phoneNumbers: computed,
			lastModifiedDateAsDate: computed,
			lastModifiedDate: computed,
			isDeleting: computed,
			keyFactsCollection: computed,
			tags: computed,
			tagsCollection: computed,
			socialProfiles: computed,
			webSite: computed,
			bio: computed,
			companyName: computed,
			isValid: computed,
			id: computed,
			name: computed,
			isFetchingRichContent: computed,
			setEntity: action,
			richContent: computed,
			setRichContent: action,
			isBusy: override,
			load: override,
			getRichContent: action,
			deleteRichContent: action,
			update: action,
			delete: action,
			updateKeyFactsCollection: action,
			updateTags: action,
			removeTags: action,
			addTags: action,
		});

		this.mUpdateTags = this.mUpdateTags.bind(this);
		this.setEntity(entity);
	}

	public get richContentViewModel() {
		if (!this.mRichContentViewModel) {
			this.mRichContentViewModel = new EntityRichContentViewModel(this);
		}

		return this.mRichContentViewModel;
	}

	public get phoneNumbers() {
		return this.entity.phoneNumbers;
	}

	public get lastModifiedDateAsDate() {
		return this.mLastModifiedDate;
	}

	public get lastModifiedDate() {
		return this.entity.lastModifiedDate;
	}

	public get isDeleting() {
		return this.deleting;
	}

	public get keyFactsCollection() {
		return this.entity.keyFactsCollection;
	}

	public get tags() {
		return this.entity.tags;
	}

	public get tagsCollection() {
		return this.entity.tagsCollection;
	}

	public get socialProfiles() {
		return this.entity.socialProfiles;
	}

	public get webSite() {
		return this.entity.webSite;
	}

	public get bio() {
		return this.entity.bio;
	}

	public get companyName() {
		return this.entity.companyName;
	}

	public get isValid() {
		return this.validEntity;
	}

	public get id() {
		return this.entity.id;
	}

	public get name() {
		return this.displayName;
	}

	public get isFetchingRichContent() {
		return this.richContentViewModel ? this.richContentViewModel.controller.isFetching : false;
	}

	public setEntity(entity: T) {
		this.entity = entity;
		this.displayName = VmUtils.getEntityDisplayName(entity);
		this.validEntity = entity.id && !VmUtils.ResolveByEmailRegExp.test(entity.id);
		this.mLastModifiedDate = entity && entity.lastModifiedDate ? new Date(entity.lastModifiedDate) : null;
	}

	public get richContent() {
		// Note: we use richContentViewModel prop because this.mRichContentViewModel is created lazily
		return this.richContentViewModel.controller.fetchResults;
	}

	public setRichContent = (richContentCollection: RichContentViewModel[]) => {
		// Note: we use richContentViewModel prop because this.mRichContentViewModel is created lazily
		this.richContentViewModel.controller.fetchResults.setItems(richContentCollection);
	};

	public get isBusy() {
		return this.busy || this.saving || this.loading;
	}

	/** Subclasses should override this... default returns empty string */
	protected getNotesApiQueryPath = () => {
		return '';
	};

	/** Subclasses should override this... default returns empty string */
	protected getEntityApiPath = () => {
		return '';
	};

	public reset() {
		this.richContentViewModel.controller.reset();
	}

	public toJs = () => {
		return this.entity;
	};

	public toMention = () => {
		return this.entity;
	};

	public load(allowArchived = false) {
		if (!this.isLoading) {
			this.loading = true;

			const promise = new Promise<T>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<T>) => {
					runInAction(() => {
						this.loading = false;
						if (opResult.success) {
							this.loaded = true;
							this.setEntity(opResult.value);
							resolve(opResult.value);
						} else {
							reject(opResult);
						}
					});
				};
				const apiPath = this.getEntityApiPath();

				// We need to start loading the full tags collection for contacts and companies
				// this is to display the tags with colors in the UI
				const isContact = apiPath === 'contact';
				const isCompany = apiPath === 'company';
				const expand = isContact
					? 'HouseholdMembers,TagsCollection,DuplicateContacts'
					: isCompany
						? 'TagsCollection'
						: undefined;

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICompany>(
					Api.ImpersonationBroker.composeApiUrl({
						queryParams: {
							expand,
							includeArchived: allowArchived,
						},
						urlPath: `${apiPath}/${encodeURIComponent(this.entity.id)}`,
					}),
					'GET',
					null,
					// FOLLOWUP: Resolve

					// @ts-ignore
					onFinish,
					onFinish
				);
			});

			return promise;
		}

		return null;
	}

	public getRichContent = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number) => {
		// Note: we use notesViewModel prop because this.mRichContentViewModel is created lazily
		return this.richContentViewModel.controller.getNext(sortDescriptor, pageSize);
	};

	public deleteRichContent = (richContent: RichContentViewModel) => {
		const promise = richContent.delete();
		if (richContent && this.richContentViewModel.controller.fetchResults.has(richContent)) {
			if (promise) {
				promise.then(() => {
					this.richContentViewModel.controller.fetchResults.removeItems([richContent]);
				});
			}
		}

		return promise;
	};

	public update = (entity: T) => {
		return this.mUpdate(entity);
	};

	public delete = () => {
		if (!this.isBusy) {
			this.deleting = true;
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.deleting = false;
						if (result.success) {
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`${this.getEntityApiPath()}/${this.entity.id}`,
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});

			return promise;
		}

		return null;
	};

	public updateKeyFactsCollection = (keyFactsCollection?: Api.IKeyFact[]) => {
		if (!this.isBusy) {
			const entity: T = {
				...(this.entity || ({} as any)),
				keyFactsCollection: keyFactsCollection || [],
			};
			return this.mUpdate(entity);
		}
		return null;
	};

	public updateTags = (tags?: string[]) => {
		return this.mUpdateTags('PUT', tags);
	};

	public removeTags = (tags?: string[]) => {
		return this.mUpdateTags('DELETE', tags);
	};

	public addTags = (tags?: string[]) => {
		return this.mUpdateTags('POST', tags);
	};

	protected mUpdateTags(method: Api.HTTPMethod, tags?: string[]) {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							switch (method) {
								case 'PUT': {
									this.entity = {
										...this.entity,
										tags: tags || [],
									};
									break;
								}
								case 'DELETE': {
									const tagsToSet = [...(this.entity.tags || [])];
									const lowerTags = tagsToSet.map(x => x.toLocaleLowerCase());
									(tags || []).forEach(x => {
										const index = lowerTags.indexOf(x.toLocaleLowerCase());
										if (index >= 0) {
											lowerTags.splice(index, 1);
											tagsToSet.splice(index, 1);
										}
									});
									this.entity = {
										...this.entity,
										tags: tagsToSet,
									};
									break;
								}
								case 'POST': {
									const tagsToSet = [...(this.entity.tags || [])];
									const lowerTags = tagsToSet.map(x => x.toLocaleLowerCase());
									(tags || []).forEach(x => {
										const index = lowerTags.indexOf(x.toLocaleLowerCase());
										if (index < 0) {
											tagsToSet.push(x);
										}
									});
									this.entity = {
										...this.entity,
										tags: tagsToSet,
									};
									break;
								}
								default: {
									break;
								}
							}
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`${this.getEntityApiPath()}/${this.entity.id}/tags${method === 'POST' ? '/add' : ''}`,
					method,
					tags || [],
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return null;
	}

	protected mUpdate = (entity: T) => {
		if (!entity || (this.entity && this.entity.id && this.entity.id !== entity.id)) {
			const error: Api.IOperationResultNoValue = {
				systemMessage: 'Missing or mismatched entity for this operation.',
			};
			return Promise.reject(error);
		}

		if (!this.isBusy) {
			this.saving = true;
			const promise = new Promise<T>((resolve, reject) => {
				// remove legacy key facts property
				const entityToSave = Api.excludeKeysOf(entity, ['keyFacts']) as T;
				const isContact = this.getEntityApiPath() === 'contact';
				let queryParams: Record<string, any> = {};

				if (isContact) {
					queryParams = {
						expand: entityToSave.id ? 'HouseholdMembers' : null,
						allowDuplicateEmail: true,
					};
				}

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<T>(
					this.composeApiUrl({
						urlPath: `${this.getEntityApiPath()}${entityToSave.id ? `/${entityToSave.id}` : ''}`,
						queryParams,
					}),
					entityToSave.id ? 'PUT' : 'POST',
					entityToSave,
					opResult => {
						runInAction(() => {
							this.saving = false;
							this.setEntity(opResult.value);
							resolve(opResult.value);
						});
					},
					error => {
						runInAction(() => {
							this.saving = false;
							reject(error);
						});
					}
				);
			});

			return promise;
		}
		return null;
	};
}

export class KeepInTouchViewModel extends ViewModel {
	protected deleting: boolean;
	protected saving: boolean;
	protected kit: Api.IKeepInTouch;
	protected promise: Promise<any>;
	private mSuggestionsPageCollectionController: ObservablePageCollectionController<Api.IContact, ContactViewModel>;

	constructor(userSession: UserSessionContext, keepInTouch?: Api.IKeepInTouch) {
		super(userSession);

		makeObservable<KeepInTouchViewModel, 'deleting' | 'saving' | 'kit' | 'promise' | 'privateLoad'>(this, {
			deleting: observable,
			saving: observable,
			kit: observable.ref,
			promise: observable.ref,
			totalNumberOfSuggestions: computed,
			fetchingSuggestions: computed,
			suggestions: computed,
			isLoaded: override,
			Id: computed,
			frequency: computed,
			isSaving: computed,
			isBusy: override,
			reset: action,
			removeSuggestions: action,
			getSuggestions: action,
			save: action,
			delete: action,
			loadByContact: action,
			setKeepInTouch: action,
			privateLoad: action,
		});

		this.setKeepInTouch(keepInTouch);
		this.mSuggestionsPageCollectionController = new ObservablePageCollectionController({
			apiPath: 'keepintouch/suggestions',
			client: userSession.webServiceHelper,
			transformer: this.getContactViewModelRepresentation,
		});
	}

	public toJs() {
		return this.kit;
	}

	public get totalNumberOfSuggestions() {
		return this.mSuggestionsPageCollectionController.totalCount;
	}

	public get fetchingSuggestions() {
		return this.mSuggestionsPageCollectionController.isFetching;
	}

	public get suggestions() {
		return this.mSuggestionsPageCollectionController.fetchResults;
	}

	public get isLoaded() {
		return this.kit && !!this.kit.id;
	}

	public get Id() {
		if (!this.kit) {
			return null;
		}
		return this.kit.id;
	}

	public get frequency() {
		if (!this.kit) {
			return -1;
		}
		return this.kit.frequency;
	}

	public get isSaving() {
		return this.saving;
	}

	public get isBusy() {
		return this.busy || this.loading || this.deleting || this.mSuggestionsPageCollectionController.isFetching;
	}

	public reset = () => {
		this.mSuggestionsPageCollectionController.reset();
		this.busy = false;
		this.deleting = false;
		this.saving = false;
		this.loading = false;
	};

	/** Only removes contacts from the local collection of suggestions, not from Levitate */
	public removeSuggestions = (contacts: ContactViewModel[]) => {
		this.removeSuggestionsPrivate(contacts);
	};

	public getSuggestions = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number, params?: any) => {
		return this.mSuggestionsPageCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	public save = (keepintouch: Api.IKeepInTouch) => {
		if (!this.busy) {
			this.saving = true;
			const promise = new Promise<Api.IKeepInTouch>((resolve, reject) => {
				const onFinish = (kit?: Api.IKeepInTouch) => {
					runInAction(() => {
						this.saving = false;
						if (this.promise === promise) {
							this.promise = null;
						}

						if (kit) {
							this.setKeepInTouch(kit);
						}
					});
				};

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IKeepInTouch>(
					'keepintouch',
					'POST',
					keepintouch,
					opResult => {
						onFinish(opResult.value);
						resolve(opResult.value);
					},
					(error: Api.IOperationResultNoValue) => {
						onFinish();
						reject(error);
					}
				);
			});
			this.promise = promise;
			return promise;
		}
	};

	public delete = () => {
		if (!this.busy) {
			this.deleting = true;
			const promise = new Promise<void>((resolve, reject) => {
				const onFinish = (error: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.deleting = false;
						if (this.promise === promise) {
							this.promise = null;
						}

						if (error) {
							reject(error);
						} else {
							this.setKeepInTouch(null);
							resolve();
						}
					});
				};

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<void>(
					`keepintouch/${this.kit.id}`,
					'DELETE',
					null,
					() => {
						onFinish(null);
					},
					onFinish
				);
			});
			this.promise = promise;
			return promise;
		}
	};

	public load() {
		return this.privateLoad();
	}

	public loadByContact(contactId?: string) {
		return this.privateLoad(contactId);
	}

	public setKeepInTouch(keepInTouch: Api.IKeepInTouch) {
		this.kit = keepInTouch;
	}

	protected privateLoad = (contactId?: string) => {
		if (!this.busy) {
			this.loading = true;
			const promise = new Promise<Api.IKeepInTouch>((resolve, reject) => {
				const onFinish = () => {
					runInAction(() => {
						this.loading = false;
						if (this.promise === promise) {
							this.promise = null;
						}
					});
				};

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IKeepInTouch>(
					contactId ? `keepintouch/byContact/${contactId}/me` : `keepintouch/${this.kit.id}`,
					'GET',
					null,
					opResult => {
						onFinish();
						this.setKeepInTouch(opResult.value);
						resolve(opResult.value);
					},
					(error: Api.IOperationResultNoValue) => {
						onFinish();
						reject(error);
					}
				);
			});
			this.promise = promise;
			return promise;
		}
	};

	private getContactViewModelRepresentation = (contact: Api.IContact) => {
		return new ContactViewModel(this.mUserSession, contact);
	};

	/** Intentionally not an action */
	private removeSuggestionsPrivate = (contacts: ContactViewModel[]) => {
		this.mSuggestionsPageCollectionController.fetchResults.removeItems(contacts);
	};
}

export class ContactViewModel extends EntityViewModel<Api.IContact> {
	protected declare kitVm: KeepInTouchViewModel;
	protected mConnections: ContactConnectionsViewModel;
	protected mRelationshipHealth: Api.IRelationshipHealth;
	protected mTagAlerts: TagAlertViewModel[];
	protected mTagValueToTagAlert: Api.IDictionary<TagAlertViewModel>;
	protected mIntegrationData: Api.IContactIntegrationData[];
	private mDataOriginEnabled: boolean;
	private mIsFetchingIntegrationData = false;
	private mUploadingProfilePic = false;

	public static Create = (userSession: UserSessionContext, contactModel: Api.IContact) => {
		const promise = new Promise<ContactViewModel>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IContact>) => {
				if (opResult.success) {
					const contact = new ContactViewModel(userSession, opResult.value);
					resolve(contact);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContact>(
				'contact?allowDuplicateEmail=true',
				'POST',
				contactModel,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	constructor(userSession: UserSessionContext, contact: Api.IContact) {
		super(userSession, contact);

		makeObservable<
			ContactViewModel,
			| 'kitVm'
			| 'mConnections'
			| 'mRelationshipHealth'
			| 'mTagAlerts'
			| 'mTagValueToTagAlert'
			| 'mIntegrationData'
			| 'mDataOriginEnabled'
			| 'mIsFetchingIntegrationData'
			| 'mUploadingProfilePic'
			| 'contact'
		>(this, {
			kitVm: observable.ref,
			mConnections: observable.ref,
			mRelationshipHealth: observable.ref,
			mTagAlerts: observable.ref,
			mTagValueToTagAlert: observable.ref,
			mIntegrationData: observable.ref,
			mDataOriginEnabled: observable,
			mIsFetchingIntegrationData: observable,
			mUploadingProfilePic: observable.ref,
			duplicateContacts: computed,
			uploadingProfilePic: computed,
			bounceMetadata: computed,
			unsubscribeMetadata: computed,
			automatedSmsOptOutMetadata: computed,
			inProgressAutomations: computed,
			companyId: computed,
			ownerId: computed,
			relationshipHealth: computed,
			isRecognizedFirstName: computed,
			customProperties: computed,
			namesFromKeyFactsCollection: computed,
			tagsWithAlerts: computed,
			tagAlerts: computed,
			keepInTouch: computed,
			connections: computed,
			isLoaded: override,
			hasLoaded: computed,
			primaryEmail: computed,
			profilePic: computed,
			jobTitle: computed,
			bio: override,
			address: computed,
			setAddress: action,
			emailAddresses: computed,
			handle: computed,
			handleReverse: computed,
			userHasKeepInTouch: computed,
			visibility: computed,
			visibilityFriendlyName: computed,
			householdMembers: computed,
			household: computed,
			creatorId: computed,
			firstName: computed,
			firstNameForSalutation: computed,
			lastName: computed,
			source: computed,
			integrationData: computed,
			isFetchingIntegrationData: computed,
			contact: computed,
			dataOriginEnabled: computed,
			isArchived: computed,
			additionalId: computed,
			uploadProfilePic: action,
			unarchive: action,
			suppressKeyFact: action,
			setConnection: action,
			setEntity: override,
			addInProgressAutomations: action,
			cancelInProgressAutomation: action,
			setOwner: action,
			suppressKeepInTouchSuggestion: action,
			updateRelationshipHealth: action,
			updateVisibility: action,
			removeKeyDate: action,
			fetchIntegrationData: action,
		});
	}

	public get additionalId() {
		return this.contact?.additionalId;
	}

	public get duplicateContacts() {
		return this.contact?.duplicateContacts?.map(x =>
			new ContactViewModel(this.mUserSession, x).impersonate(this.impersonationContext)
		);
	}

	public get uploadingProfilePic() {
		return this.mUploadingProfilePic;
	}

	public get bounceMetadata() {
		return this.entity.metadata?.bounceMetadata;
	}

	public get unsubscribeMetadata() {
		return this.entity.metadata?.unsubscribeMetadata;
	}

	public get automatedSmsOptOutMetadata() {
		return this.entity.metadata?.automatedSmsOptOutMetadata;
	}

	public get inProgressAutomations() {
		return this.entity?.inProgressAutomations;
	}

	public get companyId() {
		return this.entity.companyId;
	}

	public get ownerId() {
		return this.entity.ownerId;
	}

	public get relationshipHealth() {
		return this.mRelationshipHealth;
	}

	public get isRecognizedFirstName() {
		return this.entity.isRecognizedFirstName;
	}

	public get customProperties() {
		return this.entity.customProperties;
	}

	/**
	 * Method returns the names from the KeyFactsCollection
	 *
	 * @returns {string[]} List of names
	 * @if they fall between now and 30 days from now
	 */
	public get namesFromKeyFactsCollection() {
		const names: string[] = [];

		this.entity?.keyFactsCollection?.forEach((x: Api.IKeyFact) => {
			if (x?.keyDate?.month && x?.keyDate?.day) {
				// set up
				const now = new Date();
				now.setHours(0, 0, 0, 0); // set now to beginning of day
				const yesterdayAtEndOfDay: Date = new Date();
				yesterdayAtEndOfDay.setDate(yesterdayAtEndOfDay.getDate() - 1); // set day to yesterday's date so we can check for same day birthdays
				yesterdayAtEndOfDay.setHours(23, 59, 59, 999); // yesterdays date set to end of day
				const upComingBirthDate: Date = new Date(`${x.keyDate.month}/${x.keyDate.day}/${now.getFullYear()}`);
				upComingBirthDate.setHours(0, 0, 0, 0);
				const thirtyDaysFromNow = moment(new Date()).add(30, 'days').toDate();
				const birthdayKeyFactMetaData: Api.IBirthInfoKeyFactMetadata = x?.source?.additionalMetadata;
				const isBirthdayDateBetweenSpecifiedDates: boolean = moment(upComingBirthDate).isBetween(
					yesterdayAtEndOfDay,
					thirtyDaysFromNow
				);
				if (isBirthdayDateBetweenSpecifiedDates && birthdayKeyFactMetaData?.birthInfo?.name) {
					names.push(birthdayKeyFactMetaData?.birthInfo?.name);
				}
			}
		});
		return names;
	}

	public get tagsWithAlerts() {
		return this.entity.tagsWithAlerts;
	}

	public get tagAlerts() {
		return this.mTagAlerts;
	}

	public get keepInTouch() {
		return this.kitVm;
	}

	public get connections() {
		return this.mConnections;
	}

	public get isLoaded() {
		return this.validEntity
			? this.entity.visibility &&
					this.entity.creatorId &&
					(!!this.entity.primaryEmail || (this.entity.firstName && !!this.entity.lastName))
			: true;
	}

	public get hasLoaded() {
		return this.loaded;
	}

	public get primaryEmail() {
		return this.contact.primaryEmail;
	}

	public get profilePic() {
		return this.contact.profilePic;
	}

	public get jobTitle() {
		return this.contact.jobTitle;
	}

	public get bio() {
		return this.contact.bio;
	}

	public get address() {
		return this.contact.address;
	}

	public setAddress = async (address: Api.IAddress) => {
		if (!!address?.address1 || !!address?.address2) {
			this.contact.address = address;
			await this.update(this.contact);
		}
		return this.contact.address;
	};

	public get emailAddresses() {
		return this.contact.emailAddresses;
	}

	public get handle() {
		return this.contact.handle;
	}

	public get handleReverse() {
		return this.contact.handleReverse;
	}

	public get userHasKeepInTouch() {
		return this.contact.userHasKeepInTouch || (this.keepInTouch && this.keepInTouch.isLoaded);
	}

	public get visibility() {
		return this.contact.visibility;
	}

	public get visibilityFriendlyName() {
		return this.contact.visibilityFriendlyName;
	}

	public get householdMembers() {
		return this.contact.householdMembers;
	}

	public get household() {
		return this.contact.household;
	}

	public get contactType() {
		return this.contact.contactType;
	}

	public get creatorId() {
		return this.contact.creatorId;
	}

	public get firstName() {
		return this.contact.firstName;
	}

	public get firstNameForSalutation() {
		return this.entity?.firstNameForSalutation;
	}

	public get lastName() {
		return this.contact.lastName;
	}

	public get source() {
		// Compute on-the-fly to always return current value, avoiding stale cached data
		// when setEntity() is called with incomplete data initially
		return this.entity?.source && VmUtils.apiIntegrationSources.includes(this.entity.source)
			? this.entity.source
			: null;
	}

	get integrationData() {
		return this.mIntegrationData;
	}

	get isFetchingIntegrationData() {
		return this.mIsFetchingIntegrationData;
	}

	protected get contact() {
		return this.entity as Api.IContact;
	}

	get dataOriginEnabled() {
		return this.mDataOriginEnabled;
	}

	set dataOriginEnabled(value: boolean) {
		runInAction(() => {
			const valueBefore = this.mDataOriginEnabled;
			this.mDataOriginEnabled = !!value;
			if (this.mDataOriginEnabled && valueBefore !== this.mDataOriginEnabled && !this.integrationData) {
				this.fetchIntegrationData();
			}
		});
	}

	get isArchived() {
		return this.contact.isArchived;
	}

	public uploadProfilePic = async <TFile extends Blob = Blob>(file: TFile) => {
		this.mUploadingProfilePic = true;
		const formData = new FormData();
		formData.append(`file`, file);

		const opResult: Api.IOperationResult<Api.IContact> = await this.mUserSession.webServiceHelper.callWebServiceAsync(
			this.composeApiUrl({ urlPath: `contact/${this.entity.id}/profilePic` }),
			'PUT',
			formData
		);

		this.mUploadingProfilePic = false;
		if (!opResult.success) {
			throw opResult;
		}

		const contactModel = { ...this.entity };
		contactModel.profilePic = opResult.value.profilePic;
		this.setEntity(contactModel);
		return contactModel;
	};

	// @action
	// public deleteProfilePic

	public unarchive = (forAll = true) => {
		const promise = new Promise<Api.IOperationResult<Api.IContact>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IContact>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContact>(
				`contact/${encodeURIComponent(this.id)}/unarchive?=${forAll ? 'true' : 'false'}`,
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public suppressKeyFact = (keyFactId: string) => {
		const promise = new Promise<Api.IOperationResult<null>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<null>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<null>(
				`contact/${encodeURIComponent(this.id)}/suppress/${encodeURIComponent(keyFactId)}`,
				'PUT',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public setConnection = (connection: Api.IContactConnection, asOwner = false) => {
		if (!this.isBusy) {
			const mergedConnection: Api.IContactConnection = {
				...(this.entity.connections || []).find(x => x.user.id === connection.user.id),
				...connection,
			};
			const setConnectionPromise = this.mConnections.setConnection(mergedConnection);

			if (setConnectionPromise) {
				this.busy = true;
				return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResultNoValue) => {
						runInAction(() => {
							if (opResult.success) {
								// updated local contact
								const contactModel = { ...this.entity };
								const connections = [...(contactModel.connections || [])];

								if (mergedConnection.user && mergedConnection.user.id) {
									const index = connections.findIndex(x => x.user.id === mergedConnection.user.id);
									if (index >= 0) {
										connections.splice(index, 1, mergedConnection);
									} else {
										connections.push(mergedConnection);
									}
								}

								// update connections
								contactModel.connections = connections;
								this.setEntity(contactModel);

								if (asOwner && contactModel.ownerId !== mergedConnection.user.id) {
									// update owner
									this.mSetOwner(mergedConnection.user).then(resolve).catch(reject);
								} else {
									this.busy = false;
									resolve(opResult);
								}
							} else {
								this.busy = false;
								reject(opResult);
							}
						});
					};
					setConnectionPromise.then(onFinish).catch(onFinish);
				});
			}
		}

		return null;
	};

	public setEntity(entity: Api.IContact) {
		if (!entity.inProgressAutomations) {
			entity.inProgressAutomations = [];
		}
		super.setEntity(entity);
		this.mRelationshipHealth = entity.relationshipHealth;
		this.mConnections = new ContactConnectionsViewModel(this.mUserSession, entity);
		this.kitVm = new KeepInTouchViewModel(this.mUserSession);

		// create tag alerts
		const tagAlerts: TagAlertViewModel[] = [];
		const tagValueToTagAlert: Record<string, TagAlertViewModel> = {};
		(entity && entity.tagsWithAlerts ? entity.tagsWithAlerts : []).forEach(tag => {
			const tagAlert = new TagAlertViewModel(this.mUserSession, { tag });
			tagAlerts.push(tagAlert);
			tagValueToTagAlert[tag.toLocaleLowerCase()] = tagAlert;
		});
		this.mTagAlerts = tagAlerts;
		this.mTagValueToTagAlert = tagValueToTagAlert;
	}

	public addInProgressAutomations = (inProgressAutomations: Api.IInProgressAutomation[]) => {
		const toAdd =
			inProgressAutomations?.filter(
				x => !(this.entity?.inProgressAutomations || []).find(y => y.automationId === x.automationId)
			) || [];
		if (toAdd?.length > 0) {
			this.entity = {
				...this.entity,
				inProgressAutomations: [...(this.entity.inProgressAutomations || []), ...toAdd],
			};
		}
	};

	public cancelInProgressAutomation = (inProgressAutomation: Api.IInProgressAutomation) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<Api.IAutomation>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IAutomation>) => {
					this.busy = false;
					if (opResult.success) {
						const inProgressAutomations = (this.entity.inProgressAutomations || []).filter(
							x => x.automationId !== inProgressAutomation.automationId
						);
						this.entity = {
							...this.entity,
							inProgressAutomations,
						};
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					this.composeApiUrl({ urlPath: `Automation/${inProgressAutomation.automationId}` }),
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});
		}
	};

	public getTagAlertForTagValue = (tagValue?: string) => {
		if (tagValue && this.mTagValueToTagAlert) {
			return this.mTagValueToTagAlert[tagValue.toLocaleLowerCase()];
		}
		return null;
	};

	public reset() {
		super.reset();
		this.mConnections.reset();
		this.mUploadingProfilePic = false;
	}

	public toMention = () => {
		const contact: Api.IContact = {
			firstName: this.entity.firstName,
			id: this.entity.id,
			lastName: this.entity.lastName,
			primaryEmail: this.entity.primaryEmail,
			profilePic: this.entity.profilePic,
		};
		Object.keys(contact).forEach(key => {
			if (!(contact as any)[key]) {
				delete (contact as any)[key];
			}
		});
		return contact;
	};

	public setOwner = (user: Api.IUser) => {
		if (!this.isBusy && user && user.id) {
			this.busy = true;
			return this.mSetOwner(user);
		}

		return null;
	};

	/** Prevent this contact from appearing as a kit suggestion */
	public suppressKeepInTouchSuggestion = () => {
		if (this.entity && this.entity.id && !this.busy) {
			this.busy = true;
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((result: Api.IOperationResultNoValue) => {
					this.busy = false;
					if (result.success) {
						resolve(result);
					} else {
						reject(result);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`contact/${this.entity.id}/suppressKeepInTouchSuggestion`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return null;
	};

	public updateRelationshipHealth = (showBusy = true) => {
		if (!this.isBusy) {
			if (showBusy) {
				this.busy = true;
			}
			const contact = this.entity;
			const promise = new Promise<Api.IRelationshipHealth>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IRelationshipHealth>) => {
					if (showBusy) {
						this.busy = false;
					}
					if (opResult.success) {
						if (contact === this.entity) {
							this.mRelationshipHealth = opResult.value;
							this.entity.relationshipHealth = opResult.value;
						}
						resolve(opResult.value);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IRelationshipHealth>(
					`contact/${this.entity.id}/relationshipHealth`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return null;
	};

	public updateVisibility = (visibility: Api.ResourceVisibility) => {
		if (visibility && !this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IBulkOperationResult<Api.IContact>>((resolve, reject) => {
				const onFinish = action((result: Api.IBulkOperationResult<Api.IContact>) => {
					this.busy = false;
					if (result.success && result.succeeded.find(x => x.id === this.entity.id)) {
						this.entity = {
							...this.entity,
							visibility,
						};
						resolve(result);
					} else {
						reject(result);
					}
				});

				const request: Api.ISetContactVisibilityRequest = {
					ids: [this.entity.id],
					visibility,
				};
				this.mUserSession.webServiceHelper.callWebServiceWithBulkOperationResult<Api.IContact>(
					'contact/visibility',
					'PUT',
					request,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return null;
	};

	public toggleTextSubscriptionForContact = async (unsubscribe: boolean) => {
		if (!this.isBusy) {
			this.busy = true;
			const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IContact>(
				`contact/${this.entity.id}/metadata/automatedSMSOptOut`,
				unsubscribe ? 'PUT' : 'DELETE',
				null
			);
			if (result.success) {
				this.busy = false;
				this.setEntity(result.value);
			}
			return result.value;
		}
		return null;
	};

	public removeKeyDate = (keyFact: Api.IKeyFact) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IOperationResult<Api.IContact>>((resolve, reject) => {
				const onFinish = action((result: Api.IOperationResult<Api.IContact>) => {
					this.busy = false;
					if (result.success) {
						this.setEntity(result.value);
						resolve(result);
					} else {
						reject(result);
					}
				});

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContact>(
					`contact/${this.entity.id}/keyfacts/${keyFact.id}/keyDate`,
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return null;
	};

	public fetchIntegrationData = async () => {
		if (this.mIsFetchingIntegrationData) {
			return;
		}

		this.mIsFetchingIntegrationData = true;
		const value: Api.IContactIntegrationData[] = await this.mUserSession.webServiceHelper.callAsync(
			`contact/${this.contact.id}/integrationData`,
			'GET'
		);
		this.mIsFetchingIntegrationData = false;
		this.mIntegrationData = value;
	};

	protected mSetOwner = (user: Api.IUser) => {
		// intentionally not checking for isBusy
		return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = action((result: Api.IOperationResultNoValue) => {
				this.busy = false;
				if (result.success) {
					// update the contact
					const contactModel = { ...(this.entity || {}) };
					contactModel.ownerId = user.id;
					this.setEntity(contactModel);
					resolve(result);
				} else {
					reject(result);
				}
			});
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				`contact/${this.entity.id}/owner?userId=${user.id}`,
				'PUT',
				null,
				onFinish,
				onFinish
			);
		});
	};

	protected mUpdateTags(method: Api.HTTPMethod, tags?: string[]) {
		const tagsWithAlerts = [...(this.entity.tagsWithAlerts || [])];
		const lowerTagsWithAlerts = tagsWithAlerts.map(x => x.toLocaleLowerCase());

		const promise = super.mUpdateTags(method, tags);
		if (promise && lowerTagsWithAlerts.length > 0) {
			const updatedTagAlerts = [...(this.mTagAlerts || [])];
			const updatedTagsWithAlerts: string[] = [];
			promise.then(() => {
				runInAction(() => {
					// update tag alerts
					const lowerUpdatedTagsCollection = (this.entity.tags || []).map(x => x.toLocaleLowerCase());
					if (lowerUpdatedTagsCollection.length === 0) {
						// remove them all
						this.entity = {
							...this.entity,
							tagsWithAlerts: [],
						};
						this.mTagAlerts = [];
					} else {
						let tagAlertWasRemoved = false;
						lowerTagsWithAlerts.forEach((x, i) => {
							let index = lowerUpdatedTagsCollection.findIndex(y => x === y);
							if (index < 0) {
								// tag was removed... remove tagAlert
								index = updatedTagAlerts.findIndex(y => y.tagValue.toLocaleLowerCase() === x);
								if (index >= 0) {
									updatedTagAlerts.splice(index, 1);
									tagAlertWasRemoved = true;
								}
							} else {
								updatedTagsWithAlerts.push(tagsWithAlerts[i]);
							}
						});

						if (tagAlertWasRemoved) {
							this.entity = {
								...this.entity,
								tagsWithAlerts: updatedTagsWithAlerts,
							};
							this.mTagAlerts = updatedTagAlerts;
						}
					}
				});
			});
		}

		return promise;
	}

	public loadKeepInTouch = () => {
		return this.kitVm.loadByContact(this.contact.id);
	};

	protected getNotesApiQueryPath = () => {
		return `note/byContact/${this.entity.id}`;
	};

	protected getEntityApiPath = () => {
		return 'contact';
	};

	/** Internal setter... intentionally not @action */
	protected setDeleting = (value: boolean) => {
		this.deleting = value;
	};
}

export class SuggestedContactsViewModel extends ViewModel {
	private mSuggestionsPageCollectionController: ObservablePageCollectionController<Api.IContact, ContactViewModel>;
	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			totalNumberOfSuggestions: computed,
			isFetching: computed,
			fetchResults: computed,
			isBusy: override,
			getSuggestions: action,
			reset: action,
		});

		this.mSuggestionsPageCollectionController = new ObservablePageCollectionController({
			apiPath: 'contact/suggestions',
			client: userSession.webServiceHelper,
			transformer: this.getContactViewModelRepresentation,
		});
	}

	public get totalNumberOfSuggestions() {
		return this.mSuggestionsPageCollectionController.totalCount;
	}

	public get isFetching() {
		return this.mSuggestionsPageCollectionController.isFetching;
	}

	public get fetchResults() {
		return this.mSuggestionsPageCollectionController.fetchResults;
	}

	public get isBusy() {
		return this.busy || this.loading || this.mSuggestionsPageCollectionController.isFetching;
	}

	public getSuggestions = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number, params?: any) => {
		return this.mSuggestionsPageCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	public reset = () => {
		this.mSuggestionsPageCollectionController.reset();
		this.busy = false;
	};

	private getContactViewModelRepresentation = (contact: Api.IContact) => {
		return new ContactViewModel(this.mUserSession, contact);
	};
}

export class ContactsViewModel extends ViewModel {
	private deleting: boolean;
	private merging: boolean;
	private mSelectAll: boolean;
	private mLastSuccessfulRequest: Api.IBulkContactsRequest;
	private mLastSuccessfulSortDescriptor: Api.ISortDescriptor;
	private mExcludedContacts: ObservableCollection<ContactViewModel>;
	private mSelectedContacts: ObservableCollection<ContactViewModel>;
	public nextRequest: Api.IBulkContactsRequest;
	private contactsPageCollectionController: FilteredPageCollectionController<
		Api.IContact,
		ContactViewModel,
		Api.IBulkContactsRequest
	>;
	public static SearchCriteriaProperties: Api.ContactFilterCriteriaProperty[] = [
		Api.ContactFilterCriteriaProperty.All,
		Api.ContactFilterCriteriaProperty.Company,
		Api.ContactFilterCriteriaProperty.Email,
		Api.ContactFilterCriteriaProperty.Name,
		Api.ContactFilterCriteriaProperty.Tag,
		Api.ContactFilterCriteriaProperty.WithMailingAddress,
		Api.ContactFilterCriteriaProperty.WithPhoneNumber,
		Api.ContactFilterCriteriaProperty.CreationDate,
		Api.ContactFilterCriteriaProperty.CustomField,
	];
	public static SearchCriteriaFilterProperties: Api.ContactFilterCriteriaProperty[] = [
		Api.ContactFilterCriteriaProperty.Connections,
		Api.ContactFilterCriteriaProperty.CreatedBy,
		Api.ContactFilterCriteriaProperty.InProgressAutomations,
		Api.ContactFilterCriteriaProperty.KeepInTouch,
		Api.ContactFilterCriteriaProperty.OwnedBy,
		Api.ContactFilterCriteriaProperty.Policy,
		Api.ContactFilterCriteriaProperty.PreviousAutomations,
		Api.ContactFilterCriteriaProperty.PrivateContacts,
		Api.ContactFilterCriteriaProperty.TagAlert,
		Api.ContactFilterCriteriaProperty.UpcomingKeyDates,
		Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled,
		Api.ContactFilterCriteriaProperty.WithEmailAddress,
		Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
		Api.ContactFilterCriteriaProperty.WithoutTags,
		Api.ContactFilterCriteriaProperty.CapableOf,
		Api.ContactFilterCriteriaProperty.City,
		Api.ContactFilterCriteriaProperty.StateProvince,
		Api.ContactFilterCriteriaProperty.PostalCode,
		Api.ContactFilterCriteriaProperty.CompanyTag,
		Api.ContactFilterCriteriaProperty.JobTitle,
	];

	public static getAllByIds = (userSession: UserSessionContext, contactIds: string[]) => {
		const promise = new Promise<ContactViewModel[]>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IContact[]>) => {
				if (opResult.success) {
					const contacts = opResult.value.map(x => new ContactViewModel(userSession, x));
					resolve(contacts);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContact[]>(
				'contact/byIds',
				'POST',
				contactIds,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public static addConnection = (
		userSession: UserSessionContext,
		addConnectionRequest: Api.IContactsAddConnectionRequest
	) => {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				'contact/connection',
				'POST',
				addConnectionRequest,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public static exportContacts = (userSession: UserSessionContext, exportRequest: Api.IContactsExportRequest) => {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				'contact/export',
				'POST',
				exportRequest,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<
			ContactsViewModel,
			| 'deleting'
			| 'merging'
			| 'mSelectAll'
			| 'mLastSuccessfulRequest'
			| 'mLastSuccessfulSortDescriptor'
			| 'mExcludedContacts'
			| 'mSelectedContacts'
			| 'nextRequest'
		>(this, {
			deleting: observable,
			merging: observable,
			mSelectAll: observable,
			mLastSuccessfulRequest: observable.ref,
			mLastSuccessfulSortDescriptor: observable.ref,
			mExcludedContacts: observable.ref,
			mSelectedContacts: observable.ref,
			nextRequest: observable.ref,
			filterRequest: computed,
			sortDescriptor: computed,
			isMerging: computed,
			isDeleting: computed,
			isFetchingResults: computed,
			isSearching: computed,
			isBusy: override,
			totalNumberOfResults: computed,
			selectedContacts: computed,
			excludedContacts: computed,
			fetchResults: computed,
			isAllSelected: computed,
			selectionState: computed,
			shouldIncludeFilterInRequest: computed,
			deselectAll: action,
			selectAll: action,
			reset: action,
			getContacts: action,
			merge: action,
			setVisibility: action,
			delete: action,
			deleteMany: action,
			setKeepInTouch: action,
			removeContacts: action,
			assignOwner: action,
		});

		this.mSelectedContacts = new ObservableCollection<ContactViewModel>(null, 'id');
		this.mExcludedContacts = new ObservableCollection<ContactViewModel>(null, 'id');
		this.contactsPageCollectionController = new FilteredPageCollectionController<
			Api.IContact,
			ContactViewModel,
			Api.IBulkContactsRequest
		>({
			apiPath: () => this.composeApiUrl({ urlPath: 'contact/filter/v2' }),
			client: userSession.webServiceHelper,
			transformer: this.mCreateContactViewModel,
		});
	}

	public get filterRequest() {
		return this.mLastSuccessfulRequest;
	}

	public get sortDescriptor() {
		return this.mLastSuccessfulSortDescriptor;
	}

	public get isMerging() {
		return this.merging;
	}

	public get isDeleting() {
		return this.deleting;
	}

	public get isFetchingResults() {
		return this.contactsPageCollectionController.isFetching;
	}

	public get isSearching() {
		const contactsFilterRequest = this.nextRequest || this.mLastSuccessfulRequest;
		if (contactsFilterRequest) {
			return (
				contactsFilterRequest.filter.criteria &&
				!!contactsFilterRequest.filter.criteria.find(
					x => x.property && x.value && ContactsViewModel.SearchCriteriaProperties.indexOf(x.property) >= 0
				)
			);
		}
		return false;
	}

	public get isBusy() {
		return (
			this.contactsPageCollectionController.isFetching || this.busy || this.loading || this.deleting || this.merging
		);
	}

	public get totalNumberOfResults() {
		return this.contactsPageCollectionController.totalCount;
	}

	public get selectedContacts() {
		return this.mSelectedContacts;
	}

	public get excludedContacts() {
		return this.mExcludedContacts;
	}

	public get fetchResults() {
		return this.contactsPageCollectionController.fetchResults;
	}

	public get isAllSelected() {
		return this.mSelectAll;
	}

	public isContactSelected(id: string) {
		const contactVm = new ContactViewModel(this.mUserSession, { id });
		if (this.isAllSelected) {
			return !this.excludedContacts.has(contactVm);
		}
		return this.selectedContacts.has(contactVm);
	}

	public get selectionState(): 'all' | 'some' | 'none' {
		if (this.mSelectAll) {
			return this.mExcludedContacts.length > 0 ? 'some' : 'all';
		}

		return this.mSelectedContacts.length > 0 ? 'some' : 'none';
	}

	public get shouldIncludeFilterInRequest() {
		return this.selectionState === 'all' || (this.selectionState === 'some' && this.excludedContacts.length > 0);
	}

	public deselectAll = () => {
		this.mDeselectAll();
	};

	public selectAll = () => {
		this.mSelectedContacts.clear();
		this.mExcludedContacts.clear();
		this.mSelectAll = true;
	};

	public reset = () => {
		this.busy = false;
		this.contactsPageCollectionController.reset();
		this.deleting = false;
		this.loading = false;
		this.mDeselectAll();
		this.merging = false;
		this.mLastSuccessfulRequest = null;
		this.mLastSuccessfulSortDescriptor = null;
		this.nextRequest = null;
	};

	public getContacts = (
		filter?: Api.IBulkContactsRequest,
		sortDescriptor?: Api.ISortDescriptor,
		pageSize?: number,
		params?: any
	) => {
		// merge params with sortDescriptor
		const computedParams = {
			...(sortDescriptor || {}),
			...(params || {}),
		};

		const promise = this.contactsPageCollectionController.getNext(filter, pageSize, computedParams);
		if (promise) {
			this.nextRequest = filter;
			promise.then(() => {
				if (filter === this.nextRequest) {
					this.mLastSuccessfulRequest = filter;
					this.mLastSuccessfulSortDescriptor = sortDescriptor;
					this.nextRequest = null;
				}
			});
		}
		return promise;
	};

	public merge = (target: ContactViewModel, contacts: ContactViewModel[]) => {
		if (target && target.id && !this.busy) {
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const contactsToDelete = contacts || [];
				this.merging = true;
				this.setDeletingState(contactsToDelete, true);

				const onFinish = (opResult: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.merging = false;
						// delete the succeeded items from collections
						this.mRemoveContacts(contacts);
						this.setDeletingState(contactsToDelete, false);

						if (opResult.success) {
							resolve(opResult);
						} else {
							reject(opResult);
						}
					});
				};

				const listOfContactIds = contactsToDelete.map(x => x.id).filter(x => x);
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<void>(
					`contact/${target.id}/merge`,
					'POST',
					listOfContactIds,
					onFinish,
					onFinish
				);
			});

			return promise;
		}

		return null;
	};

	public setVisibility = (setVisibilityRequest: Api.IContactsSetVisibilityRequest) => {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				'contact/visibility',
				'POST',
				setVisibilityRequest,
				onFinish,
				onFinish
			);
		});

		return promise;
	};

	public delete = (contacts: ContactViewModel[]) => {
		const contactsToDelete = contacts || [];
		if (!this.isBusy && contactsToDelete.length > 0 && contactsToDelete.every(x => !x.isBusy)) {
			const idToContactToDelete = contactsToDelete.reduce<Api.IDictionary<ContactViewModel>>((res, curr) => {
				res[curr.id] = curr;
				return res;
			}, {});

			this.deleting = true;
			this.setDeletingState(contactsToDelete, true);
			const promise = new Promise<Api.IBulkOperationResult<string>>((resolve, reject) => {
				const onFinish = (opResult: Api.IBulkOperationResult<string>) => {
					runInAction(() => {
						this.deleting = false;

						// delete the succeeded items from collections
						const deletedContacts = opResult.success
							? contactsToDelete
							: (opResult.succeeded || []).map(x => idToContactToDelete[x]).filter(x => x);
						this.mRemoveContacts(deletedContacts);
						this.setDeletingState(contactsToDelete, false);

						if (opResult.success) {
							resolve(opResult);
						} else {
							reject(opResult);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithBulkOperationResult<string>(
					'contact',
					'DELETE',
					contactsToDelete.map(x => x.id),
					onFinish,
					onFinish
				);
			});

			return promise;
		}

		return null;
	};

	/** Delete all selected with current filter */
	public deleteMany = async () => {
		if (!this.isBusy) {
			this.deleting = true;
			const request: Api.IBulkContactsRequest = {
				excludeContactIds: this.excludedContacts.map(x => x.id),
				filter: this.filterRequest.filter,
				includeContactIds: this.selectedContacts.map(x => x.id),
			};
			const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<number>(
				this.composeApiUrl({ urlPath: 'contact/delete' }),
				'POST',
				request
			);

			this.deleting = false;
			if (result.success) {
				return result;
			} else {
				throw Api.asApiError(result);
			}
		}
	};

	public setKeepInTouch = (keepInTouch: Partial<Api.IKeepInTouch>, contacts: ContactViewModel[]) => {
		if (!this.busy && contacts && contacts.length > 0 && contacts.every(x => x.id)) {
			const idToContact = contacts.reduce<Api.IDictionary<ContactViewModel>>((res, curr) => {
				res[curr.id] = curr;
				VmUtils.setViewModelBusy(curr, true);
				return res;
			}, {});
			this.busy = true;

			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (opResult: Api.IBulkOperationResult<Api.IKeepInTouch>) => {
					runInAction(() => {
						this.busy = false;
						contacts.forEach(x => VmUtils.setViewModelBusy(x, false));
						if (opResult.success) {
							// set the kits back on the contacts
							(opResult.succeeded || []).forEach(x => {
								const contact = idToContact[x.contactId];
								if (contact && contact.keepInTouch) {
									contact.keepInTouch.setKeepInTouch(x);
								}
							});

							resolve(opResult);
						} else {
							reject(opResult);
						}
					});
				};

				// create the kits using keepInTouch as the base
				const keepInTouches = contacts.map(x => {
					const kit: Api.IKeepInTouch = {
						...keepInTouch,
						contactId: x.id,
					};
					kit.accountId = kit.accountId || this.mUserSession.account.id;
					kit.userId = kit.userId || this.mUserSession.user.id;
					return kit;
				});

				// TODO: don't like this... should be patch and should return a list of contact models... we shouldn't trust the input
				this.mUserSession.webServiceHelper.callWebServiceWithBulkOperationResult<Api.IKeepInTouch>(
					'keepintouch/multiple',
					'POST',
					keepInTouches,
					onFinish,
					onFinish
				);
			});

			return promise;
		}

		return null;
	};

	/** Removes the given contacts from all collections but does not delete them from Levitate */
	public removeContacts = (contacts: ContactViewModel[]) => {
		this.mRemoveContacts(contacts);
	};

	public assignOwner = (userSession: UserSessionContext, assignOwnerRequest: Api.IContactsAssignOwnerRequest) => {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				this.composeApiUrl({ urlPath: 'contact/owner/assign' }),
				'POST',
				assignOwnerRequest,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	/** Uses the current filter to a bulk contact request */
	public getBulkContactRequest = () => {
		const exportRequest: Api.IBulkContactsRequest = {
			excludeContactIds: this.mExcludedContacts.map(this.selectById),
			includeContactIds: this.mSelectedContacts.map(this.selectById),
		};

		// add filter if selecting all or selecting all with exclusions (no explicit selection of contacts)
		if (this.shouldIncludeFilterInRequest) {
			exportRequest.filter = {
				...(this.nextRequest?.filter || this.mLastSuccessfulRequest?.filter || {}),
			};
			exportRequest.ownershipFilter = this.nextRequest?.ownershipFilter || this.mLastSuccessfulRequest?.ownershipFilter;
		}
		return exportRequest;
	};

	public exportContacts = () => {
		return ContactsViewModel.exportContacts(this.mUserSession, this.getBulkContactRequest());
	};

	public unarchiveContacts = () => {
		return this.userSession.webServiceHelper.callAsync<number>(
			this.composeApiUrl({ urlPath: 'contact/unarchive' }),
			'POST',
			this.getBulkContactRequest()
		);
	};

	private selectById = (contactViewModel: ContactViewModel) => contactViewModel.id;

	private mDeselectAll = () => {
		this.mSelectAll = false;
		this.mSelectedContacts.clear();
		this.mExcludedContacts.clear();
	};

	/** Helper method for setting delete state of managed contacts */
	private setDeletingState = (contacts: ContactViewModel[], deleting: boolean) => {
		this.deleting = deleting;
		contacts.forEach(x => {
			// set deleting state... a bit of a hack
			if (Object.prototype.hasOwnProperty.call(x, 'setDeleting')) {
				(x as any).setDeleting(deleting);
			}
		});
	};

	/** Intentionally not an action */
	private mRemoveContacts = (contacts: ContactViewModel[]) => {
		this.contactsPageCollectionController.fetchResults.removeItems(contacts);
		this.selectedContacts.removeItems(contacts);
		this.excludedContacts.removeItems(contacts);
	};

	private mCreateContactViewModel = (contact: Api.IContact) => {
		return new ContactViewModel(this.mUserSession, contact);
	};
}

export class CompanyViewModel extends EntityViewModel<Api.ICompany> {
	private merging: boolean;
	public fetchingContacts: boolean;
	public contacts: ContactViewModel[];
	private contactsPageCollectionController: Api.IPageCollectionController<Api.IContact>;

	constructor(userSession: UserSessionContext, company: Api.ICompany) {
		super(userSession, company);

		makeObservable<CompanyViewModel, 'merging' | 'company'>(this, {
			merging: observable,
			fetchingContacts: observable,
			contacts: observable.ref,
			isBusy: override,
			isMerging: computed,
			isLoaded: override,
			emailDomain: computed,
			emailDomains: computed,
			address: computed,
			logoUrl: computed,
			timeZone: computed,
			timeZoneAbbr: computed,
			setTimezone: action,
			reset: action,
			getContacts: action,
			merge: action,
			company: computed,
		});

		this.contactsPageCollectionController = new Api.PageCollectionController(
			userSession.webServiceHelper,
			`contact/byCompany/${company.id}`
		);
	}

	public get isBusy() {
		return this.busy || this.saving || this.loading || this.merging || this.deleting;
	}

	public get isMerging() {
		return this.merging;
	}

	public get isLoaded() {
		return this.validEntity ? !!this.entity.companyName : true;
	}

	public get emailDomain() {
		return this.company.emailDomain;
	}

	public get emailDomains() {
		return this.company.emailDomains;
	}

	public get address() {
		return this.company.address;
	}

	public get logoUrl() {
		return this.company.logoUrl;
	}

	public get timeZone() {
		return this.company.timeZone;
	}

	public get timeZoneAbbr() {
		// TODO: these cases are wrong
		switch (this.timeZone) {
			case 'Eastern Standard Time':
			case 'US/Eastern':
				return 'EST';
			case 'Central Standard Time':
			case 'US/Central':
				return 'CST';
			case 'Mountain Standard Time':
			case 'US/Mountain':
				return 'MST';
			case 'Hawaii Standard Time':
			case 'US/Hawaii':
				return 'HST';
			case 'Alaska Standard Time':
			case 'US/Alaska':
				return 'AKST';
			case 'Pacific Standard Time':
			case 'US/Pacific':
				return 'PST';
			default:
				return this.timeZone ?? 'Unknown';
		}
	}

	public setTimezone = (timeZone: string) => {
		this.entity = { ...this.entity, timeZone };
	};

	public reset() {
		super.reset();
		this.contacts = null;
		this.contactsPageCollectionController.reset();
		this.deleting = false;
		this.fetchingContacts = false;
		this.merging = false;
	}

	public getContacts = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number) => {
		if (this.contactsPageCollectionController.hasAllPages(sortDescriptor, pageSize)) {
			return getEmptyPageControllerResolvedPromise<Api.IContact>();
		}

		this.fetchingContacts = true;
		const promise = this.contactsPageCollectionController.getNext(sortDescriptor, pageSize);
		promise
			.then(result => {
				runInAction(() => {
					const fetchResults = result.values.map(x => new ContactViewModel(this.mUserSession, x));
					this.contacts = result.fetchedFirstPage
						? fetchResults
						: fetchResults.length > 0
							? [...this.contacts, ...fetchResults]
							: this.contacts;
					this.fetchingContacts = false;
				});
			})
			.catch(() => {
				runInAction(() => {
					this.contacts = [];
					this.fetchingContacts = false;
				});
			});
		return promise;
	};

	/**
	 * Merge companies with the receiver.
	 *
	 * @param companies Companies to merge with. The receiver will update with the single resulting company
	 */
	public merge = (companies: CompanyViewModel[]) => {
		if (!this.merging && this.company && this.company.id) {
			const companiesToMerge = (companies || []).filter(x => x && x.id && x.id !== this.company.id && !x.merging);
			if (companiesToMerge.length > 0) {
				this.merging = true;
				companiesToMerge.forEach(x => (x.merging = true));
				const promise = new Promise<Api.ICompany>((resolve, reject) => {
					const onFinish = (result: Api.IOperationResult<Api.ICompany>) => {
						runInAction(() => {
							this.merging = false;
							companiesToMerge.forEach(x => (x.merging = false));
							if (result.success) {
								this.setEntity(result.value);
								resolve(result.value);
							} else {
								reject(result);
							}
						});
					};
					this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICompany>(
						`company/${this.company.id}/merge`,
						'POST',
						// first company id is what the others merge into
						companiesToMerge.map(x => x.id),
						onFinish,
						onFinish
					);
				});
				return promise;
			}
		}
		return null;
	};

	public toMention = () => {
		const company: Api.ICompany = {
			companyName: this.entity.companyName,
			emailDomain: this.entity.emailDomain,
			id: this.entity.id,
			logoUrl: this.entity.logoUrl,
		};
		Object.keys(company).forEach(key => {
			if (!(company as any)[key]) {
				delete (company as any)[key];
			}
		});
		return company;
	};

	protected get company() {
		return this.entity as Api.ICompany;
	}

	protected getNotesApiQueryPath = () => {
		return `note/byCompany/${this.entity.id}`;
	};

	protected getEntityApiPath = () => {
		return 'company';
	};

	/** Internal setter... intentionally not @action */
	protected setDeleting = (value: boolean) => {
		this.deleting = value;
	};

	/** Internal setter... intentionally not @action */
	protected setMerging = (value: boolean) => {
		this.merging = value;
	};
}

export type ActionItemLoadFilter = 'all' | 'assignedOnly' | 'upcomingOnly';

export class ActionItemsViewModel extends ViewModel {
	public searchQuery: string;
	private assignedOnly: boolean;
	private completedItemsPageCollectionController: FilteredPageCollectionController<
		Api.IActionItem,
		ActionItemViewModel
	>;
	private inProgressItemsPageCollectionController: FilteredPageCollectionController<
		Api.IActionItem,
		ActionItemViewModel,
		Api.IRichContentRequest
	>;
	private searchPageCollectionController: FilteredPageCollectionController<
		Api.IActionItem,
		ActionItemViewModel,
		Api.IRichContentRequest
	>;

	constructor(userSession: UserSessionContext, filter?: ActionItemLoadFilter) {
		super(userSession);

		makeObservable(this, {
			searchQuery: observable,
			isSearching: computed,
			fetchingCompletedActionItems: computed,
			fetchingInProgressActionItems: computed,
			fetchingSearchResults: computed,
			completedActionItems: computed,
			inProgressActionItems: computed,
			searchResults: computed,
			isBusy: override,
			reset: action,
			resetSearch: action,
		});

		this.inProgressItemsPageCollectionController = new FilteredPageCollectionController<
			Api.IActionItem,
			ActionItemViewModel
		>({
			apiPath: 'actionitem/filter',
			client: userSession.webServiceHelper,
			transformer: this.createActionItemVm,
			httpMethod: 'POST',
		});

		if (filter && filter !== 'all') {
			this.assignedOnly = filter === 'assignedOnly';
			if (filter === 'upcomingOnly') {
				this.inProgressItemsPageCollectionController = new FilteredPageCollectionController<
					Api.IActionItem,
					ActionItemViewModel
				>({
					apiPath: 'actionitem/upcoming',
					client: userSession.webServiceHelper,
					transformer: this.createActionItemVm,
				});
			}
		}

		this.completedItemsPageCollectionController = new FilteredPageCollectionController<
			Api.IActionItem,
			ActionItemViewModel
		>({
			apiPath: 'actionitem/filter',
			client: userSession.webServiceHelper,
			transformer: this.createActionItemVm,
			httpMethod: 'POST',
		});

		this.searchPageCollectionController = new FilteredPageCollectionController<Api.IActionItem, ActionItemViewModel>({
			apiPath: 'actionitem/filter',
			client: userSession.webServiceHelper,
			transformer: this.createActionItemVm,
			httpMethod: 'POST',
		});
	}

	public get isSearching() {
		return (
			this.searchPageCollectionController.isFetching ||
			this.searchPageCollectionController.hasContext ||
			this.searchPageCollectionController.hasFetchedFirstPage
		);
	}

	public get fetchingCompletedActionItems() {
		return this.completedItemsPageCollectionController.isFetching;
	}

	public get fetchingInProgressActionItems() {
		return this.inProgressItemsPageCollectionController.isFetching;
	}

	public get fetchingSearchResults() {
		return this.searchPageCollectionController.isFetching;
	}

	public get completedActionItems() {
		return this.completedItemsPageCollectionController.fetchResults;
	}

	public get inProgressActionItems() {
		return this.inProgressItemsPageCollectionController.fetchResults;
	}

	public get searchResults() {
		return this.searchPageCollectionController.fetchResults;
	}

	public get isBusy() {
		return (
			this.inProgressItemsPageCollectionController.isFetching ||
			this.completedItemsPageCollectionController.isFetching ||
			this.searchPageCollectionController.isFetching
		);
	}

	public reset = () => {
		this.completedItemsPageCollectionController.reset();
		this.inProgressItemsPageCollectionController.reset();
		this.searchPageCollectionController.reset();
		this.searchQuery = null;
	};

	public resetSearch = () => {
		this.searchPageCollectionController.reset();
		this.searchQuery = null;
	};

	public removeActionItems = (actionItems: ActionItemViewModel[], completed: boolean) => {
		if (completed) {
			this.completedItemsPageCollectionController.fetchResults.removeItems(actionItems);
		} else {
			this.inProgressItemsPageCollectionController.fetchResults.removeItems(actionItems);
		}
	};

	public getInProgressItems = (request?: Api.IRichContentRequest, sort?: Api.ISortDescriptor, pageSize?: number) => {
		const params = { assignedOnly: this.assignedOnly, ...(sort || {}) };
		return this.inProgressItemsPageCollectionController.getNext(request, pageSize, params);
	};

	public getCompletedItems = (request?: Api.IRichContentRequest, sort?: Api.ISortDescriptor, pageSize?: number) => {
		const params = { assignedOnly: this.assignedOnly, ...(sort || {}) };
		return this.completedItemsPageCollectionController.getNext(request, pageSize, params);
	};

	public toggleComplete = (actionItem: ActionItemViewModel, complete?: boolean) => {
		const promise = actionItem.toggleComplete(complete);
		promise.then(() => {
			// remove the item from the list it came from
			if (complete) {
				this.inProgressItemsPageCollectionController.fetchResults.removeItems([actionItem]);
			} else {
				this.completedItemsPageCollectionController.fetchResults.removeItems([actionItem]);
			}
		});
		return promise;
	};

	public filterItems = (
		filterRequest: Api.IRichContentRequest,
		pageSize?: number,
		sortOption?: NotesSortOptionValue
	) => {
		const query = (this.searchQuery || '').trim();
		if (query) {
			filterRequest.criteria?.push({
				property: Api.RichContentProperty.Content,
				value: query,
			});
		}
		// if filterRequest doesnt have criteria add ALL
		if (!filterRequest.criteria || filterRequest.criteria.length === 0) {
			filterRequest.criteria?.push({
				property: Api.RichContentProperty.All,
				value: 'All',
			});
		}
		const options = this.getOptions(sortOption);
		if (filterRequest.criteria?.includes({ property: Api.RichContentProperty.Content, value: query })) {
			return this.searchPageCollectionController.getNext(filterRequest, pageSize, {
				sort: options.sort,
				sortBy: options.sortBy,
			});
		}

		return filterRequest.criteria?.find(x => x.property === Api.RichContentProperty.IsCompleted && x.value === 'true')
			? this.completedItemsPageCollectionController.getNext(filterRequest, pageSize, {
					sort: options.sort,
					sortBy: options.sortBy,
				})
			: this.inProgressItemsPageCollectionController.getNext(filterRequest, pageSize, {
					sort: options.sort,
					sortBy: options.sortBy,
				});
	};

	private createActionItemVm = (actionItemModel: Api.IActionItem) => {
		return new ActionItemViewModel(this.userSession, actionItemModel);
	};

	private getOptions = (sortOption?: NotesSortOptionValue) => {
		let newSort = '';
		let sortBy = '';
		switch (sortOption) {
			case 'LastModifiedDate-asc':
				newSort = 'asc';
				sortBy = 'lastModifiedDate';
				break;
			case 'LastModifiedDate-dsc':
				newSort = 'desc';
				sortBy = 'lastModifiedDate';
				break;
			case 'CreationDate-asc':
				newSort = 'asc';
				sortBy = 'creationDate';
				break;
			case 'CreationDate-dsc':
				newSort = 'desc';
				sortBy = 'creationDate';
				break;
			case 'DueDate-asc':
				newSort = 'asc';
				sortBy = 'dueDate';
				break;
			case 'DueDate-dsc':
				newSort = 'desc';
				sortBy = 'dueDate';
				break;
			default:
				break;
		}
		return { sort: newSort, sortBy };
	};
}

export class ContactConnectionsViewModel extends ViewModel {
	public fetchingConnections: boolean;
	mContact: Api.IContact;
	private connectionsPageCollectionController: ObservablePageCollectionControllerOld<
		Api.IContactConnection,
		Api.IContactConnection
	>;

	constructor(userSession: UserSessionContext, contact: Api.IContact) {
		super(userSession);

		makeObservable(this, {
			fetchingConnections: observable,
			mContact: observable.ref,
			connections: computed,
			reset: action,
			setConnection: action,
			removeConnectionToUser: action,
		});

		this.mContact = contact;
		this.connectionsPageCollectionController = new ObservablePageCollectionControllerOld<
			Api.IContactConnection,
			Api.IContactConnection
		>(userSession.webServiceHelper, `contact/${contact.id}/connections`, null, null);
	}

	public get connections() {
		return this.connectionsPageCollectionController.fetchResults;
	}

	public reset() {
		this.connectionsPageCollectionController.reset();
		this.fetchingConnections = false;
	}

	public getConnections = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number) => {
		return this.connectionsPageCollectionController.getNext(sortDescriptor, pageSize);
	};

	/**
	 * Sets (adds/updates) a connection with the receiver. If this is an update, the vm will handle replacing the
	 * connection in the list
	 */
	public setConnection = (connection: Api.IContactConnection) => {
		return this.editContactConnections('PUT', connection, () => {
			const matchingIndex = (this.connectionsPageCollectionController.fetchResults || []).findIndex(
				x => x.user.id === connection.user.id
			);
			if (matchingIndex >= 0) {
				// replace
				const fetchResults = [...this.connectionsPageCollectionController.fetchResults];
				fetchResults.splice(matchingIndex, 1, connection);
				this.connectionsPageCollectionController.fetchResults = fetchResults;
			}
		});
	};

	/** The vm will handle removing the connection from the list */
	public removeConnectionToUser = (user: Api.IUser) => {
		const connection: Api.IContactConnection = { user };
		return this.editContactConnections('DELETE', connection, () => {
			const matchingIndex = (this.connectionsPageCollectionController.fetchResults || []).findIndex(
				x => x.user.id === connection.user.id
			);
			if (matchingIndex >= 0) {
				// remove
				const fetchResults = [...this.connectionsPageCollectionController.fetchResults];
				fetchResults.splice(matchingIndex, 1);
				this.connectionsPageCollectionController.fetchResults = fetchResults;
			}
		});
	};

	/** Intentionally not an action */
	private editContactConnections = (
		method: 'DELETE' | 'PUT',
		connection: Api.IContactConnection,
		onSuccess?: () => void
	) => {
		if (connection && connection.user && connection.user.id && !this.busy) {
			this.busy = true;
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((result: Api.IOperationResultNoValue) => {
					this.busy = false;
					if (result.success) {
						if (onSuccess) {
							onSuccess();
						}
						resolve(result);
					} else {
						reject(result);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`contact/${this.mContact.id}/connections/${connection.user.id}`,
					method,
					method !== 'DELETE' ? connection : null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}

		return null;
	};
}

export type QuickAddEntityCompletion = (error: Api.IOperationResultNoValue, entity?: Api.IEntity) => void;

/** @returns `false` to indicate the default action (onComplete) should be prevented. */
export type QuickAddEntityErrorHandler = (error: Api.IOperationResultNoValue) => boolean;

export interface IQuickAddEntityContactCreateOptions {
	requireEmail?: boolean;
	requireFirstName?: boolean;
	requireJobTitle?: boolean;
	requireLastName?: boolean;
}

export interface IQuickAddEntityShowOptions {
	contactCreateOptions?: IQuickAddEntityContactCreateOptions;
	entity?: Api.IEntity;
	onComplete?: QuickAddEntityCompletion;
	onError?: QuickAddEntityErrorHandler;
	type: 'contact' | 'company';
}

export class QuickAddEntityViewModel {
	public companyName: string;
	public emailAddress: string;
	public emailAddressLabel: string;
	public emailDomain: string;
	public entityType: 'contact' | 'company';
	public firstName: string;
	public newPhoneNumber: string;
	public phoneNumbers: Api.IPhoneNumber[] = [];
	public isBusy: boolean;
	public isOpen: boolean;
	public jobTitle: string;
	public lastName: string;
	public publicVisibility: boolean;
	private userSession: UserSessionContext;
	public contactCreateOptions: IQuickAddEntityContactCreateOptions;

	private createContactPromise: Promise<Api.IOperationResult<Api.IContact>>;
	private createCompanyPromise: Promise<Api.IOperationResult<Api.ICompany>>;

	private onComplete: QuickAddEntityCompletion;
	private onError: QuickAddEntityErrorHandler;
	public defaultOnErrorHandler: QuickAddEntityErrorHandler;

	constructor(userSession?: UserSessionContext) {
		makeObservable<QuickAddEntityViewModel, 'userSession' | 'createContact' | 'createCompany'>(this, {
			companyName: observable,
			emailAddress: observable,
			emailAddressLabel: observable,
			emailDomain: observable,
			entityType: observable,
			firstName: observable,
			newPhoneNumber: observable,
			phoneNumbers: observable.ref,
			isBusy: observable,
			isOpen: observable,
			jobTitle: observable,
			lastName: observable,
			publicVisibility: observable,
			userSession: observable.ref,
			contactCreateOptions: observable.ref,
			show: action,
			reset: action,
			close: action,
			setUserSession: action,
			canCreate: computed,
			save: action,
			createContact: action,
			createCompany: action,
		});

		this.userSession = userSession;
		this.publicVisibility = true;
		this.reset();
	}

	public show = (options: IQuickAddEntityShowOptions) => {
		this.reset();
		this.entityType = options.type;
		const defaultVisibilityIsPublic = VmUtils.getDefaultVisibility(this.userSession.user) === 'all';

		if (options.entity) {
			this.companyName = options.entity.companyName;

			if (options.type === 'contact') {
				const contact: Api.IContact = options.entity;
				this.emailAddress = contact.primaryEmail ? contact.primaryEmail.value : null;
				this.firstName = contact.firstName;
				this.jobTitle = contact.jobTitle;
				this.lastName = contact.lastName;
				this.publicVisibility = Object.prototype.hasOwnProperty.call(contact, 'visibility')
					? contact.visibility === 'all'
					: defaultVisibilityIsPublic;
			} else {
				const company: Api.ICompany = options.entity;
				this.emailDomain = company.emailDomain;
			}
		} else {
			if (options.type === 'contact') {
				this.publicVisibility = defaultVisibilityIsPublic;
			}
		}

		this.isOpen = true;
		this.onComplete = options.onComplete;
		this.onError = options.onError;
		this.contactCreateOptions = options.contactCreateOptions;
	};

	public reset() {
		this.companyName = null;
		this.contactCreateOptions = null;
		this.emailAddress = null;
		this.emailAddressLabel = null;
		this.emailDomain = null;
		this.entityType = 'contact';
		this.firstName = null;
		this.isOpen = false;
		this.jobTitle = null;
		this.lastName = null;
		this.onComplete = null;
		this.onError = null;
		this.publicVisibility = true;
		this.phoneNumbers = [];
		this.newPhoneNumber = null;
	}

	public close = () => {
		this.reset();
	};

	public setUserSession = (userSession: UserSessionContext) => {
		this.userSession = userSession;
	};

	public get canCreate() {
		if (!this.userSession) {
			return false;
		}

		if (this.entityType === 'contact') {
			let canCreate = (this.firstName && !!this.lastName) || !!this.emailAddress;
			if (canCreate && this.contactCreateOptions) {
				if (
					Object.prototype.hasOwnProperty.call(this.contactCreateOptions, 'requireEmail') &&
					this.contactCreateOptions.requireEmail &&
					!this.emailAddress
				) {
					canCreate = false;
				}
			}
			return canCreate;
		}

		return !!this.companyName;
	}

	public save = () => {
		return this.entityType === 'company' ? this.createCompany() : this.createContact();
	};

	private createContact = () => {
		if (this.isBusy) {
			return;
		}

		const visibility = this.publicVisibility ? 'all' : 'admin';
		this.isBusy = true;
		const emailAddresses: Api.EmailAddress[] = this.emailAddress
			? [{ label: this.emailAddressLabel, value: this.emailAddress }]
			: null;
		let phoneNumbers: Api.IPhoneNumber[] = this.phoneNumbers;
		if (this.newPhoneNumber) {
			phoneNumbers = [...phoneNumbers, { value: this.newPhoneNumber }];
		}
		this.createContactPromise = this.create<Api.IContact>('contact', {
			emailAddresses,
			firstName: this.firstName,
			jobTitle: this.jobTitle,
			lastName: this.lastName,
			phoneNumbers,
			visibility,
		});

		return this.createContactPromise;
	};

	private createCompany = () => {
		if (this.isBusy) {
			return;
		}
		this.isBusy = true;
		this.createCompanyPromise = this.create<Api.ICompany>('company', {
			companyName: this.companyName,
			emailDomain: this.emailDomain,
		});

		return this.createCompanyPromise;
	};

	private create = <T extends Api.IEntity>(type: 'company' | 'contact', entity: T) => {
		const url = type === 'contact' ? 'contact?allowDuplicateEmail=true' : type;
		return new Promise((resolve, reject) =>
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEntity>(
				url,
				'POST',
				entity,
				result => {
					runInAction(() => {
						this.isBusy = false;
						resolve(result);
						if (this.onComplete) {
							this.onComplete(null, result.value);
						}
						this.reset();
					});
				},
				error => {
					runInAction(() => {
						this.isBusy = false;
						let finish = true;
						const onErrorHandler = this.onError || this.defaultOnErrorHandler;
						if (onErrorHandler) {
							finish = onErrorHandler(error);
						}

						if (finish) {
							if (this.onComplete) {
								this.onComplete(error);
							}
							this.reset();
						}
						reject(error);
					});
				}
			)
		);
	};
}

export class MeetingAttendee {
	mContact: ContactViewModel;
	mMeetingAttendee: Api.IMeetingAttendee;

	constructor(userSession: UserSessionContext, meetingAttendee: Api.IMeetingAttendee) {
		makeObservable(this, {
			mContact: observable.ref,
			mMeetingAttendee: observable.ref,
			isOrganizer: computed,
			isOptional: computed,
			isResource: computed,
			contact: computed,
		});

		this.mMeetingAttendee = meetingAttendee;
		if (meetingAttendee.contact) {
			this.mContact = new ContactViewModel(userSession, meetingAttendee.contact);
		}
	}

	public get isOrganizer() {
		return this.mMeetingAttendee.organizer;
	}

	public get isOptional() {
		return this.mMeetingAttendee.optional;
	}

	public get isResource() {
		return this.mMeetingAttendee.resource;
	}

	public get contact() {
		return this.mContact;
	}

	public toJs = () => {
		return this.mMeetingAttendee;
	};
}

export class MeetingViewModel {
	public dateFormat = 'MM/DD/YY';
	public dayFormat = 'dddd';
	public timeFormat = 'h:mm A';
	private mAttendees: MeetingAttendee[];
	private mEndTime: Date;
	private mMeeting: Api.IMeeting;
	private mStartTime: Date;
	protected userSession: UserSessionContext;

	constructor(userSession: UserSessionContext, meeting: Api.IMeeting) {
		makeObservable<MeetingViewModel, 'mAttendees' | 'mEndTime' | 'mMeeting' | 'mStartTime' | 'setMeeting'>(this, {
			dateFormat: observable,
			dayFormat: observable,
			timeFormat: observable,
			mAttendees: observable.ref,
			mEndTime: observable.ref,
			mMeeting: observable.ref,
			mStartTime: observable.ref,
			id: computed,
			recurringId: computed,
			iCalUid: computed,
			summary: computed,
			startTime: computed,
			endTime: computed,
			isBusyDuringMeeting: computed,
			isAllDay: computed,
			attendees: computed,
			durationStringValue: computed,
			setMeeting: action,
		});

		this.userSession = userSession;
		this.setMeeting(meeting);
	}

	public get id() {
		return this.mMeeting.id;
	}

	public get recurringId() {
		return this.mMeeting.recurringId;
	}

	public get iCalUid() {
		return this.mMeeting.iCalUid;
	}

	public get summary() {
		return this.mMeeting.summary;
	}

	public get startTime() {
		return this.mStartTime;
	}

	public get endTime() {
		return this.mEndTime;
	}

	public get isBusyDuringMeeting() {
		return this.mMeeting.isBusy;
	}

	public get isAllDay() {
		return this.mMeeting.isAllDay;
	}

	public get attendees() {
		return this.mAttendees;
	}

	public get durationStringValue() {
		if (this.mMeeting) {
			if (this.mMeeting.isAllDay) {
				return moment(this.startTime).format(this.dateFormat);
			} else {
				const today = moment();
				const endOfToday = today.endOf('day');
				const endOfTomorrow = today.add(1, 'day').endOf('day');

				const startMoment = moment(this.startTime);
				const endMoment = moment(this.endTime);

				const timeRangeStringValue = `${startMoment.format(this.timeFormat)} - ${endMoment.format(this.timeFormat)}`;
				let dayStringValue: string = null;
				if (startMoment < endOfToday) {
					dayStringValue = 'TODAY';
				} else if (startMoment < endOfTomorrow) {
					dayStringValue = 'TOMORROW';
				} else {
					dayStringValue = startMoment.format(this.dayFormat).toUpperCase();
				}

				return `${dayStringValue} ${timeRangeStringValue}`;
			}
		}

		return null;
	}

	public toJs = () => {
		return this.mMeeting;
	};

	protected setMeeting(meeting: Api.IMeeting) {
		this.mMeeting = meeting;
		this.mStartTime = meeting.startTime ? new Date(meeting.startTime) : null;
		this.mEndTime = meeting.endTime ? new Date(meeting.endTime) : null;
		this.mAttendees = (meeting.attendees || []).map(x => new MeetingAttendee(this.userSession, x));
	}
}

export class RecentMeetingViewModel extends MeetingViewModel {
	private mNote: NoteViewModel;

	constructor(userSession: UserSessionContext, recentMeeting: Api.IMeeting) {
		super(userSession, recentMeeting);

		makeObservable<RecentMeetingViewModel, 'mNote' | 'setMeeting'>(this, {
			mNote: observable.ref,
			note: computed,
			setMeeting: override,
		});

		// Call setMeeting again to ensure the override is executed and mNote is initialized
		// This is necessary because the parent constructor calls setMeeting before our override is defined
		this.setMeeting(recentMeeting);
	}

	public get note() {
		return this.mNote;
	}

	public suppress = () => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const suppress: Api.IDashboardSuppress = {
				context: Api.DashboardSuppressContext.RecentMeeting,
				id: this.id,
			};

			const onFinish = (result: Api.IOperationResultNoValue) => {
				if (result.success) {
					resolve(result);
				} else {
					reject(result);
				}
			};

			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				'dashboard/suppress',
				'POST',
				suppress,
				onFinish,
				onFinish
			);
		});

		return promise;
	};

	protected setMeeting(recentMeeting: Api.IMeeting) {
		super.setMeeting(recentMeeting);

		// create a note using meeting meta
		const noteModel: Api.INote = {
			context: {
				name: recentMeeting.summary,
				source: Api.RichContentContextSource.Meeting,
			},
			referencedEntities: {
				companies: [],
				contacts: (recentMeeting.attendees || [])
					.filter(x => x.contact)
					.map(attendee => {
						const ref: Api.IRichContentEntityReference<Api.IContact> = {
							entity: attendee.contact,
							method: Api.RichContentReferenceMethod.Implicit,
						};
						return ref;
					}),
				users: [],
			},
		};

		this.mNote = new NoteViewModel(this.userSession, noteModel);
	}
}

export class ClassifyContactViewModel {
	private mContact: ContactViewModel;
	private mMostRecentInteraction: {
		interactionDate?: Date;
		interactionType: Api.InteractionType;
	};
	private mSuggestedTags: string[];
	private mRecentEmailSubjects: string[];
	private mUserSession: UserSessionContext;

	constructor(userSession: UserSessionContext, model?: Api.IClassifyContact) {
		makeObservable<
			ClassifyContactViewModel,
			'mContact' | 'mMostRecentInteraction' | 'mSuggestedTags' | 'mRecentEmailSubjects'
		>(this, {
			mContact: observable.ref,
			mMostRecentInteraction: observable.ref,
			mSuggestedTags: observable.ref,
			mRecentEmailSubjects: observable.ref,
			id: computed,
			contact: computed,
			suggestedTags: computed,
			recentEmailSubjects: computed,
			mostRecentInteraction: computed,
		});

		this.mUserSession = userSession;
		this.mSetModel(model);
	}

	public get id() {
		return this.mContact ? this.mContact.id : null;
	}

	public get contact() {
		return this.mContact;
	}

	public get suggestedTags() {
		return this.mSuggestedTags;
	}

	public get recentEmailSubjects() {
		return this.mRecentEmailSubjects;
	}

	public get mostRecentInteraction() {
		return this.mMostRecentInteraction;
	}

	private mSetModel = (model: Api.IClassifyContact) => {
		if (model && model.contact) {
			this.mContact = new ContactViewModel(this.mUserSession, model.contact);
			this.mSuggestedTags = [...(model.suggestedTags || [])];
			this.mMostRecentInteraction = model.mostRecentInteraction
				? {
						interactionDate: model.mostRecentInteraction.interactionDate
							? new Date(model.mostRecentInteraction.interactionDate)
							: null,
						interactionType: model.mostRecentInteraction.interactionType,
					}
				: null;
			this.mRecentEmailSubjects = model.recentEmailSubjects ?? [];
		} else {
			this.mSuggestedTags = [];
			this.mContact = null;
			this.mMostRecentInteraction = null;
			this.mRecentEmailSubjects = [];
		}
	};
}

export class EmailActivityViewModel {
	private mContact: ContactViewModel;
	private mOpenDate: Date;
	private mRepliedDate: Date;
	private mSentContent: NoteViewModel;
	private mSentDate: Date;
	private mEmailActivity: Api.IEmailActivity;
	private mUserSession: UserSessionContext;

	constructor(userSession: UserSessionContext, emailActivity?: Api.IEmailActivity) {
		makeObservable<
			EmailActivityViewModel,
			'mContact' | 'mOpenDate' | 'mRepliedDate' | 'mSentContent' | 'mSentDate' | 'mEmailActivity'
		>(this, {
			mContact: observable.ref,
			mOpenDate: observable.ref,
			mRepliedDate: observable.ref,
			mSentContent: observable.ref,
			mSentDate: observable.ref,
			mEmailActivity: observable.ref,
			id: computed,
			contact: computed,
			openDate: computed,
			repliedDate: computed,
			sentContent: computed,
			sentDate: computed,
			status: computed,
		});

		this.mUserSession = userSession;
		this.mSetModel(emailActivity);
	}

	public get id() {
		return this.mEmailActivity ? this.mEmailActivity.id : null;
	}

	public get contact() {
		return this.mContact;
	}

	public get openDate() {
		return this.mOpenDate;
	}

	public get repliedDate() {
		return this.mRepliedDate;
	}

	public get sentContent() {
		return this.mSentContent;
	}

	public get sentDate() {
		return this.mSentDate;
	}

	public get status() {
		return this.mEmailActivity ? this.mEmailActivity.status : null;
	}

	private mSetModel = (emailActivity?: Api.IEmailActivity) => {
		this.mEmailActivity = emailActivity;
		if (this.mEmailActivity) {
			this.mContact = emailActivity.contact ? new ContactViewModel(this.mUserSession, emailActivity.contact) : null;
			this.mSentContent = emailActivity.sentContent
				? new NoteViewModel(this.mUserSession, emailActivity.sentContent)
				: null;
			this.mOpenDate = emailActivity.openDate ? new Date(emailActivity.openDate) : null;
			this.mRepliedDate = emailActivity.repliedDate ? new Date(emailActivity.repliedDate) : null;
			this.mSentDate = emailActivity.sentDate ? new Date(emailActivity.sentDate) : null;
		} else {
			this.mContact = null;
			this.mSentContent = null;
			this.mOpenDate = null;
			this.mRepliedDate = null;
			this.mSentDate = null;
		}
	};
}

export interface IDashboardReachOutInfo {
	contactsWithKeepInTouchesDue: ContactViewModel[];
	tagAlerts: TagAlertViewModel[];
	totalCount: number;
}

export class KeepInTouchReferenceViewModel extends ViewModel {
	private mFrequency: number;
	private mId: string;
	private mContact: ContactViewModel;
	private keepInTouchReference: Api.IKeepInTouchReference;

	constructor(userSession: UserSessionContext, keepInTouchReference: Api.IKeepInTouchReference) {
		super(userSession);

		makeObservable<
			KeepInTouchReferenceViewModel,
			'mFrequency' | 'mId' | 'mContact' | 'keepInTouchReference' | 'setKeepInTouchReference'
		>(this, {
			mFrequency: observable,
			mId: observable,
			mContact: observable.ref,
			keepInTouchReference: observable.ref,
			contact: computed,
			id: computed,
			frequency: computed,
			setKeepInTouchReference: action,
		});

		this.setKeepInTouchReference(keepInTouchReference);
	}

	public get contact() {
		return this.mContact;
	}

	public get id() {
		return this.mId;
	}

	public get frequency() {
		return this.mFrequency;
	}

	public toJs = () => {
		return this.keepInTouchReference;
	};

	private setKeepInTouchReference = (keepInTouchReference: Api.IKeepInTouchReference) => {
		if (keepInTouchReference) {
			this.mContact = keepInTouchReference.contact
				? new ContactViewModel(this.mUserSession, keepInTouchReference.contact)
				: null;
			this.mId = keepInTouchReference.id;
			this.mFrequency = keepInTouchReference.frequency || -1;
		} else {
			this.mId = null;
			this.mContact = null;
			this.mFrequency = -1;
		}
		this.keepInTouchReference = keepInTouchReference;
	};
}

/** Base email view model for all things email Can be used for an email that needs to be customized per recipient */
export class EmailMessageViewModel<
		TAttachmentType extends FileWithExtensionsType = FileWithExtensionsType,
		TSendResult = Api.ISendEmailResponse,
		TFollowUpOptions extends Api.IFollowUpOptions = Api.IEmailMessageFollowUpOptions,
	>
	extends ViewModel
	implements Api.IEmailMessage<TFollowUpOptions>
{
	public aiReference: Api.IAIReference;
	public contactsFilterRequest: Api.IEmailMessageContactsFilterRequest;
	public templateReference: Api.ITemplateReference;
	protected mContactIdToCustomComposeContentMap: Api.IDictionary<Api.IEmailMessageComposeContact>;
	protected mPreferredEmailAddressesMap: Api.IDictionary<Api.EmailAddress>;
	public bcc: Api.IRecipient[];
	public cc: Api.IRecipient[];
	protected mContactsToAdd: ObservableCollection<ContactViewModel>;
	protected mContactsToOmit: ObservableCollection<ContactViewModel>;
	protected mSending: boolean;
	public content: Api.IRawRichTextContentState;
	public options: Api.IEmailMessageComposeOptions<TFollowUpOptions>;
	public signatureTemplate: Api.ITemplate;
	public subject: string;
	public citation: string;
	protected mContext: Api.IEmailMessageComposeContext;
	protected mNewAttachments: AttachmentsToBeUploadedViewModel<TAttachmentType>;
	protected mSavedAttachments: Api.IFileAttachment[];
	private mContactEmailApproximation?: Api.ContactFilterApproximation | null = null;
	private mApproximationPromise?: Promise<Api.ContactFilterApproximation> | null = null;
	public sendEmailRoute = 'email';
	/** Set to true if user sends or schedules the email and the request was successfully accepted by the backend. */
	public didScheduleOrSendSuccessFully: boolean;
	public sendAttachmentsAsLink: boolean;
	public sharedLinkCreationOptions?: Api.ISharedLinkCreationOptions;
	protected mSetDefaultKeepInTouchFrequency: boolean;
	public static MaxByteCount = 6 * 1024 * 1024; // max allowed by the platform
	protected mGroupByHousehold: boolean;

	public groupId: string | undefined = '';

	public static instanceForCampaign = <
		STAttachmentType extends FileWithExtensionsType = FileWithExtensionsType,
		STSendResult = any,
	>(
		userSession: UserSessionContext,
		campaign: CampaignViewModel,
		setDefaultKeepInTouchFrequency?: boolean
	) => {
		const email = new EmailMessageViewModel<STAttachmentType, STSendResult>(
			userSession,
			setDefaultKeepInTouchFrequency
		);
		email.content = campaign.defaultMessageContent;
		email.subject = campaign.subject;
		email.contactsFilterRequest = campaign.filterRequest
			? {
					contactFilterRequest: campaign.filterRequest,
				}
			: null;
		email.templateReference = campaign.templateReference;

		if (campaign.schedule) {
			email.options.scheduledSend = {
				...email.options.scheduledSend,
				...campaign.schedule,
			};
		}
		return email;
	};

	constructor(
		userSession: UserSessionContext,
		setDefaultKeepInTouchFrequency?: boolean,
		emailDraft?: Api.IEmailMessageDraft
	) {
		super(userSession);

		makeObservable<
			EmailMessageViewModel<TAttachmentType, TSendResult, TFollowUpOptions>,
			| 'mContactIdToCustomComposeContentMap'
			| 'mPreferredEmailAddressesMap'
			| 'mSending'
			| 'mContext'
			| 'mNewAttachments'
			| 'mSavedAttachments'
			| 'mContactEmailApproximation'
			| 'mApproximationPromise'
			| 'mGroupByHousehold'
		>(this, {
			aiReference: observable,
			contactsFilterRequest: observable,
			templateReference: observable.ref,
			mContactIdToCustomComposeContentMap: observable.ref,
			mPreferredEmailAddressesMap: observable.ref,
			bcc: observable.ref,
			cc: observable.ref,
			mSending: observable,
			content: observable,
			options: observable,
			signatureTemplate: observable,
			subject: observable,
			citation: observable,
			mContext: observable.ref,
			mNewAttachments: observable.ref,
			mSavedAttachments: observable.ref,
			mContactEmailApproximation: observable.ref,
			mApproximationPromise: observable.ref,
			sendEmailRoute: observable,
			didScheduleOrSendSuccessFully: observable,
			sendAttachmentsAsLink: observable,
			mGroupByHousehold: observable,
			groupId: observable,
			isSendingImmediately: computed,
			contactsToAdd: computed,
			contactsToOmit: computed,
			contactEmailApproximation: computed,
			isApproximating: computed,
			sendLimits: computed,
			hasUserSelectedContactFilterSearchCriteria: computed,
			isAddressedOnlyToPickedContacts: computed,
			shouldGroupByDuplicate: computed,
			setMinimumDurationInDays: action,
			setGroupByDuplicate: action,
			setScheduleExpirationDate: action,
			setContactsToOmit: action,
			setContentWithTemplate: action,
			setPreferredEmailAddressForContact: action,
			getEmailApproximation: action,
			setCustomMessageForContact: action,
			setSendEmailFromUser: action,
			groupByHousehold: computed,
			isBusy: override,
			context: computed,
			setContext: action,
			isSending: computed,
			savedAttachments: computed,
			setSavedAttachments: action,
			removeSavedAttachment: action,
			attachments: computed,
			setAttachments: action,
			send: action,
			reset: action,
		});

		this.mReset();
		this.mSetDefaultKeepInTouchFrequency = setDefaultKeepInTouchFrequency;
		if (emailDraft) {
			this.content = emailDraft.content;
			this.subject = emailDraft.subject;
			this.templateReference = emailDraft.templateReference;
			this.options = {
				...this.options,
				...(emailDraft.options || {}),
			} as any;
			this.signatureTemplate = emailDraft.signatureTemplateId ? { id: emailDraft.signatureTemplateId } : null;
			this.mSavedAttachments = emailDraft.attachments || [];
		}
	}

	public get isSendingImmediately() {
		return !this.options.scheduledSend || this.options.scheduledSend.criteria === Api.ScheduleCriteria.Immediately;
	}

	public get contactsToAdd() {
		return this.mContactsToAdd;
	}

	public get contactsToOmit() {
		return this.mContactsToOmit;
	}

	public get contactEmailApproximation() {
		return this.mContactEmailApproximation;
	}

	public get isApproximating() {
		return Boolean(this.mApproximationPromise);
	}

	public get sendLimits() {
		return this.options.emailSendLimits;
	}

	public get hasUserSelectedContactFilterSearchCriteria() {
		const sortedCriteria = VmUtils.sortContactFilterCriteria(
			this.contactsFilterRequest?.contactFilterRequest?.criteria
		);
		const findHandler = (x: Api.IContactFilterCriteria) => (y: Api.IContactFilterCriteria) =>
			y.op === x.op && y.property === x.property && y.value === x.value;
		const searches = sortedCriteria.searches.filter(
			x => !Api.DefaultBulkSendExcludedFilterCriteria.find(findHandler(x))
		);

		// Check for user-selected filters (like "With Email", "With Phone", etc.)
		// These are status/capability filters that users can select from the contacts page
		const hasUserSelectedFilters = sortedCriteria.filters.some(filter => {
			// Skip default excluded filters (like "Unsubscribe" and "Email Bounced")
			if (Api.DefaultBulkSendExcludedFilterCriteria.find(findHandler(filter))) {
				return false;
			}

			// Check for CapableOf filters which are used for status filters like "With Email"
			if (filter.property === Api.ContactFilterCriteriaProperty.CapableOf) {
				// These are user-selected status filters
				return filter.value === 'ReceiveEmail' || filter.value === 'ReceiveText' || filter.value === 'ReceiveCard';
			}

			// Check for other user-selectable filters
			return (
				filter.property === Api.ContactFilterCriteriaProperty.WithEmailAddress ||
				filter.property === Api.ContactFilterCriteriaProperty.WithPhoneNumber ||
				filter.property === Api.ContactFilterCriteriaProperty.WithMailingAddress ||
				filter.property === Api.ContactFilterCriteriaProperty.CreatedBy ||
				filter.property === Api.ContactFilterCriteriaProperty.KeepInTouch ||
				filter.property === Api.ContactFilterCriteriaProperty.Policy ||
				filter.property === Api.ContactFilterCriteriaProperty.Connections ||
				filter.property === Api.ContactFilterCriteriaProperty.InProgressAutomations ||
				filter.property === Api.ContactFilterCriteriaProperty.PreviousAutomations ||
				filter.property === Api.ContactFilterCriteriaProperty.PrivateContacts ||
				filter.property === Api.ContactFilterCriteriaProperty.TagAlert ||
				filter.property === Api.ContactFilterCriteriaProperty.UpcomingKeyDates ||
				filter.property === Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled ||
				filter.property === Api.ContactFilterCriteriaProperty.WithoutTags ||
				filter.property === Api.ContactFilterCriteriaProperty.City ||
				filter.property === Api.ContactFilterCriteriaProperty.StateProvince ||
				filter.property === Api.ContactFilterCriteriaProperty.PostalCode ||
				filter.property === Api.ContactFilterCriteriaProperty.CompanyTag ||
				filter.property === Api.ContactFilterCriteriaProperty.JobTitle
			);
		});

		// Check for key fact filters that might be in complex criteria structures
		const hasKeyFactFilters = this.contactsFilterRequest?.contactFilterRequest?.criteria?.some(criteria => {
			// Check if this is a complex criteria with nested key fact filters
			if (criteria.criteria && Array.isArray(criteria.criteria)) {
				return criteria.criteria.some(
					subCriteria =>
						// Key fact type (like 'Renewal')
						subCriteria.property === Api.ContactFilterCriteriaProperty.KeyDateKind ||
						// Key fact date filters
						subCriteria.property === Api.ContactFilterCriteriaProperty.KeyDate ||
						// Look for specific key fact values
						subCriteria.value === Api.KeyDateKind.Renewal ||
						subCriteria.value === Api.KeyDateKind.Anniversary ||
						subCriteria.value === Api.KeyDateKind.Birthday
				);
			}
			// Check direct criteria
			return (
				criteria.property === Api.ContactFilterCriteriaProperty.KeyDateKind ||
				criteria.property === Api.ContactFilterCriteriaProperty.KeyDate ||
				criteria.value === Api.KeyDateKind.Renewal ||
				criteria.value === Api.KeyDateKind.Anniversary ||
				criteria.value === Api.KeyDateKind.Birthday
			);
		});

		if (searches.length > 0 || hasUserSelectedFilters || hasKeyFactFilters) {
			return true;
		}

		return false;
	}

	public get isAddressedOnlyToPickedContacts() {
		return this.contactsToAdd?.length > 0 && !this.hasUserSelectedContactFilterSearchCriteria;
	}

	// The API treats an unset groupByDuplicate as true when sending, so every preview must default the same way.
	public get shouldGroupByDuplicate() {
		return this.contactsFilterRequest?.groupByDuplicate ?? true;
	}

	public setGroupByDuplicate = (groupByDuplicate: boolean) => {
		this.contactsFilterRequest = { ...this.contactsFilterRequest, groupByDuplicate };
	};

	/** @param minimumDays Pass null here to clear out minimumDurationInDays */
	public setMinimumDurationInDays = (minimumDurationInDays?: number) => {
		this.options = {
			...this.options,
			minimumDurationInDays,
		};
	};

	public setScheduleExpirationDate = (newValue: Date | null) => {
		this.options.scheduledSend = {
			...this.options.scheduledSend,
			expirationDate: newValue?.toISOString() ?? null,
		};
	};

	public setContactsToOmit = (contacts: ObservableCollection<ContactViewModel>) => {
		this.mContactsToOmit = contacts;
	};

	public setContentWithTemplate = (template: Api.ITemplate, includeTemplateAttachments = true) => {
		this.templateReference = template
			? {
					isCustomized: false,
					isSystemTemplate:
						template.scope === Api.TemplateScope.Industry || template.scope === Api.TemplateScope.System,
					templateId: template.id,
					name: template.name,
				}
			: null;
		this.subject = template?.subject;
		this.content = template?.content ? { ...template.content } : null;
		this.citation = template?.citation;
		if (includeTemplateAttachments) {
			this.setSavedAttachments(template?.attachments);
		}
	};

	/** @param emailAddress Pass null here to remove previously set override for preferred email */
	public setPreferredEmailAddressForContact = (
		contact: Api.IContact | ContactViewModel,
		emailAddress?: Api.EmailAddress
	) => {
		if (contact && contact.id && emailAddress) {
			const map: Api.IDictionary<Api.EmailAddress> = {
				...this.mPreferredEmailAddressesMap,
			};
			if (!emailAddress) {
				delete map[contact.id];
			} else {
				map[contact.id] = emailAddress;
			}
			this.mPreferredEmailAddressesMap = map;
		}
	};

	public setEmailApproximation = (approximation?: Api.ContactFilterApproximation) => {
		this.mContactEmailApproximation = approximation;
		this.mApproximationPromise = undefined;
	};

	public get bulkContactsRequest() {
		// Mirrors toJs: with no tag, search or filter chosen, the send goes only to the picked contacts, so the
		// leftover default exclusions and ownership scope must not widen the preview to every contact owned.
		const onlyPickedContacts = this.isAddressedOnlyToPickedContacts;
		return {
			excludeContactIds: (this.contactsToOmit?.map(x => x.id).filter(Boolean) ?? []) as string[],
			filter: onlyPickedContacts ? undefined : (this.contactsFilterRequest?.contactFilterRequest ?? undefined),
			groupByHousehold: this.groupByHousehold,
			includeContactIds: (this.contactsToAdd?.map(x => x.id).filter(Boolean) ?? []) as string[],
			groupByDuplicate: onlyPickedContacts ? undefined : this.shouldGroupByDuplicate,
			groupByHouseholdFilter: onlyPickedContacts ? undefined : this.contactsFilterRequest?.groupByHouseholdFilter,
			ownershipFilter: onlyPickedContacts ? undefined : this.contactsFilterRequest?.ownershipFilter,
		} as Api.IBulkContactsRequest;
	}

	/**
	 * @param alternateFilter an explicit filter that could differ from one composed by default
	 * @param forceReload reset/disregard an in-flight approximation promise and force a new approximation
	 * @returns Promise<Api.IContactFilterApproximation>
	 */
	public getEmailApproximation = (alternateFilter?: Api.IBulkContactsRequest, forceReload = false) => {
		if (this.isApproximating && !forceReload) {
			return;
		}

		const filterRequest = alternateFilter ?? this.bulkContactsRequest;

		const sendType =
			this.content?.sourceFormat === Api.ContentSourceFormat.UnlayerHtmlNewsletter
				? Api.ContactEmailApproximation.Html
				: Api.ContactEmailApproximation.GroupSend;

		const requestBody = {
			totalCountFilter: filterRequest,
			capabilities: [Api.ContactCapability.ReceiveEmail],
			emailSendCategory: sendType,
		};

		const promise = this.mUserSession.webServiceHelper.callAsync<Api.ContactFilterApproximation>(
			this.composeApiUrl({ urlPath: `contact/approximate` }),
			'POST',
			requestBody
		);
		this.mApproximationPromise = promise as Promise<Api.ContactFilterApproximation>;

		promise
			.then(approximation => {
				if (promise === this.mApproximationPromise) {
					this.mContactEmailApproximation = approximation;
				}
			})
			.finally(() => {
				if (promise === this.mApproximationPromise) {
					this.mApproximationPromise = undefined;
				}
			});

		return promise;
	};

	public getPreferredEmailAddressForContact = (contact: Api.IContact | ContactViewModel) => {
		const emailAddress = contact && contact.id ? this.mPreferredEmailAddressesMap[contact.id] : null;
		if (emailAddress) {
			if (contact.emailAddresses && contact.emailAddresses.find(x => equal(x, emailAddress))) {
				return emailAddress;
			} else {
				delete this.mPreferredEmailAddressesMap[contact.id];
			}
		}

		if (contact) {
			return contact.primaryEmail || (contact.emailAddresses || [])[0] || null;
		}

		return null;
	};

	/** @param customMessage Pass null to remove a custom message from the collection */
	public setCustomMessageForContact = (
		contact: ContactViewModel,
		customMessage?: Pick<Api.IEmailMessageComposeContact, Exclude<keyof Api.IEmailMessageComposeContact, 'contactId'>>
	) => {
		if (contact && contact.id) {
			const contactIdToCustomComposeContentMap = {
				...this.mContactIdToCustomComposeContentMap,
			};
			if (customMessage) {
				contactIdToCustomComposeContentMap[contact.id] = {
					...customMessage,
					contactId: contact.id,
				};
				// always add them to this explicit list when creating
				this.contactsToAdd.add(contact);
			} else {
				delete contactIdToCustomComposeContentMap[contact.id];
			}
			this.mContactIdToCustomComposeContentMap = contactIdToCustomComposeContentMap;
		}
	};

	public getCustomMessageForContact = (contact: ContactViewModel) => {
		if (contact && contact.id) {
			return this.mContactIdToCustomComposeContentMap[contact.id];
		}
		return null;
	};

	public getAllRecipientSpecificAttachments = () => {
		return Object.values(this.mContactIdToCustomComposeContentMap).flatMap(contact => contact.attachments || []);
	};

	public deleteSavedEmailAttachmentAsync = async (savedAttachment: Api.IFileAttachment) => {
		// wait for inprogress to finish
		const asInProgressFileAttachment = savedAttachment as Api.IInProgressFileAttachment;
		if (asInProgressFileAttachment.uploadPromise) {
			await asInProgressFileAttachment.uploadPromise;
		}

		const recipientMessagesMap = this.mContactIdToCustomComposeContentMap || {};
		const recipientSpecificEmailMessageWithAttachment = Object.keys(recipientMessagesMap).reduce((res, curr) => {
			if (!res) {
				const message = recipientMessagesMap[curr];
				if (message.attachments?.find(x => x.id === savedAttachment.id)) {
					return message;
				}
			}
			return res;
		}, null as Api.IEmailMessageComposeContact);

		if (recipientSpecificEmailMessageWithAttachment) {
			const attachmentVM = new AttachmentsViewModel(this.mUserSession);
			await attachmentVM.delete(savedAttachment);
			recipientSpecificEmailMessageWithAttachment.attachments =
				recipientSpecificEmailMessageWithAttachment.attachments.filter(x => x.id !== savedAttachment.id);
			this.mContactIdToCustomComposeContentMap[recipientSpecificEmailMessageWithAttachment.contactId] = {
				...recipientSpecificEmailMessageWithAttachment,
			};
		} else {
			const currentAttachments = this.savedAttachments;
			const filteredAttachments = currentAttachments?.filter(x => x.id !== savedAttachment.id);
			this.setSavedAttachments(filteredAttachments);
		}
	};

	public toJs = () => {
		const options = toJS(this.options);
		options.sendEmailFromUser = options.sendEmailFromUser
			? Api.selectKeysOf(options.sendEmailFromUser, ['id', 'email', 'primaryEmail', 'firstName', 'lastName'])
			: null;

		if (
			!options.followUpIfNoResponseInDays ||
			isNaN(options.followUpIfNoResponseInDays) ||
			options.followUpIfNoResponseInDays < 1
		) {
			delete options.followUpIfNoResponseInDays;
		}

		const emailMessageModel: Api.IEmailMessageDraft<TFollowUpOptions> = {
			attachments: this.mSavedAttachments,
			content: this.content,
			options,
			subject: this.subject,
			groupId: this.groupId,
		};
		const result: Api.IEmailMessageCompose<TFollowUpOptions> = {
			...emailMessageModel,
			aiReference: this.aiReference,
			contactIdsToOmit: this.contactsToOmit ? this.contactsToOmit.map(x => x.id) : [],
			sendAttachmentsAsLink: this.sendAttachmentsAsLink || undefined,
			sharedLinkCreationOptions: this.sendAttachmentsAsLink ? this.sharedLinkCreationOptions : undefined,
			contacts: [],
			contactsFilterRequest: !this.hasUserSelectedContactFilterSearchCriteria
				? { contactFilterRequest: { criteria: null }, groupByHousehold: this.groupByHousehold }
				: this.contactsFilterRequest
					? { ...this.contactsFilterRequest, groupByHousehold: this.groupByHousehold }
					: null,
			signatureTemplateId: this.signatureTemplate ? this.signatureTemplate.id : 'default', // explicit no signature is modeled as { id: "none" },
			templateReference: this.templateReference || null,
		};
		VmUtils.removeEmptyKvpFromObject(result);

		const idsOfContactsWithCustomMessages = Object.keys(this.mContactIdToCustomComposeContentMap) || [];
		// apply explicit contacts... these get the default message, context, and options
		// note: we do not add contacts who have custom messages here. They will be handled below
		if (this.contactsToAdd.length > 0) {
			this.contactsToAdd.forEach(x => {
				if (idsOfContactsWithCustomMessages.indexOf(x.id) < 0 && result.contactIdsToOmit.indexOf(x.id) < 0) {
					const preferredEmailAddress = this.getPreferredEmailAddressForContact(x) || null;
					result.contacts.push({
						contactId: x.id,
						context: this.mContext,
						preferredEmailAddress:
							preferredEmailAddress && preferredEmailAddress.value ? preferredEmailAddress.value : null,
					});
				}
			});
		}

		// apply custom messages
		if (idsOfContactsWithCustomMessages && idsOfContactsWithCustomMessages.length > 0) {
			idsOfContactsWithCustomMessages.forEach(x => {
				const customMessage = this.mContactIdToCustomComposeContentMap[x];
				if (customMessage && result.contactIdsToOmit.indexOf(x) < 0) {
					const preferredEmailAddress = this.getPreferredEmailAddressForContact({ id: x }) || null;
					result.contacts.push({
						...customMessage,
						context: this.mContext,
						preferredEmailAddress:
							preferredEmailAddress && preferredEmailAddress.value ? preferredEmailAddress.value : null,
					});
				}
			});
		}

		// bcc/cc
		result.carbonCopy = {
			bcc: (this.bcc || []).filter(x => x.email).map(x => x.email),
			cc: (this.cc || []).filter(x => x.email).map(x => x.email),
		};

		return result;
	};

	public setSendEmailFromUser(user?: Api.IUser) {
		this.options.sendEmailFromUser = user ?? undefined;
		this.options.sendEmailFromUserId = user?.id ?? undefined;
	}

	public isSendFromOption = (option: Api.ISendEmailFrom) => {
		return this.options.sendEmailFrom === option;
	};

	public get groupByHousehold() {
		return this.mGroupByHousehold;
	}

	public set groupByHousehold(value: boolean) {
		this.mGroupByHousehold = value;
	}

	public get isBusy() {
		return this.busy || this.loading || this.mSending;
	}

	public get context() {
		return this.mContext;
	}

	public setContext(context: Api.IEmailMessageComposeContext) {
		this.mSetContext(context);
	}

	public get isSending() {
		return this.mSending;
	}

	public get savedAttachments() {
		return this.mSavedAttachments;
	}

	public setSavedAttachments(attachments: Api.IFileAttachment[]) {
		this.mSavedAttachments = attachments;
	}

	public removeSavedAttachment(attachment: Api.IFileAttachment): Promise<void> {
		return new Promise(resolve => {
			const filteredAttachments = this.mSavedAttachments?.filter(x => x.id !== attachment.id);
			this.mSavedAttachments = filteredAttachments;
			resolve();
		});
	}

	public get attachments() {
		return this.mNewAttachments;
	}

	public setAttachments = (attachments: AttachmentsToBeUploadedViewModel<TAttachmentType>) => {
		this.mNewAttachments = attachments;
	};

	public send = (suggestionId?: string) => {
		if (!this.mSending) {
			this.mSending = true;
			const promise = new Promise<Api.IOperationResult<TSendResult>>((resolve, reject) => {
				const onFinish = action((result: Api.IOperationResult<TSendResult>) => {
					this.mSending = false;
					if (result.success) {
						this.didScheduleOrSendSuccessFully = true;
						resolve(result);
					} else {
						reject(result);
					}
				});

				// pull out email and attachments into form data if attachments are present
				const formData: FormData = new FormData();
				formData.append('value', JSON.stringify(this.toJs()));
				if (this.mNewAttachments && this.mNewAttachments.count > 0) {
					this.mNewAttachments.attachments.forEach(x => formData.append('files', x));
				}

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResult<TSendResult>>(
					this.composeApiUrl({
						queryParams: {
							contentCalendarSuggestionId: suggestionId,
						},
						urlPath: this.sendEmailRoute,
					}),
					'POST',
					formData,
					// FOLLOWUP: Resolve

					// @ts-ignore
					onFinish,
					onFinish
				);
			});

			return promise;
		}
		return null;
	};

	public reset() {
		this.mReset();
	}

	public mGetEstimatedTotalByteCount = () => {
		let byteCount = new Blob([JSON.stringify(this.toJs())]).size;
		if (this.attachments) {
			byteCount = byteCount + this.attachments.byteCount;
		}

		return byteCount;
	};

	public sendTestEmail = async (options?: Api.ISendTestEmailOptions<ContactViewModel>) => {
		if (!this.busy) {
			this.busy = true;
			const tempSendFromOptions = {
				sendEmailFrom: this.options.sendEmailFrom,
				sendEmailFromUser: this.options.sendEmailFromUser,
				sendEmailFromUserId: this.options.sendEmailFromUserId,
				sendFromEmailAddress: this.options.sendFromEmailAddress,
			};

			const draft: Api.IEmailMessageDraft = {
				content: toJS(this.content),
				options: {
					saveAsNote: false,
					scheduledSend: {
						criteria: Api.ScheduleCriteria.Immediately,
					},
					...tempSendFromOptions,
				},
				signatureTemplateId: this.signatureTemplate?.id ?? 'default',
				subject: this.subject || 'Test email from Levitate',
			};
			const computedOptions: Api.ISendTestEmailOptions<ContactViewModel> = {
				includeAttachments: true,
				...tempSendFromOptions,
				...(options || {}),
			};
			const testEmail = new EmailMessageViewModel<TAttachmentType>(
				this.mUserSession,
				false,
				computedOptions?.onCreateDraft ? computedOptions.onCreateDraft(draft) : draft
			);

			if (this.templateReference) {
				testEmail.templateReference = {
					isCustomized: false,
					isSystemTemplate: false,
					templateId: this.templateReference.templateId,
					name: this.templateReference.name,
				};
			}

			testEmail.impersonate(this.impersonationContext);
			const contactId =
				computedOptions?.to?.id || (this.mImpersonationContext?.user?.contactId ?? this.mUserSession.user.contactId);
			const emailProviderConfigurations =
				this.mImpersonationContext?.account?.emailProviderConfigurations ||
				this.mUserSession?.account?.emailProviderConfiguration;

			if (computedOptions?.includeAttachments) {
				testEmail.mSavedAttachments = this.mSavedAttachments?.length ? [...this.mSavedAttachments] : undefined;
				if (this.attachments?.count > 0) {
					testEmail.setAttachments(
						new AttachmentsToBeUploadedViewModel<TAttachmentType>(
							null,
							VmUtils.getMaxAttachmentByteCountForEmailConfigurations(emailProviderConfigurations)
						)
					);
					testEmail.attachments.add(this.attachments.attachments);
				}
			}

			try {
				const contact = new ContactViewModel(this.mUserSession, {
					id: contactId,
				}).impersonate(this.mImpersonationContext);
				testEmail.contactsToAdd.add(contact);
				const res = await testEmail.send();
				this.busy = false;
				return res;
			} catch (err) {
				this.busy = false;
				throw Api.asApiError(err);
			}
		}
	};

	protected mReset = () => {
		this.aiReference = null;
		this.bcc = [];
		this.cc = [];
		this.contactsFilterRequest = null;
		this.mContactIdToCustomComposeContentMap = {};
		this.mContactsToAdd = new ObservableCollection<ContactViewModel>(null, 'id');
		this.mContactsToOmit = new ObservableCollection<ContactViewModel>(null, 'id');
		this.mPreferredEmailAddressesMap = {};
		this.templateReference = null;
		this.options = {
			followUp: null,
			followUpIfNoResponseInDays: 0,
			keepInTouchFrequency: this.mGetDefaultKeepInTouchFrequency(),
			noteVisibility: VmUtils.getDefaultVisibility(this.mUserSession.user),
			saveAsNote: this.userSession?.account?.preferences?.saveEmailAsNoteDefault,
			scheduledSend: {
				criteria: Api.ScheduleCriteria.Immediately,
				endDate: null,
				startDate: null,
			},
			sendEmailFrom: Api.SendEmailFrom.CurrentUser,
			sendEmailFromUser: undefined,
			sendFromConnectionType: null,
		};
		this.subject = null;
		this.content = null;
		this.signatureTemplate = null;
		this.mSetContext({
			ref: null,
		});
		this.mGroupByHousehold = this.userSession?.account?.features?.households?.sendToHouseholdByDefault ?? false;
	};

	protected mGetDefaultKeepInTouchFrequency = () => {
		return this.mSetDefaultKeepInTouchFrequency ? 3 : null;
	};

	protected mSetContext = (context: Api.IEmailMessageComposeContext) => {
		this.mContext = context;
	};
}

export class AutomationStepStatusEmailMessageViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends EmailMessageViewModel<File> {
	protected mStepStatus: Api.IAutomationStepStatus<Api.IEmailAutomationStep>;

	constructor(
		userSession: TUserSession,
		stepStatus: Api.IAutomationStepStatus<Api.IEmailAutomationStep>,
		setDefaultKeepInTouchFrequency?: boolean
	) {
		super(userSession, setDefaultKeepInTouchFrequency, {
			content: stepStatus?.step?.content,
			options: {
				followUpIfNoResponseInDays: stepStatus?.step?.options?.followUpIfNoResponseInDays,
				keepInTouchFrequency: stepStatus?.step?.options?.keepInTouchFrequency,

				noteVisibility: stepStatus?.step?.options?.noteVisibility || VmUtils.getDefaultVisibility(userSession.user),
				saveAsNote:
					stepStatus?.step?.options && Object.prototype.hasOwnProperty.call(stepStatus.step.options, 'saveAsNote')
						? stepStatus.step.options.saveAsNote
						: true,
			},
			signatureTemplateId: stepStatus?.step?.options?.signatureTemplateId,
			subject: stepStatus?.step?.options?.subject,
			templateReference: stepStatus?.step?.options?.templateReference,
		});

		makeObservable<AutomationStepStatusEmailMessageViewModel, 'mStepStatus'>(this, {
			mStepStatus: observable.ref,
			savedAttachments: override,
			update: action,
			addAttachments: action,
			removeAttachment: action,
		});

		this.mStepStatus = stepStatus;
		this.cc = stepStatus?.step?.additionalRecipientEmails ?? [];
	}

	public get savedAttachments() {
		return this.mStepStatus?.step?.attachments;
	}

	public async update() {
		if (!this.isBusy) {
			this.busy = true;
			try {
				const contentPromise = this.mUserSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: `Automation/Step/${this.mStepStatus?.id}/Content` }),
					'PUT',
					this.content
				);
				const emailOptions: Api.IEmailMessageOptions = {
					...(this.mStepStatus?.step?.options || {}),
					additionalRecipientEmails: this.cc ?? [],
					followUpIfNoResponseInDays: this.options.followUpIfNoResponseInDays,
					keepInTouchFrequency: this.options.keepInTouchFrequency,
					noteVisibility: this.options.noteVisibility,
					saveAsNote: this.options.saveAsNote,
					subject: this.subject,
				};
				const optionsPromise = this.mUserSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: `Automation/Step/${this.mStepStatus?.id}/EmailOptions` }),
					'PUT',
					emailOptions
				);

				const opResults = await Promise.all<Api.IOperationResult<Api.IAutomationStepStatus<Api.IEmailAutomationStep>>>([
					contentPromise,
					optionsPromise,
				]);

				const errorOpResult = opResults.find(x => !x.success);
				if (errorOpResult) {
					throw errorOpResult;
				}
				this.mStepStatus = {
					...this.mStepStatus,
					step: {
						...(this.mStepStatus?.step || {}),
						content: this.content,
						options: emailOptions,
					},
				};

				if (this.mNewAttachments.count > 0) {
					const formData = new FormData();
					this.mNewAttachments.attachments.forEach(x => formData.append('files', x));
					const attachmentsOpResult = await this.mUserSession.webServiceHelper.callWebServiceAsync(
						this.composeApiUrl({ urlPath: `Automation/Step/${this.mStepStatus?.id}/Attachment` }),
						'POST',
						formData
					);
					if (!attachmentsOpResult.success) {
						throw attachmentsOpResult;
					}

					opResults.push(attachmentsOpResult);

					this.mStepStatus = attachmentsOpResult.value;
				}
				this.busy = false;
				return opResults;
			} catch (err) {
				this.busy = false;
				return Api.asApiError(err);
			}
		}
	}

	public addAttachments(attachments: AttachmentsToBeUploadedViewModel<File>) {
		if (!this.isBusy && attachments?.count > 0) {
			this.busy = true;
			return new Promise<Api.IOperationResult<Api.IAutomationStepStatus<Api.IEmailAutomationStep>>>(
				(resolve, reject) => {
					const onFinish = action(
						(opResult: Api.IOperationResult<Api.IAutomationStepStatus<Api.IEmailAutomationStep>>) => {
							this.busy = false;
							if (opResult.success) {
								this.mStepStatus = opResult.value;
								resolve(opResult);
							} else {
								reject(opResult);
							}
						}
					);

					const formData = new FormData();
					attachments.attachments.forEach(x => formData.append('files', x));
					this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
						this.composeApiUrl({ urlPath: `Automation/Step/${this.mStepStatus?.id}/Attachment` }),
						'POST',
						formData,
						onFinish,
						onFinish
					);
				}
			);
		}
	}

	public removeAttachment(attachment: Api.IFileAttachment) {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<Api.IAutomationStepStatus<Api.IEmailAutomationStep>>>(
				(resolve, reject) => {
					const onFinish = action(
						(opResult: Api.IOperationResult<Api.IAutomationStepStatus<Api.IEmailAutomationStep>>) => {
							this.busy = false;
							if (opResult.success) {
								this.mStepStatus = opResult.value;
								resolve(opResult);
							} else {
								reject(opResult);
							}
						}
					);
					this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
						this.composeApiUrl({ urlPath: `Automation/Step/${this.mStepStatus?.id}/Attachment/${attachment.id}` }),
						'DELETE',
						null,
						onFinish,
						onFinish
					);
				}
			);
		}
	}
}

export class DeactivateAccountViewModel {
	public cancelReason: string;
	public cancelConfirmed: boolean;
	private busy: boolean;
	private userSession: UserSessionContext;

	constructor(userSession: UserSessionContext) {
		makeObservable<DeactivateAccountViewModel, 'busy' | 'reset'>(this, {
			cancelReason: observable,
			cancelConfirmed: observable,
			busy: observable,
			canDeactivate: computed,
			isBusy: computed,
			deactivate: action,
			reset: action,
		});

		this.userSession = userSession;
		this.reset();
	}

	public get canDeactivate() {
		return this.cancelConfirmed;
	}

	public get isBusy() {
		return this.busy;
	}

	public toJs = (): Api.ICancelAccount => {
		return {
			reason: this.cancelReason,
		};
	};

	public deactivate() {
		if (!this.canDeactivate) {
			return null;
		}

		this.busy = true;
		return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = action((result: Api.IOperationResultNoValue) => {
				this.busy = false;
				if (result.success) {
					this.reset();
					resolve(result);
				} else {
					reject(result);
				}
			});

			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				'account/cancel',
				'POST',
				this.toJs(),
				onFinish,
				onFinish
			);
		});
	}

	private reset() {
		this.busy = false;
		this.cancelReason = null;
		this.cancelConfirmed = false;
	}
}

export type EditActionItemCompletion = (actionItem?: ActionItemViewModel, deleted?: boolean) => void;

export class PasswordValidationViewModel {
	private mPasswordConfirmation: string;
	private mPassword: string;

	constructor(password?: string, confirmPassword?: string) {
		makeObservable<PasswordValidationViewModel, 'mPasswordConfirmation' | 'mPassword'>(this, {
			mPasswordConfirmation: observable,
			mPassword: observable,
			password: computed,
			setPassword: action,
			passwordConfirmation: computed,
			setPasswordConfirmation: action,
			hasValidCharCount: computed,
			hasUpperChar: computed,
			hasLowerChar: computed,
			hasNumber: computed,
			matchesConfirmPassword: computed,
		});

		this.mPassword = password;
		this.mPasswordConfirmation = confirmPassword;
	}

	public get password() {
		return this.mPassword;
	}

	public setPassword(password: string) {
		this.mPassword = password;
	}

	public get passwordConfirmation() {
		return this.mPasswordConfirmation;
	}

	public setPasswordConfirmation(value: string) {
		this.mPasswordConfirmation = value;
	}

	public get hasValidCharCount() {
		return this.mPassword && this.mPassword.length >= 8;
	}

	public get hasUpperChar() {
		return this.mPassword && /^(?=.*[A-Z]).*$/.test(this.mPassword);
	}

	public get hasLowerChar() {
		return this.mPassword && /^(?=.*[a-z]).*$/.test(this.mPassword);
	}

	public get hasNumber() {
		return this.mPassword && /^(?=.*[0-9]).*$/.test(this.mPassword);
	}

	public get matchesConfirmPassword() {
		return this.mPassword && this.mPasswordConfirmation && this.mPasswordConfirmation === this.mPassword;
	}
}

export class PasswordResetWithTokenViewModel extends PasswordValidationViewModel {
	busy = false;
	mUserSession: UserSessionContext;
	constructor(userSession: UserSessionContext, password?: string, confirmPassword?: string) {
		super(password, confirmPassword);

		makeObservable(this, {
			busy: observable.ref,
			mUserSession: observable.ref,
			resetPassword: action,
			isBusy: computed,
		});

		this.mUserSession = userSession;
	}

	public resetPassword(newPassword: string, token: string) {
		this.busy = true;
		return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResultNoValue) => {
				runInAction(() => {
					this.busy = false;
					if (result.success) {
						resolve(result);
					} else {
						reject(result);
					}
				});
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				`user/resetPasswordWithToken?token=${token}`,
				'POST',
				{ newPassword },
				onFinish,
				onFinish
			);
		});
	}

	public get isBusy() {
		return this.busy;
	}
}

export class SystemJobViewModel<TSystemJob extends Api.ISystemJob = Api.ISystemJob> extends ViewModel {
	protected mSystemJob: TSystemJob;
	protected mPollingTimeoutHandle: any;
	protected mPollingPromise: Promise<Api.IOperationResult<TSystemJob>>;
	protected mUpdateCallback: (opResult: Api.IOperationResult<TSystemJob>) => void;

	constructor(userSession: UserSessionContext, systemJob?: TSystemJob) {
		super(userSession);

		makeObservable<SystemJobViewModel<TSystemJob>, 'mSystemJob' | 'mPollingTimeoutHandle'>(this, {
			mSystemJob: observable,
			mPollingTimeoutHandle: observable.ref,
			isWatching: computed,
			id: computed,
			percentComplete: computed,
			jobType: computed,
			status: computed,
			additionalFields: computed,
			additionalInfo: computed,
			pdf: computed,
			recordsFailed: computed,
			recordsProcessed: computed,
			recordsSucceeded: computed,
			totalRecords: computed,
			isBusy: override,
			load: override,
			get: action,
			startWatching: action,
			stopWatching: action,
		});

		this.setSystemJob = this.setSystemJob.bind(this);
		this.setSystemJob(systemJob);
	}

	public get isWatching() {
		return !!this.mPollingTimeoutHandle;
	}

	public get id() {
		return this.mSystemJob ? this.mSystemJob.id : null;
	}

	public get percentComplete() {
		return this.mSystemJob.percentComplete;
	}

	public get jobType() {
		return this.mSystemJob.jobType;
	}

	public get status() {
		return this.mSystemJob.status;
	}

	public get additionalFields() {
		return this.mSystemJob.additionalFields;
	}

	public get hasDownloadableFile() {
		return this.mSystemJob.hasDownloadableFile;
	}

	public get additionalInfo() {
		return this.mSystemJob.additionalInfo;
	}

	public get recordsFailed() {
		return this.mSystemJob.recordsFailed;
	}

	public get recordsProcessed() {
		return this.mSystemJob.recordsProcessed;
	}

	public get recordsSucceeded() {
		return this.mSystemJob.recordsSucceeded;
	}

	public get totalRecords() {
		return this.mSystemJob.totalRecords;
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public get isLoading() {
		return this.loading;
	}

	public get pdf() {
		return (this.mSystemJob as Api.IExportTemplatePdfJob)?.pdf;
	}

	public load(): Promise<Api.IOperationResult<TSystemJob>> {
		if (this.isLoaded) {
			return Promise.resolve<Api.IOperationResult<TSystemJob>>({
				success: true,
				systemCode: 200,
				value: this.mSystemJob,
			});
		}

		return this.get();
	}

	public get = () => {
		if (!this.isBusy) {
			if (!this.isLoaded) {
				this.loading = true;
			}
			this.busy = true;
			return new Promise<Api.IOperationResult<TSystemJob>>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TSystemJob>) => {
					runInAction(() => {
						this.loading = false;
						this.busy = false;
						if (result.success) {
							const systemJob = result.value;
							this.setSystemJob(systemJob);
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TSystemJob>(
					`systemjob/${this.mSystemJob.id}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	/** @param continueWatching Called once for each successful/error update. Return true to continue polling for updates. */
	public startWatching = (
		continueWatching: (opResult: Api.IOperationResult<TSystemJob>) => boolean,
		interval = 2000
	) => {
		this.mStopWatching();
		if (continueWatching) {
			this.mUpdateCallback = continueWatching;
			const update = (firstRun = false) => {
				this.mPollingPromise = this.get();
				this.mPollingPromise
					.then(opResult => {
						if ((this.isWatching || firstRun) && continueWatching === this.mUpdateCallback && this.mUpdateCallback) {
							const reschedule = continueWatching(opResult);
							if (reschedule) {
								this.mPollingTimeoutHandle = setTimeout(() => {
									update();
								}, interval);
							} else {
								this.stopWatching();
							}
						}
					})
					.catch(continueWatching);
			};
			update(true);
		}
	};

	public stopWatching = () => {
		this.mStopWatching();
	};

	public toJs = () => {
		return this.mSystemJob;
	};

	private mStopWatching = () => {
		this.mPollingPromise = null;
		this.mUpdateCallback = null;
		if (this.mPollingTimeoutHandle) {
			clearTimeout(this.mPollingTimeoutHandle);
			this.mPollingTimeoutHandle = null;
		}
	};

	protected setSystemJob(systemJob: TSystemJob) {
		this.mSystemJob = systemJob;
		this.loaded = systemJob && systemJob.id && !!systemJob.dateCreatedUtc;
	}
}

export class AddUserViewModel {
	private busy: boolean;
	private mBasicCredential: Api.IBasicCredential;
	private mEmailAddress: string;
	private mPasswordConfirmation: string;
	private mUser: Api.IUser;
	private mIsTermsAcked: boolean;
	public contactImportVisibility: Api.ResourceVisibility;
	private mContactImportSystemJob: SystemJobViewModel;
	private mOAuthLinkSystemJob: SystemJobViewModel;
	private mPasswordValidation: PasswordValidationViewModel;
	private mUserSession: UserSessionContext;

	constructor(
		userSession: UserSessionContext,
		userToCreate?: Api.IUser,
		contactImportVisibility?: Api.ResourceVisibility
	) {
		makeObservable<
			AddUserViewModel,
			| 'busy'
			| 'mBasicCredential'
			| 'mEmailAddress'
			| 'mPasswordConfirmation'
			| 'mUser'
			| 'mIsTermsAcked'
			| 'mContactImportSystemJob'
			| 'mOAuthLinkSystemJob'
		>(this, {
			busy: observable,
			mBasicCredential: observable,
			mEmailAddress: observable,
			mPasswordConfirmation: observable,
			mUser: observable,
			mIsTermsAcked: observable,
			contactImportVisibility: observable,
			mContactImportSystemJob: observable.ref,
			mOAuthLinkSystemJob: observable.ref,
			isBusy: computed,
			isTermsAcked: computed,
			emailProviderBasicCredential: computed,
			oauthLinkJob: computed,
			user: computed,
			contactImportJob: computed,
			setContactImportJob: action,
			firstName: computed,
			setFirstName: action,
			emailAddress: computed,
			setEmailAddress: action,
			lastName: computed,
			setLastName: action,
			mobilePhone: computed,
			setMobilePhone: action,
			password: computed,
			setPassword: action,
			passwordConfirmation: computed,
			setPasswordConfirmation: action,
			passwordRecoveryAnswer: computed,
			setPasswordRecoveryAnswer: action,
			passwordRecoveryQuestion: computed,
			setPasswordRecoveryQuestion: action,
			passwordRecoveryIsValid: computed,
			isTermsAcknowledged: action,
			acknowledgeTerms: action,
			createOAuthLinkJob: action,
			setEmailProviderBasicCredential: action,
			createContactImportJob: action,
			verifyWelcomeToken: action,
			createUser: action,
			registerUserByEmail: action,
		});

		this.mUserSession = userSession;
		this.mUser = {
			accountId: null,
			firstName: null,
			id: null,
			lastName: null,
			mobilePhone: null,
			password: null,
			passwordRecoveryAnswer: null,
			passwordRecoveryQuestion: null,
			token: null,
			...(userToCreate || {}),
		};
		this.contactImportVisibility = contactImportVisibility || 'all';
		this.mPasswordValidation = new PasswordValidationViewModel(this.mUser.password, this.mPasswordConfirmation);
		this.mBasicCredential = {
			password: null,
			username: null,
		};
	}

	public get passwordValidation() {
		return this.mPasswordValidation;
	}

	public get userSession() {
		return this.mUserSession;
	}

	public get isBusy() {
		return this.busy;
	}

	public get isTermsAcked() {
		return this.mIsTermsAcked;
	}

	public get emailProviderBasicCredential() {
		return this.mBasicCredential;
	}

	public get oauthLinkJob() {
		return this.mOAuthLinkSystemJob;
	}

	public get user() {
		return this.mUser;
	}

	public get contactImportJob() {
		return this.mContactImportSystemJob;
	}

	public setContactImportJob(systemJob: Api.ISystemJob) {
		this.mContactImportSystemJob = new SystemJobViewModel(this.mUserSession, systemJob);
	}

	public get firstName() {
		return this.mUser.firstName;
	}

	public setFirstName(value: string) {
		this.mUser.firstName = value;
	}

	public get emailAddress() {
		return this.mEmailAddress;
	}

	public setEmailAddress(value: string) {
		this.mEmailAddress = value;
	}

	public get lastName() {
		return this.mUser.lastName;
	}

	public setLastName(value: string) {
		this.mUser.lastName = value;
	}

	public get mobilePhone() {
		return this.mUser.mobilePhone;
	}

	public setMobilePhone(value: string) {
		this.mUser.mobilePhone = value;
	}

	public get password() {
		return this.mUser.password;
	}

	public setPassword(value: string) {
		this.mUser.password = value;
		this.mPasswordValidation.setPassword(value);
	}

	public get passwordConfirmation() {
		return this.mPasswordConfirmation;
	}

	public setPasswordConfirmation(value: string) {
		this.mPasswordConfirmation = value;
		this.mPasswordValidation.setPasswordConfirmation(value);
	}

	public get passwordRecoveryAnswer() {
		return this.mUser.passwordRecoveryAnswer;
	}

	public setPasswordRecoveryAnswer(value: string) {
		this.mUser.passwordRecoveryAnswer = value;
	}

	public get passwordRecoveryQuestion() {
		return this.mUser.passwordRecoveryQuestion;
	}

	public setPasswordRecoveryQuestion(value: string) {
		this.mUser.passwordRecoveryQuestion = value;
	}

	public get passwordRecoveryIsValid() {
		return (
			this.user &&
			this.mUser.passwordRecoveryAnswer &&
			this.mUser.passwordRecoveryQuestion &&
			this.mUser.passwordRecoveryQuestion.length >= 4 &&
			this.mUser.passwordRecoveryQuestion !== this.mUser.passwordRecoveryAnswer
		);
	}

	public isTermsAcknowledged = () => {
		return new Promise<Api.IOperationResult<boolean>>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<boolean>) => {
				runInAction(() => {
					if (result.success) {
						this.mIsTermsAcked = result.value;
						resolve(result);
					} else {
						reject(result);
					}
				});
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<boolean>(
				`termsOfService/${this.mUser.accountId}/acknowledged`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public acknowledgeTerms = () => {
		return new Promise<Api.IOperationResult<Api.ITermsOfServiceAck>>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<Api.ITermsOfServiceAck>) => {
				runInAction(() => {
					if (result.success) {
						this.mIsTermsAcked = result.value.accountId ? true : false;
						resolve(result);
					} else {
						reject(result);
					}
				});
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ITermsOfServiceAck>(
				`termsOfService`,
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public createOAuthLinkJob = () => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.ISystemJob>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.ISystemJob>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							const systemJob = result.value;
							this.mOAuthLinkSystemJob = new SystemJobViewModel(this.mUserSession, systemJob);
							resolve(systemJob);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
					'oauthlink/authUrl',
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public setEmailProviderBasicCredential = (credential: Api.IBasicCredential) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IOperationResultNoValue>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mUserSession.user.emailCalendarTokenExists = true;
							resolve(undefined);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`user/${this.mUserSession.user.id}/configureBasicCredentials`,
					'POST',
					credential,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public createContactImportJob = () => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.ISystemJob>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.ISystemJob>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							const systemJob = result.value;
							this.mContactImportSystemJob = new SystemJobViewModel(this.mUserSession, systemJob);
							resolve(systemJob);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
					`contact/import?visibility=${this.contactImportVisibility}`,
					'POST',
					{},
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public verifyWelcomeToken = (verifyToken: Api.IVerifyToken) => {
		return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<Api.IOperationResultNoValue>) => {
				if (result.success) {
					resolve(result);
				} else {
					reject(result);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				'user/verifyToken',
				'POST',
				verifyToken,
				onFinish,
				onFinish
			);
		});
	};

	public autoLoginAfterCreatingUser = (createdUser: Api.IUser, password: string, attempts = 1) =>
		new Promise((resolve, reject) => {
			this.mUserSession
				.updateWithCredential({
					email: createdUser.primaryEmail.value,
					password,
				})
				.then(() => resolve(null))
				.catch((err: Api.IOperationResultNoValue) => {
					if (attempts < 2) {
						setTimeout(() => resolve(this.autoLoginAfterCreatingUser(createdUser, password, attempts + 1)), 3000);
					} else {
						reject(err);
					}
				});
		});

	public createUser = (user = this.mUser, autoLogin = true) => {
		if (!this.isBusy) {
			this.busy = true;
			const password = user.password;
			const promise = new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						if (result.success) {
							const createdUser = {
								...user,
								...result.value,
							};
							if (autoLogin) {
								this.autoLoginAfterCreatingUser(createdUser, password)
									.then(() => {
										this.busy = false;
										this.mUser = createdUser;
										resolve(this.mUser);
									})
									.catch((loginError: Api.IOperationResultNoValue) => {
										this.busy = false;
										reject(loginError);
									});
							} else {
								this.busy = false;
								this.mUser = createdUser;
								resolve(this.mUser);
							}

							this.contactImportVisibility =
								createdUser.userPreferences && createdUser.userPreferences.defaultGroup
									? createdUser.userPreferences.defaultGroup
									: this.contactImportVisibility;
						} else {
							this.busy = false;
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					'user',
					'POST',
					user,
					onFinish,
					onFinish,
					null,
					false
				);
			});
			return promise;
		}

		return null;
	};

	public registerUserByEmail = (accountId: string, emailAddress: string) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					'user/register',
					'POST',
					{ accountId, email: emailAddress },
					onFinish,
					onFinish
				);
			});
		}
	};
}

export class BaseResourceViewModel<
	TResource extends Api.IBaseResourceModel = Api.IBaseResourceModel,
> extends ViewModel {
	protected mResource?: TResource;
	protected apiPathDictionary: Api.IDictionary<string>;
	protected apiBasePath: string;

	constructor(userSession: UserSessionContext, resource?: TResource) {
		super(userSession);

		makeObservable<BaseResourceViewModel<TResource>, 'mResource'>(this, {
			mResource: observable,
			resource: computed,
			load: override,
			save: action,
			delete: action,
			setResource: action,
		});

		this.apiPathDictionary = {};
		this.mSetResource = this.mSetResource.bind(this);
		this.mSetResource(resource);
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public get resource() {
		return this.mResource;
	}

	public load() {
		if (!this.isBusy && this.mResource) {
			// note: 'as any' due to ts typing issues
			const promise = this.executeCrudRequest('GET', {
				id: this.mResource.id,
			} as any);
			if (promise) {
				this.loading = true;
				const onFinish = () => {
					runInAction(() => {
						this.loading = false;
					});
				};
				promise.then(onFinish).catch(onFinish);
			}
			return promise;
		}
		return null;
	}

	public save = (resource: TResource) => {
		if (resource) {
			this.busy = true;
			return this.executeCrudRequest(resource.id ? 'PUT' : 'POST', resource, (resolve, reject, opResult) => {
				runInAction(() => {
					this.busy = false;
					if (opResult.success) {
						this.mSetResource(opResult.value);
						resolve(opResult.value);
					} else {
						reject(opResult);
					}
				});
			});
		}
		return null;
	};

	public delete = () => {
		if (this.mResource && !this.isBusy) {
			this.busy = true;
			return this.executeCrudRequest('DELETE', this.mResource, (resolve, reject, opResult) => {
				runInAction(() => {
					this.busy = false;
					// note: we eat the 404 error here
					if (opResult.success || opResult.systemCode === 404) {
						resolve(opResult.value || this.mResource);
					} else {
						reject(opResult);
					}
				});
			});
		}
		return null;
	};

	public setResource = (resource: TResource) => {
		this.mSetResource(resource);
	};

	public toJs = () => {
		return this.mResource;
	};

	protected setApiPathForMethod(method: Api.HTTPMethod, path: string) {
		this.apiPathDictionary[method] = path;
	}

	protected mSetResource(resource?: TResource) {
		this.mResource = resource;
	}

	protected executeCrudRequest = (
		method: Api.HTTPMethod,
		resource?: TResource,
		onFinish?: (
			resolve: (value?: TResource | PromiseLike<TResource>) => void,
			reject: (error: Api.IOperationResultNoValue) => void,
			opResult: Api.IOperationResult<TResource>
		) => void
	) => {
		return new Promise<TResource>((resolve, reject) => {
			const onComplete = (opResult: Api.IOperationResult<TResource>) => {
				if (onFinish) {
					onFinish(resolve, reject, opResult);
				} else {
					runInAction(() => {
						// note: we eat the 404 error w.r.t delete ops.
						if (opResult.success || (method === 'DELETE' && opResult.systemCode === 404)) {
							const value = opResult.value || resource;
							this.mSetResource(value);
							resolve(value);
						} else {
							reject(opResult);
						}
					});
				}
			};
			const path = this.apiPathDictionary[method] || `${this.apiBasePath}${method === 'POST' ? '' : `/${resource.id}`}`;
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TResource>(
				path,
				method,
				method !== 'GET' ? resource : null,
				onComplete,
				onComplete
			);
		});
	};
}

export class BoardStageDefinitionViewModel implements Api.IBoardStage {
	public enableStageIndicator: boolean;
	public errorMessage: string;
	public id: string;
	public name: string;
	private mStage: BoardStageViewModel;
	protected mUuid: string;

	constructor(stage?: BoardStageViewModel) {
		makeObservable<BoardStageDefinitionViewModel, 'mStage'>(this, {
			enableStageIndicator: observable,
			errorMessage: observable,
			id: observable,
			name: observable,
			mStage: observable.ref,
			stage: computed,
		});

		if (stage) {
			this.id = stage.id;
			this.enableStageIndicator = stage.enableStageIndicator;
			this.name = stage.name;
			this.mStage = stage;
		}

		this.mUuid = uuidgen();
	}

	public get uuid() {
		return this.mUuid;
	}

	/** Get the actual column in an existing board, if it exists. */
	public get stage() {
		return this.mStage;
	}

	public toJs = () => {
		const stage: Api.IBoardStageReference = {
			config: {
				enableStageIndicator: this.enableStageIndicator,
			},
			id: this.id,
			name: this.name,
			templateType: this.mStage ? this.mStage.templateType : null,
		};
		return stage;
	};
}

export class EditBoardViewModel {
	private saving: boolean;
	private mMoveItemSourceIds: string[];
	private mName: string;
	private mStageDefinitions: BoardStageDefinitionViewModel[];
	private mDefaultAssignees: Partial<Api.IUser>[];
	private mId: string;
	private mUserSession: UserSessionContext;

	constructor(
		userSession: UserSessionContext,
		name?: string,
		stageDefinitions?: BoardStageDefinitionViewModel[],
		defaultAssignees?: Partial<Api.IUser>[]
	) {
		makeObservable<
			EditBoardViewModel,
			'saving' | 'mMoveItemSourceIds' | 'mName' | 'mStageDefinitions' | 'mDefaultAssignees'
		>(this, {
			saving: observable,
			mMoveItemSourceIds: observable.ref,
			mName: observable.ref,
			mStageDefinitions: observable.ref,
			mDefaultAssignees: observable.ref,
			isBusy: computed,
			isMovingStageItems: computed,
			isSaving: computed,
			setName: action,
			columns: computed,
			defaultAssignees: computed,
			defaultAssigneeIds: computed,
			setDefaultAssignees: action,
			addDefaultAssignee: action,
			removeDefaultAssignee: action,
			removeColumnAtIndex: action,
			insertColumn: action,
			moveColumn: action,
			moveStageItems: action,
		});

		this.mUserSession = userSession;
		this.mStageDefinitions = stageDefinitions || [];
		this.mDefaultAssignees = defaultAssignees || [];
		this.mMoveItemSourceIds = [];
		this.mName = name || 'Opportunities';
		this.mId = uuidgen();
	}

	public get isBusy() {
		return this.saving || this.isMovingStageItems;
	}

	public get isMovingStageItems() {
		return this.mMoveItemSourceIds && this.mMoveItemSourceIds.length > 0;
	}

	public get isSaving() {
		return this.saving;
	}

	public get id() {
		return this.mId;
	}

	public setName(newName: string) {
		this.mName = newName;
	}

	public get name() {
		return this.mName;
	}

	public get userSession() {
		return this.mUserSession;
	}

	public get columns() {
		return this.mStageDefinitions;
	}

	public get defaultAssignees() {
		return this.mDefaultAssignees;
	}

	public get defaultAssigneeIds() {
		return this.mDefaultAssignees.map(user => user.id).filter((id): id is string => !!id);
	}

	public setDefaultAssignees = (users: Partial<Api.IUser>[]) => {
		this.mDefaultAssignees = users || [];
	};

	public addDefaultAssignee = (user: Partial<Api.IUser>) => {
		if (!user?.id || this.mDefaultAssignees.some(existing => existing.id === user.id)) {
			return;
		}
		this.mDefaultAssignees = [...this.mDefaultAssignees, user];
	};

	public removeDefaultAssignee = (userId: string) => {
		this.mDefaultAssignees = this.mDefaultAssignees.filter(user => user.id !== userId);
	};

	public removeColumnAtIndex = (index: number) => {
		const columns = [...this.mStageDefinitions];
		columns.splice(index, 1);
		this.mStageDefinitions = columns;
	};

	/** @param index Default = 0. */
	public insertColumn = (stageDefinition: BoardStageDefinitionViewModel, index?: number) => {
		const stageDefinitions = [...this.mStageDefinitions];
		stageDefinitions.splice(index || 0, 0, stageDefinition);
		this.mStageDefinitions = stageDefinitions;
	};

	public moveColumn = (fromIndex: number, toIndex: number) => {
		const stageDefinitions = [...this.mStageDefinitions];
		const removed = stageDefinitions.splice(fromIndex, 1);
		stageDefinitions.splice(toIndex, 0, removed[0]);
		this.mStageDefinitions = stageDefinitions;
	};

	public moveStageItems = (fromStage: BoardStageViewModel, toStage: BoardStageViewModel) => {
		if (fromStage && fromStage.id && toStage && toStage.id) {
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResultNoValue) => {
					runInAction(() => {
						// remove the source id from the tracked collection... affects this.isMovingStageItems
						const moveItemSourceIds = [...(this.mMoveItemSourceIds || [])];
						moveItemSourceIds.splice(moveItemSourceIds.indexOf(fromStage.id), 1);
						this.mMoveItemSourceIds = moveItemSourceIds;

						if (opResult.success) {
							resolve(undefined);
						} else {
							reject(opResult);
						}
					});
				};

				const moveItemsRequest: Api.IMoveBoardItem = {
					stageId: toStage.id,
				};

				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`board/stage/${fromStage.id}/moveItems`,
					'POST',
					moveItemsRequest,
					onFinish,
					onFinish
				);
			});

			// add the source id to the tracked collection... affects this.isMovingStageItems
			this.mMoveItemSourceIds.push(fromStage.id);
			return promise;
		}

		return null;
	};
}
export class BoardItemViewModel<
	TItem extends Api.IBoardItem = Api.IBoardItem,
	TStage extends Api.IBoardStage = Api.IBoardStage,
> extends BaseResourceViewModel<TItem> {
	private mActivityCollectionController: ObservablePageCollectionControllerOld<
		Api.IBoardItemActivity,
		Api.IBoardItemActivity
	>;
	protected mItem: TItem;
	protected mLastModifiedDate: Date;
	protected mStage: BoardStageViewModel;

	constructor(userSession: UserSessionContext, stage?: BoardStageViewModel, item?: TItem) {
		super(userSession);

		makeObservable<
			BoardItemViewModel<TItem, TStage>,
			'mActivityCollectionController' | 'mItem' | 'mLastModifiedDate' | 'mStage'
		>(this, {
			mActivityCollectionController: observable.ref,
			mItem: observable.ref,
			mLastModifiedDate: observable.ref,
			mStage: observable.ref,
			lastModifiedDate: computed,
			lastModifiedDateAsDate: computed,
			id: computed,
			activityItems: computed,
			fetchingActivityItems: computed,
			position: computed,
			setPosition: action,
			stage: computed,
			setStage: action,
			reset: action,
			archive: action,
		});

		this.apiBasePath = 'board/item';
		// set the stage before the resource
		this.mStage = stage;
		this.mSetResource(item);
	}

	public get lastModifiedDate() {
		return this.mItem.lastModifiedDate;
	}

	public get lastModifiedDateAsDate() {
		return this.mLastModifiedDate;
	}

	public get id() {
		return this.mItem.id;
	}

	public get activityItems() {
		return this.mActivityCollectionController.fetchResults;
	}

	public get fetchingActivityItems() {
		return this.mActivityCollectionController.fetching;
	}

	public get position() {
		return this.mItem.position;
	}

	public setPosition(value: number) {
		this.mItem.position = value;
	}

	public get stage() {
		return this.mStage;
	}

	public setStage(stage: BoardStageViewModel) {
		this.mStage = stage;
	}

	public reset = () => {
		if (this.mActivityCollectionController) {
			this.mActivityCollectionController.reset();
		}
	};

	public archive = () => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IOperationResultNoValue>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							resolve(undefined);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`board/item/${this.mItem.id}/archive`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public getActivity = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number, params?: any) => {
		return this.mActivityCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	protected mSetResource(item: TItem) {
		const previousItem = this.mItem;
		super.mSetResource(item);
		this.mItem = item;
		this.mLastModifiedDate = item && item.lastModifiedDate ? new Date(item.lastModifiedDate) : null;

		if (item) {
			if (!this.mStage && item.boardStage) {
				this.mStage = this.createStageViewModel(item.boardStage as TStage);
			}

			// only replace the activity page controller if the item actually changes (not on update)
			if (!previousItem || previousItem.id !== item.id) {
				this.mActivityCollectionController = new ObservablePageCollectionControllerOld<
					Api.IBoardItemActivity,
					Api.IBoardItemActivity
				>(this.userSession.webServiceHelper, `board/item/${item.id}/activity`);
			}
		}
	}

	public toJs = () => {
		return this.mItem;
	};

	protected createStageViewModel(stage: TStage): BoardStageViewModel {
		// FOLLOWUP: Resolve

		// @ts-ignore
		return new BoardStageViewModel(this.mUserSession, stage, null);
	}
}

export class BoardStageViewModel<
	TStage extends Api.IBoardStage = Api.IBoardStage,
	TItem extends Api.IBoardItem = Api.IBoardItem,
	TRollup extends Api.IBoardStageRollup = Api.IBoardStageRollup,
	TItemViewModel extends BoardItemViewModel<TItem> = BoardItemViewModel<TItem>,
	TSearchContext = any,
	TSearchController extends BaseObservablePageCollectionController<
		TItem,
		TItemViewModel,
		TSearchContext
	> = BaseObservablePageCollectionController<TItem, TItemViewModel, TSearchContext>,
> extends BaseResourceViewModel<TStage> {
	protected mboard: BoardViewModel;
	protected mItemsCollectionController: BaseObservablePageCollectionController<
		TItem,
		TItemViewModel,
		Api.ISortDescriptor
	>;
	protected mSearchController: TSearchController;
	protected mStage: TStage;

	constructor(userSession: UserSessionContext, stage: TStage, board?: BoardViewModel) {
		super(userSession);

		makeObservable<
			BoardStageViewModel<TStage, TItem, TRollup, TItemViewModel, TSearchContext, TSearchController>,
			'mboard' | 'mItemsCollectionController' | 'mSearchController' | 'mStage'
		>(this, {
			mboard: observable.ref,
			mItemsCollectionController: observable.ref,
			mSearchController: observable.ref,
			mStage: observable.ref,
			isSearching: computed,
			isFetchingSearchResults: computed,
			isFetchingItems: computed,
			items: computed,
			config: computed,
			isBusy: override,
			id: computed,
			rollup: computed,
			indexInBoard: computed,
			boardStageCount: computed,
			enableStageIndicator: computed,
			name: computed,
			board: computed,
			templateType: computed,
			repositionItem: action,
			moveItemToStage: action,
			deleteItem: action,
			createItem: action,
			archiveItem: action,
			reset: action,
			resetSearch: action,
			setStage: action,
			updateWithItems: action,
			setItems: action,
		});

		this.createItem = this.createItem.bind(this);
		this.createSearchController = this.createSearchController.bind(this);
		this.deleteItem = this.deleteItem.bind(this);
		this.moveItemToStage = this.moveItemToStage.bind(this);
		this.apiBasePath = 'board/stage';
		this.mboard = board;
		this.mSetResource(stage);
	}

	public get isSearching() {
		return this.mSearchController && (this.mSearchController.isFetching || this.mSearchController.hasContext);
	}

	public get isFetchingSearchResults() {
		return this.mSearchController && this.mSearchController.isFetching;
	}

	/** @returns This is only applies to stage items and not search results */
	public get isFetchingItems() {
		return this.mItemsCollectionController && this.mItemsCollectionController.isFetching;
	}

	/** @returns Note: If searching, this will return this.searchController.fetchResults instead of stage items */
	public get items() {
		return this.isSearching
			? this.mSearchController
				? this.mSearchController.fetchResults
				: null
			: this.mItemsCollectionController
				? this.mItemsCollectionController.fetchResults
				: null;
	}

	public get config() {
		return this.mStage.config;
	}

	public get isBusy() {
		return (
			this.busy ||
			this.loading ||
			(this.mItemsCollectionController && this.mItemsCollectionController.isFetching) ||
			(this.mSearchController && this.mSearchController.isFetching)
		);
	}

	public get id() {
		return this.mStage.id;
	}

	public get rollup(): TRollup {
		return this.mStage ? (this.mStage.rollup as TRollup) : ({} as TRollup);
	}

	public get indexInBoard() {
		return this.mStage.boardIndex;
	}

	public get boardStageCount() {
		return this.mStage.boardTotalStageCount;
	}

	public get enableStageIndicator() {
		return this.mStage.config && this.mStage.config.enableStageIndicator;
	}

	public get name() {
		return this.mStage.name;
	}

	public get board() {
		return this.mboard;
	}

	public get templateType() {
		return this.mStage.templateType || (this.mboard ? this.mboard.templateType : null);
	}

	/** Reposition an item within this board */
	public repositionItem = (item: TItemViewModel, toIndex: number): Promise<void> => {
		if (this.items) {
			const currentIndex = this.items.indexOf(item);
			if (!this.isBusy && !item.isBusy && currentIndex >= 0 && currentIndex !== toIndex) {
				this.busy = true;
				VmUtils.setViewModelBusy(item, true);
				const originalItemCollection = this.items.toArray();

				// reorder in UI layer first... don't block the UI
				const itemCollection = [...originalItemCollection];
				itemCollection.splice(currentIndex, 1);
				itemCollection.splice(toIndex, 0, item);
				this.items.setItems(itemCollection);
				item.setPosition(toIndex);

				const itemAtDestination = originalItemCollection[toIndex];

				return new Promise<void>((resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResultNoValue) => {
						runInAction(() => {
							VmUtils.setViewModelBusy(item, false);
							this.busy = false;
							if (opResult.success) {
								resolve();
							} else {
								// restore original collection
								this.items.setItems(originalItemCollection);
								item.setPosition(currentIndex);
								reject(opResult);
							}
						});
					};

					// create the move request
					const moveBoardItem: Api.IMoveBoardItem = {
						fallbackIndex: toIndex,
						position: toIndex > currentIndex ? Api.MoveBoardItemPosition.After : Api.MoveBoardItemPosition.Before,
						relativeToItemId: itemAtDestination ? itemAtDestination.id : null,
						stageId: this.mStage.id,
					};

					this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
						`board/item/${item.id}/move`,
						'POST',
						moveBoardItem,
						onFinish,
						onFinish
					);
				});
			}
		}

		return null;
	};

	/** Move an item to another stage */
	public moveItemToStage(
		item: TItemViewModel,
		toStage: BoardStageViewModel<TStage, TItem>,
		atIndex = 0
	): Promise<void> {
		if (!this.isBusy && item && !item.isBusy && toStage && !toStage.isBusy && this.items && toStage.items) {
			this.busy = true;
			toStage.busy = true;

			// make changes in UI first
			const sourceStageItemCollection = this.items.toArray();
			const originalSourceStageItemCollection = [...sourceStageItemCollection];
			const destinationStageItemCollection = toStage.items.toArray();
			const originalDestinationStageItemCollection = [...destinationStageItemCollection];
			const currentIndexInSourceStage = sourceStageItemCollection.indexOf(item);
			const itemAtDestination = originalDestinationStageItemCollection[atIndex];

			VmUtils.setViewModelBusy(item, true);
			sourceStageItemCollection.splice(currentIndexInSourceStage, 1);
			destinationStageItemCollection.splice(atIndex, 0, item);
			// FOLLOWUP: Resolve

			// @ts-ignore
			item.setStage(toStage);
			item.setPosition(atIndex);

			this.items.setItems(sourceStageItemCollection);
			toStage.items.setItems(destinationStageItemCollection);

			return new Promise<void>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResultNoValue) => {
					runInAction(() => {
						VmUtils.setViewModelBusy(item, false);
						this.busy = false;
						toStage.busy = false;
						if (opResult.success) {
							resolve();
						} else {
							// restore original collections
							this.items.setItems(originalSourceStageItemCollection);
							toStage.items.setItems(originalDestinationStageItemCollection);
							// FOLLOWUP: Resolve

							// @ts-ignore
							item.setStage(this);
							item.setPosition(currentIndexInSourceStage);
							reject(opResult);
						}
					});
				};

				// create the move request
				const moveBoardItem: Api.IMoveBoardItem = {
					fallbackIndex: atIndex,
					position: Api.MoveBoardItemPosition.Before,
					relativeToItemId: itemAtDestination ? itemAtDestination.id : null,
					stageId: toStage.id,
				};

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`board/item/${item.id}/move`,
					'POST',
					moveBoardItem,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	}

	/**
	 * Delete an item from this stage
	 *
	 * @param item
	 */
	public deleteItem(item: TItemViewModel) {
		if (!this.isBusy && !item.isBusy && this.items) {
			this.busy = true;
			return new Promise<TItemViewModel>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<TItem>) => {
					runInAction(() => {
						this.busy = false;
						if (opResult.success) {
							// preform delete in UI
							this.items.removeItems([item]);
							resolve(item);
						} else {
							reject(opResult);
						}
					});
				};

				item
					.delete()
					.then(deletedItem => onFinish({ success: true, value: deletedItem }))
					.catch(onFinish);
			});
		}

		return null;
	}

	/**
	 * Create an item in this stage
	 *
	 * @param item
	 */
	public createItem(item: TItem) {
		if (!this.isBusy && this.items) {
			this.busy = true;
			const boardItem = this.createBoardItemViewModel();

			return new Promise<TItemViewModel>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<TItem>) => {
					runInAction(() => {
						this.busy = false;
						if (opResult.success) {
							this.items.splice(item.position, 0, boardItem);
							resolve(boardItem);
						} else {
							reject(opResult);
						}
					});
				};

				boardItem
					.save(item)
					.then(createdItem => onFinish({ success: true, value: createdItem }))
					.catch(onFinish);
			});
		}
	}

	/** Archive an item */
	public archiveItem(item: TItemViewModel) {
		if (!this.isBusy && !item.isBusy && this.items) {
			this.busy = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.busy = false;
						if (opResult.success) {
							// preform delete in UI
							this.items.removeItems([item]);
							resolve(undefined);
						} else {
							reject(opResult);
						}
					});
				};

				item
					.archive()
					.then(() => onFinish({ success: true }))
					.catch(onFinish);
			});
		}

		return null;
	}

	public reset = () => {
		if (this.mItemsCollectionController) {
			this.mItemsCollectionController.reset();
		}

		if (this.mSearchController) {
			this.mSearchController.reset();
		}
	};

	public resetSearch = () => {
		if (this.mSearchController) {
			this.mSearchController.reset();
		}
	};

	public setStage = (stage: TStage, resetItems = false) => {
		const prevStage = this.mStage;
		// super.mSetResource(stage);
		this.mStage = stage;

		if (this.mStage) {
			// only update/replace this if the prevStage.id has changed... if we need to clear these items, call reset()
			if (resetItems || !prevStage || prevStage.id !== this.mStage.id) {
				this.mItemsCollectionController = new BaseObservablePageCollectionController<TItem, TItemViewModel>({
					apiPath: `board/stage/${this.mStage.id}/items`,
					client: this.mUserSession.webServiceHelper,
					transformer: (itemModel: TItem) => this.createBoardItemViewModel(itemModel),
				});
			}
		} else {
			this.mItemsCollectionController = null;
		}
	};

	/**
	 * Updates items collection (vms) with the given item models. If an item (model) does not has a corresponding vm the
	 * current collection, then it will be added. If a vm the current collection does not exist in the give item (model)
	 * collection, then it will be removed. Item vms will be updated with new models only if "lastModifiedDate" doesn't
	 * match the current value.
	 */
	public updateWithItems = (items: TItem[]) => {
		if (items && this.mStage && this.mItemsCollectionController) {
			const itemModelsDictionary: Api.IDictionary<TItem> = items.reduce<Api.IDictionary<TItem>>((result, item) => {
				result[item.id] = item;
				return result;
			}, {});

			let itemsCollectionModified = false;
			const currentItemsDictionary: Api.IDictionary<TItemViewModel> = this.items.reduce<
				Api.IDictionary<TItemViewModel>
			>((result, itemVm) => {
				// only add current items that are present in the incoming collection
				if (itemModelsDictionary[itemVm.id]) {
					result[itemVm.id] = itemVm;
				} else {
					// item removed
					itemsCollectionModified = true;
				}
				return result;
			}, {});

			const fetchResults: TItemViewModel[] = [];
			items.forEach(x => {
				let itemVm = currentItemsDictionary[x.id];
				if (itemVm) {
					if (itemVm.lastModifiedDate !== x.lastModifiedDate || itemVm.position !== x.position) {
						// only update if the lastModifiedDate has changed
						itemVm.setResource(x);
						itemsCollectionModified = true;
					}
				} else {
					itemVm = this.createBoardItemViewModel(x);
					itemsCollectionModified = true;
				}
				fetchResults.push(itemVm as TItemViewModel);
			});

			if (itemsCollectionModified) {
				this.mItemsCollectionController.fetchResults.setItems(fetchResults);
			}
		}
	};

	public setItems = (items: TItem[]) => {
		if (items && this.mStage && this.mItemsCollectionController) {
			const currentItemsDictionary: Api.IDictionary<TItemViewModel> = this.items.reduce<
				Api.IDictionary<TItemViewModel>
			>((result, item) => {
				result[item.id] = item;
				return result;
			}, {});

			const fetchResults: TItemViewModel[] = [];
			items.forEach(x => {
				let itemVm = currentItemsDictionary[x.id];
				if (itemVm) {
					itemVm.setResource(x);
				} else {
					itemVm = this.createBoardItemViewModel(x);
				}
				fetchResults.push(itemVm as TItemViewModel);
			});

			this.mItemsCollectionController.fetchResults.setItems(fetchResults);
		}
	};

	public getSearchResults = (searchContext?: TSearchContext, pageSize?: number, params?: any) => {
		if (this.mSearchController) {
			return this.mSearchController.getNext(searchContext, pageSize, params);
		}
		return null;
	};

	public getItems = (
		sortDescriptor?: Api.ISortDescriptor,
		pageSize?: number,
		params?: any
	): Bluebird<Api.IPageCollectionControllerFetchResult<TItem[]>> => {
		return this.mItemsCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	public toJs = () => {
		return this.mStage;
	};

	public toColumnDefinition = () => {
		// @ts-ignore
		return new BoardStageDefinitionViewModel(this);
	};

	protected mSetResource(stage: TStage) {
		super.mSetResource(stage);
		this.mStage = stage;

		if (this.mStage) {
			this.mItemsCollectionController = new BaseObservablePageCollectionController<
				TItem,
				TItemViewModel,
				Api.ISortDescriptor
			>({
				apiPath: `board/stage/${this.mStage.id}/items`,
				client: this.mUserSession.webServiceHelper,
				transformer: (itemModel: TItem) => this.createBoardItemViewModel(itemModel),
			});

			this.mSearchController = this.createSearchController(stage);
		} else {
			this.mItemsCollectionController = null;
			this.mSearchController = null;
		}
	}

	protected createBoardItemViewModel(item?: TItem) {
		// @ts-ignore
		return new BoardItemViewModel(this.mUserSession, this, item) as TItemViewModel;
	}

	protected createSearchController(stage: TStage): TSearchController {
		return new BaseObservablePageCollectionController<TItem, TItemViewModel>({
			apiPath: `board/stage/${stage.id}/items/search`,
			client: this.mUserSession.webServiceHelper,
			transformer: (item: TItem) => {
				return this.createBoardItemViewModel(item);
			},
		}) as TSearchController;
	}
}

export class BoardViewModel<
	TBoard extends Api.IBoard = Api.IBoard,
	TStage extends Api.IBoardStage = Api.IBoardStage,
	TItem extends Api.IBoardItem = Api.IBoardItem,
> extends BaseResourceViewModel<TBoard> {
	protected mBoard: TBoard;
	protected mStages: BoardStageViewModel<TStage>[];
	protected mShowArchivedColumn = false;
	protected mStageToReset: TStage;

	constructor(userSession: UserSessionContext, board?: TBoard) {
		super(userSession);

		makeObservable<
			BoardViewModel<TBoard, TStage, TItem>,
			'mBoard' | 'mStages' | 'mShowArchivedColumn' | 'mStageToReset'
		>(this, {
			mBoard: observable.ref,
			mStages: observable.ref,
			mShowArchivedColumn: observable.ref,
			mStageToReset: observable.ref,
			isFetchingSearchResultsInStages: computed,
			isBusy: override,
			stages: computed,
			id: computed,
			name: computed,
			templateType: computed,
			defaultAssigneeIds: computed,
			stageToReset: computed,
			resetStageSearches: action,
			reset: action,
			resetColumn: action,
		});

		this.apiBasePath = 'board';
		this.mSetResource(board);
	}

	/** @returns True if fetching search results in stages */
	public get isFetchingSearchResultsInStages() {
		return this.mStages && this.mStages.length > 0 && !!this.mStages.some(x => x.isFetchingSearchResults);
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public get stages() {
		return this.mStages;
	}

	public get id() {
		return this.mBoard.id;
	}

	public get name() {
		return this.mBoard.name;
	}

	public get templateType() {
		return this.mBoard.template ? this.mBoard.template.type : null;
	}

	public get defaultAssigneeIds(): string[] {
		return this.mBoard.defaultAssigneeIds || [];
	}

	public get stageToReset() {
		return this.mStageToReset;
	}

	public set stageToReset(value: TStage) {
		this.mStageToReset = value;
	}

	public resetStageSearches = () => {
		if (this.mStages && this.mStages.length > 0) {
			this.mStages.forEach(x => x.resetSearch());
		}
	};

	public reset = () => {
		if (this.mStages && this.mStages.length > 0) {
			this.mStages.forEach(x => {
				x.reset();
			});
		}
	};

	resetColumn = () => {
		const stageId = this.stageToReset.id;
		const stage = this.mStages.find(x => x.id === stageId);
		if (stage) {
			stage.reset();
		}
		this.stageToReset = null;
	};

	protected mSetResource(board: TBoard) {
		const prevStages: Api.IDictionary<BoardStageViewModel<TStage>> = (this.stages || []).reduce<
			Api.IDictionary<BoardStageViewModel<TStage>>
		>((result, stage) => {
			result[stage.id] = stage as BoardStageViewModel<TStage>;
			return result;
		}, {});

		super.mSetResource(board);
		this.mBoard = board;
		if (this.mBoard) {
			// FOLLOWUP: Resolve

			// @ts-ignore
			this.mStages = (this.mBoard.stages || []).map(x => {
				const prevBoardStage = prevStages[x.id];
				if (prevBoardStage) {
					// if the board already had a stage for this id, just update and return that so we preserve the items
					prevBoardStage.setStage(x as TStage, false);
					return prevBoardStage;
				}
				return this.createBoardStageViewModel(x as TStage);
			});
		}
	}

	protected createBoardStageViewModel(stage: TStage): BoardStageViewModel {
		// FOLLOWUP: Resolve

		// @ts-ignore
		// @ts-ignore
		return new BoardStageViewModel<TStage>(this.mUserSession, stage, this);
	}

	protected createBoardItemViewModel(item: TItem) {
		const stage =
			this.mStages && this.mStages.length > 0 && item.boardStage && item.boardStage.id
				? this.mStages.find(x => x.id === item.boardStage.id)
				: null;
		// FOLLOWUP: Resolve

		// @ts-ignore
		return new BoardItemViewModel(this.mUserSession, stage, item);
	}
}

export class OpportunitiesSearchController extends BaseObservablePageCollectionController<
	Api.IOpportunity,
	OpportunityViewModel,
	Api.IOpportunitySearchRequest
> {
	protected mUserSession: UserSessionContext;
	protected createdStages: Api.IDictionary<OpportunitiesBoardStageViewModel> = {};

	public static createBoardStageSearchController = (
		userSession: UserSessionContext,
		board: OpportunitiesBoardViewModel,
		stage: OpportunitiesBoardStageViewModel,
		apiParams?: Api.IDictionary
	) => {
		return new OpportunitiesSearchController(userSession, board, stage, apiParams);
	};

	public static createBoardSearchController = (
		userSession: UserSessionContext,
		board: OpportunitiesBoardViewModel,
		apiParams?: Api.IDictionary
	) => {
		return new OpportunitiesSearchController(userSession, board, null, apiParams);
	};

	constructor(
		userSession: UserSessionContext,
		board: OpportunitiesBoardViewModel,
		stage?: OpportunitiesBoardStageViewModel,
		apiParams?: Api.IDictionary
	) {
		super({
			apiParams,
			apiPath: stage ? `board/stage/${stage.id}/items/search` : `board/${board.id}/items/search`,
			client: userSession.webServiceHelper,
			transformer: (opportunityModel: Api.IOpportunity): OpportunityViewModel => {
				let stageVm: OpportunitiesBoardStageViewModel = null;
				if (opportunityModel.boardStage && opportunityModel.boardStage.id) {
					// FOLLOWUP: Resolve

					// @ts-ignore
					stageVm = board.stages.find(x => x.id === opportunityModel.boardStage.id) as OpportunitiesBoardStageViewModel;
					if (!stageVm) {
						// create one
						stageVm = new OpportunitiesBoardStageViewModel(userSession, opportunityModel.boardStage, board);
						this.createdStages[stageVm.id] = stageVm;
					}
				}
				// TS2345: Argument of type 'OpportunitiesBoardStageViewModel' is not assignable to parameter of type 'BoardStageViewModel<IBoardStage, IBoardItem, IBoardStageRollup, BoardItemViewModel<IBoardItem, IBoardStage>, any, BaseObservablePageCollectionController<...>>'.
				// @ts-ignore
				return new OpportunityViewModel(userSession, stageVm, opportunityModel);
			},
		});
		this.mUserSession = userSession;
	}

	protected executeFetch(
		searchRequest?: Api.IOpportunitySearchRequest,
		pageSize?: number,
		params?: Api.IDictionary,
		isFetchEqualToPreviousFetch = true
	) {
		return new Promise<Api.IOperationResult<Api.IPagedCollection<Api.IOpportunity>>>((resolve, reject) => {
			const queryParams = {
				...(this.mApiParams || {}),
				...(params || {}),
				pageSize: pageSize || this.mPageSize,
			};
			VmUtils.removeEmptyKvpFromObject(queryParams);
			const query = toQueryStringParams(queryParams);

			const apiUrl = searchRequest?.stageId ? `board/stage/${searchRequest?.stageId}/items/search` : this.mApiPath;

			this.mClient.callWebServiceWithOperationResults<Api.IPagedCollection<Api.IOpportunity>>(
				`${apiUrl}?${query}`,
				'POST',
				searchRequest,
				opResult => {
					if (!isFetchEqualToPreviousFetch) {
						// clear created stages
						this.createdStages = {};
					}
					resolve(opResult);
				},
				error => {
					reject(error);
				}
			);
		});
	}
}

export class OpportunityViewModel extends BoardItemViewModel<Api.IOpportunity> {
	private declare mCompany: CompanyViewModel;
	private declare mCreator: UserViewModel;
	private declare mOpportunity: Api.IOpportunity;
	private declare mOwner: UserViewModel;
	private declare mPrimaryContact: ContactViewModel;

	constructor(userSession: UserSessionContext, stage: BoardStageViewModel, opportunity?: Api.IOpportunity) {
		super(userSession, stage, opportunity);
		if (!this.mCompany) {
			this.mCompany = undefined;
		}

		makeObservable<OpportunityViewModel, 'mCompany' | 'mCreator' | 'mOpportunity' | 'mOwner' | 'mPrimaryContact'>(
			this,
			{
				mCompany: observable.ref,
				mCreator: observable.ref,
				mOpportunity: observable.ref,
				mOwner: observable.ref,
				mPrimaryContact: observable.ref,
				isLoaded: override,
				company: computed,
				name: computed,
				owner: computed,
				creator: computed,
				primaryContact: computed,
				closeDate: computed,
				dealSize: computed,
				isArchived: computed,
				details: computed,
				mSetResource: action,
			}
		);
	}

	public get isLoaded() {
		return (
			this.loaded ||
			(this.mOpportunity && this.mOpportunity.id && this.mOpportunity.boardStage && !!this.mOpportunity.creator)
		);
	}

	public get company() {
		return this.mCompany;
	}

	public get name() {
		return this.mOpportunity.name;
	}

	public get owner() {
		return this.mOwner;
	}

	public get creator() {
		return this.mCreator;
	}

	public get primaryContact() {
		return this.mPrimaryContact;
	}

	public get closeDate() {
		return this.mOpportunity.closeDate ? new Date(this.mOpportunity.closeDate) : null;
	}

	public get dealSize() {
		return this.mOpportunity.dealSize;
	}

	public get isArchived() {
		return this.mOpportunity.isArchived;
	}

	public get details() {
		return this.mOpportunity.details;
	}

	public mSetResource(opportunity: Api.IOpportunity) {
		super.mSetResource(opportunity);
		this.mOpportunity = opportunity;

		if (opportunity) {
			if (opportunity.company) {
				this.mCompany = new CompanyViewModel(this.mUserSession, opportunity.company);
			}

			if (opportunity.assignees && opportunity.assignees.length > 0) {
				this.mOwner = new UserViewModel(this.mUserSession, opportunity.assignees[0]);
			}

			if (opportunity.creator) {
				this.mCreator = new UserViewModel(this.mUserSession, opportunity.creator);
			}

			if (opportunity.primaryContact) {
				this.mPrimaryContact = new ContactViewModel(this.mUserSession, opportunity.primaryContact);
			}

			if (opportunity.id) {
				this.apiPathDictionary.PUT = `board/item/${opportunity.id}/opportunity`;
			}
		}

		const stageId =
			this.mStage && this.mStage.id
				? this.mStage.id
				: opportunity && opportunity.boardStage
					? opportunity.boardStage.id
					: null;
		if (stageId) {
			this.apiPathDictionary.POST = `board/stage/${stageId}/opportunity`;
		}
	}
}

export class OpportunitiesBoardStageViewModel extends BoardStageViewModel<
	Api.IBoardStage,
	Api.IOpportunity,
	Api.IOpportunityBoardStageRollup,
	OpportunityViewModel,
	Api.IOpportunitySearchRequest,
	OpportunitiesSearchController
> {
	constructor(userSession: UserSessionContext, stage: Api.IBoardStage, board?: OpportunitiesBoardViewModel) {
		// FOLLOWUP: Resolve

		// @ts-ignore
		super(userSession, stage, board);

		makeObservable(this, {
			totalDealSize: computed,
		});
	}

	public get totalDealSize() {
		if (this.rollup) {
			return this.rollup.total || 0;
		}

		return 0;
	}

	public get searchController() {
		return this.mSearchController;
	}

	public mSetResource(resource: Api.IOpportunity) {
		super.mSetResource(resource);
	}

	protected createBoardItemViewModel(item: Api.IOpportunity): OpportunityViewModel {
		// FOLLOWUP: Resolve

		// @ts-ignore
		return new OpportunityViewModel(this.mUserSession, this, item);
	}

	protected createSearchController(): OpportunitiesSearchController {
		return OpportunitiesSearchController.createBoardStageSearchController(
			this.mUserSession,
			// FOLLOWUP: Resolve

			// @ts-ignore
			this.mboard as OpportunitiesBoardViewModel,
			this
		);
	}
}

export class OpportunitiesBoardViewModel extends BoardViewModel<Api.IBoard, Api.IBoardStage, Api.IOpportunity> {
	constructor(userSession: UserSessionContext, board?: Api.IBoard) {
		super(userSession, board);
	}

	// FOLLOWUP: Resolve

	// @ts-ignore
	protected createBoardStageViewModel(stage: Api.IBoardStage): OpportunitiesBoardStageViewModel {
		return new OpportunitiesBoardStageViewModel(this.mUserSession, stage, this);
	}

	public exportBoard = (exportRequest: Api.IOpportunitySearchRequest, includeActivity: boolean) => {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};

			const request: Api.IBoardExportRequest = {
				filter: exportRequest,
				id: this.id,
				includeActivity,
			};

			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				`board/items/export`,
				'POST',
				request,
				onFinish,
				onFinish
			);
		});
		return promise;
	};
}

export class OpportunitiesViewModel extends ViewModel {
	private boardsCollectionController: ObservablePageCollectionControllerOld<Api.IBoard, OpportunitiesBoardViewModel>;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			isBusy: override,
			isFetchingBoards: computed,
			boards: computed,
			reset: action,
			createBoard: action,
		});

		this.boardsCollectionController = new ObservablePageCollectionControllerOld<
			Api.IBoard,
			OpportunitiesBoardViewModel
		>(userSession.webServiceHelper, 'board', { type: 'Opportunity' }, this.createBoardViewModel);
	}

	public get isBusy() {
		return this.loading || this.busy || this.boardsCollectionController.fetching;
	}

	public get isFetchingBoards() {
		return this.boardsCollectionController.fetching;
	}

	public get boards() {
		return this.boardsCollectionController.fetchResults;
	}

	public reset = () => {
		this.boardsCollectionController.reset();
		this.loading = false;
		this.busy = false;
	};

	public createBoard = (board: Api.IBoard) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise((resolve, reject) => {
				const boardVm = new OpportunitiesBoardViewModel(this.mUserSession);
				boardVm
					.save(board)
					.then(() => {
						runInAction(() => {
							this.busy = false;
							resolve(boardVm);
						});
					})
					.catch(reject);
			});
		}
	};

	public getBoards = (
		sortDescriptor?: Api.ISortDescriptor,
		pageSize?: number,
		params?: any
	): Bluebird<Api.IPageCollectionControllerFetchResult<Api.IBoard[]>> => {
		return this.boardsCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	private createBoardViewModel = (board: Api.IBoard) => {
		return new OpportunitiesBoardViewModel(this.mUserSession, board);
	};
}

export class TagViewModel extends ViewModel {
	private mAccountTag: Api.IAccountTag;
	private mDeleting: boolean;
	private mUpdating: boolean;
	private mAlertViewModel: TagAlertViewModel;

	constructor(userSession: UserSessionContext, accountTag?: Api.IAccountTag) {
		super(userSession);

		makeObservable<TagViewModel, 'mAccountTag' | 'mDeleting' | 'mUpdating' | 'mAlertViewModel'>(this, {
			mAccountTag: observable,
			mDeleting: observable,
			mUpdating: observable,
			mAlertViewModel: observable,
			id: computed,
			isDeleting: computed,
			isUpdating: computed,
			stats: computed,
			value: computed,
			name: computed,
			category: computed,
			favoriteFor: computed,
			source: computed,
			canChangeFavoriteFor: computed,
			isBusy: override,
			isLoaded: override,
			tagAlert: computed,
			load: override,
			delete: action,
			setFavoriteFor: action,
			setAccountTag: action,
		});

		this.setAccountTag(accountTag);
	}

	public get id() {
		return this.mAccountTag.id;
	}

	public get isDeleting() {
		return this.mDeleting;
	}

	public get isUpdating() {
		return this.mUpdating;
	}

	public get stats() {
		return this.mAccountTag.stats;
	}

	public get value() {
		return this.mAccountTag.name;
	}

	public get name() {
		return this.mAccountTag.name;
	}

	public get category() {
		return this.mAccountTag.category;
	}

	public get favoriteFor() {
		return this.mAccountTag.favoriteFor;
	}

	public get source() {
		return this.mAccountTag.source;
	}

	public get canChangeFavoriteFor() {
		if (this.mUpdating) {
			return false;
		}

		switch (this.favoriteFor) {
			case Api.FavoriteFor.Account:
				return this.isAdmin;

			default:
				return true;
		}
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public get isLoaded() {
		return this.mAccountTag && this.mAccountTag.name && !!this.mAccountTag.stats;
	}

	public get tagAlert() {
		return this.mAlertViewModel;
	}

	public load() {
		if (!this.isBusy) {
			this.loading = true;
			return new Promise<Api.IAccountTag>((resolve, reject) => {
				const query = this.mAccountTag.name || this.mAccountTag.id || '';
				const onFinish = action((opResult: Api.IOperationResult<Api.IAccountTag[]>) => {
					this.loading = false;
					if (opResult.success) {
						const lowerQuery = query.toLocaleLowerCase();
						const accountTag = opResult.value?.find(
							x => x.name?.toLocaleLowerCase() === lowerQuery || (x.id && query === x.id)
						);
						if (accountTag) {
							this.setAccountTag(accountTag);
							resolve(accountTag);
							return;
						}
						resolve(null);
					} else {
						reject(opResult);
					}
				});

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag[]>(
					`tag/search?query=${encodeURIComponent(query)}&expand=TagAlert`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
	}

	public delete = () => {
		if (!this.isBusy) {
			this.mDeleting = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResultNoValue) => {
					this.mDeleting = false;
					if (opResult.success) {
						resolve(undefined);
					} else {
						reject(opResult);
					}
				});

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					`tag/tags`,
					'DELETE',
					[this.mAccountTag ? this.mAccountTag.name || '' : ''],
					onFinish,
					onFinish
				);
			});
		}
	};

	public setFavoriteFor = (favoriteFor: Api.FavoriteFor) => {
		this.mUpdating = true;
		return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<Api.IAccountTag>) => {
				this.mUpdating = false;
				if (opResult.success) {
					this.mSetAccountTag(opResult.value);
					resolve(undefined);
				} else {
					reject(opResult);
				}
			});

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag>(
				`tag/${this.id}/favorite?favoriteFor=${favoriteFor}&expand=TagAlert`,
				'PUT',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public toJs = () => {
		return this.mAccountTag;
	};

	public setAccountTag = (accountTag: Api.IAccountTag) => {
		this.mSetAccountTag(accountTag);
	};

	private mSetAccountTag(accountTag: Api.IAccountTag) {
		this.mAccountTag = accountTag;
		this.mAlertViewModel = accountTag.tagAlert
			? new TagAlertViewModel(this.mUserSession, accountTag.tagAlert)
			: undefined;
	}
}

export class TagsViewModel extends ViewModel {
	private deleting: boolean;
	private merging: boolean;
	private mSearchQuery: string;
	private searching: boolean;
	private mActiveTag: TagViewModel;
	protected mSearchResults: ObservableCollection<TagViewModel>;
	protected mSelectedTags: ObservableCollection<TagViewModel>;
	protected mTagsPageCollectionController: BaseObservablePageCollectionController<
		Api.IAccountTag,
		TagViewModel,
		Api.ISortDescriptor
	>;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<
			TagsViewModel,
			'deleting' | 'merging' | 'mSearchQuery' | 'searching' | 'mActiveTag' | 'mSearchResults' | 'mSelectedTags'
		>(this, {
			deleting: observable,
			merging: observable,
			mSearchQuery: observable,
			searching: observable,
			mActiveTag: observable.ref,
			mSearchResults: observable.ref,
			mSelectedTags: observable.ref,
			isLoading: override,
			searchQuery: computed,
			searchResults: computed,
			isSearching: computed,
			isDeleting: computed,
			isMerging: computed,
			activeTag: computed,
			selectedTags: computed,
			isBusy: override,
			isFetchingResults: computed,
			tags: computed,
			totalCount: computed,
			removeTags: action,
			reset: action,
			resetSearch: action,
			createTag: action,
			search: action,
			mergeTags: action,
			deleteTags: action,
			getTags: action,
			checkTagForAutomation: action,
			getDefaultTagsByIndustry: action,
			deleteUnused: action,
			reevaluateTaggingGame: action,
		});

		this.mTagsPageCollectionController = new BaseObservablePageCollectionController<
			Api.IAccountTag,
			TagViewModel,
			Api.ISortDescriptor
		>({
			apiParams: { expand: 'TagAlert' },
			apiPath: () => this.composeApiUrl({ urlPath: 'tag' }),
			client: userSession.webServiceHelper,
			itemUniqueIdentifierPropertyPath: 'value',
			transformer: this.createTagViewModel,
		});
		this.mSelectedTags = new ObservableCollection<TagViewModel>(null, 'value');
		this.mSearchResults = new ObservableCollection<TagViewModel>(null, 'value');
	}

	public get isLoading() {
		return (
			this.loading ||
			(!this.mTagsPageCollectionController.hasFetchedFirstPage && this.mTagsPageCollectionController.isFetching)
		);
	}

	public get searchQuery() {
		return this.mSearchQuery;
	}

	public get searchResults() {
		return this.mSearchResults;
	}

	public get isSearching() {
		return this.searching;
	}

	public get isDeleting() {
		return this.deleting;
	}

	public get isMerging() {
		return this.merging;
	}

	public get activeTag() {
		return this.mActiveTag;
	}

	public set activeTag(value: TagViewModel) {
		this.mActiveTag = value;
	}

	public get selectedTags() {
		return this.mSelectedTags;
	}

	public get isBusy() {
		return this.busy || this.loading || this.mTagsPageCollectionController.isFetching || this.merging || this.deleting;
	}

	public get isFetchingResults() {
		return this.mTagsPageCollectionController.isFetching;
	}

	public get tags() {
		return this.mTagsPageCollectionController.fetchResults;
	}

	public get totalCount() {
		return this.mTagsPageCollectionController.totalCount;
	}

	public removeTags = (tags: TagViewModel[]) => {
		this.mRemoveTags(tags);
	};

	public reset = () => {
		this.busy = false;
		this.deleting = false;
		this.loading = false;
		this.mActiveTag = null;
		this.merging = false;
		this.mSearchQuery = null;
		this.mSearchResults.clear();
		this.mSelectedTags.clear();
		this.mTagsPageCollectionController.reset();
		this.searching = false;
	};

	public resetSearch = () => {
		this.mSearchQuery = null;
		this.mSearchResults.clear();
		this.searching = false;
	};

	public createTag = (accountTag: Api.IAccountTag) => {
		return new Promise<TagViewModel>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IAccountTag>) => {
				if (opResult.success) {
					const tag = this.createTagViewModel(opResult.value);
					resolve(tag);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag>(
				this.composeApiUrl({ urlPath: 'tag' }),
				'POST',
				accountTag,
				onFinish,
				onFinish
			);
		});
	};

	public search = (query: string, sortDescriptor?: Api.ISortDescriptor) => {
		if (!this.searching) {
			this.searching = true;
			this.mSearchResults.clear();
			return new Promise<ObservableCollection<TagViewModel>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccountTag[]>) => {
					runInAction(() => {
						this.searching = false;
						if (opResult.success) {
							this.mSearchQuery = query;
							this.mSearchResults.setItems((opResult.value || []).map(x => this.createTagViewModel(x)));
							resolve(this.mSearchResults);
						} else {
							reject(opResult);
						}
					});
				};

				const params: Api.IDictionary<string> = {
					expand: 'TagAlert',
					query,
				};
				if (sortDescriptor) {
					params.sort = sortDescriptor.sort;
					params.sortBy = sortDescriptor.sortBy;
				}

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag[]>(
					this.composeApiUrl({ queryParams: params, urlPath: 'tag/search' }),
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public mergeTags = (tags: TagViewModel[], target: TagViewModel) => {
		if (!this.merging) {
			this.merging = true;
			return new Promise<TagViewModel>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccountTag>) => {
					runInAction(() => {
						const tagsToRemove: TagViewModel[] = [];
						(tags || []).forEach(x => {
							if (x !== target) {
								tagsToRemove.push(x);
							}
							VmUtils.setViewModelBusy(x, false);
						});
						this.mRemoveTags(tagsToRemove);
						this.merging = false;
						if (opResult.success) {
							target.setAccountTag(opResult.value);
							resolve(target);
						} else {
							reject(opResult);
						}
					});
				};

				const mergeRequest: Api.IMergeTagsRequest = {
					intoTag: target.value,
					sourceTags: (tags || []).map(x => {
						VmUtils.setViewModelBusy(x, true);
						return x.value;
					}),
				};

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag>(
					this.composeApiUrl({ queryParams: { expand: 'TagAlert' }, urlPath: 'tag/merge' }),
					'POST',
					mergeRequest,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public deleteTags = (tags: TagViewModel[]) => {
		if (!this.deleting) {
			this.deleting = true;
			return new Promise<TagViewModel>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResultNoValue) => {
					runInAction(() => {
						(tags || []).forEach(x => VmUtils.setViewModelBusy(x, false));
						this.mRemoveTags(tags);
						this.deleting = false;
						if (opResult.success) {
							resolve(undefined);
						} else {
							reject(opResult);
						}
					});
				};

				const tagValues = (tags || []).map(x => {
					VmUtils.setViewModelBusy(x, true);
					return x.value;
				});

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
					this.composeApiUrl({ urlPath: 'tag/tags' }),
					'DELETE',
					tagValues,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public getTags = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number, params?: any) => {
		return this.mTagsPageCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	public exportTags = async () => {
		try {
			const opResult = await this.userSession.webServiceHelper.callWebServiceAsync<Api.ISystemJob>(
				this.composeApiUrl({ urlPath: `tag/export` }),
				'POST'
			);
			if (opResult.success) {
				return opResult.value;
			}

			throw Api.asApiError(opResult);
		} catch (err) {
			throw Api.asApiError(err);
		}
	};

	/** @deprecated Please use getSuggestedTagsForContactsV2 instead */
	public getSuggestedTagsForContacts = (addKeepInTouchTag = false, pageSize = 5) => {
		return this.mGetSuggestedTagsWithPath<string>('contact/suggestedTags', {
			addKeepInTouchTag,
			pageSize,
		});
	};

	public getSuggestedTagsForContactsV2 = (addKeepInTouchTag = false, pageSize = 25) => {
		return this.mGetSuggestedTagsWithPath<Api.IAccountTag>('contact/suggestedTags/v2', { addKeepInTouchTag, pageSize });
	};

	public getSuggestedTagsForEmail = () => {
		return this.mGetSuggestedTagsWithPath<string>('email/suggestedTags');
	};

	private mGetSuggestedTagsWithPath = <T = string | Api.IAccountTag>(
		path: string,
		params?: Api.IDictionary<string | boolean | number>
	) => {
		return new Promise<T[]>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<T[]>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<T[]>(
				this.composeApiUrl({ queryParams: params, urlPath: path }),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	private mRemoveTags = (tags: TagViewModel[]) => {
		this.mTagsPageCollectionController.fetchResults.removeItems(tags);

		tags.forEach(x => {
			// remove from active
			if (this.mActiveTag && x.id === this.mActiveTag.id) {
				this.mActiveTag = null;
			}
		});

		// remove from selected collection
		this.mSelectedTags.removeItems(tags);

		// remove from search results
		this.mSearchResults.removeItems(tags);
	};

	public checkTagForAutomation = (tagToCheck: string) => {
		return new Promise<Api.IAccountTag>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IAccountTag>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccountTag>(
				this.composeApiUrl({ queryParams: { name: tagToCheck }, urlPath: 'tag/byName' }),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public getDefaultTagsByIndustry = (industry: string) => {
		return new Promise<string[]>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string[]>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<string[]>(
				this.composeApiUrl({ queryParams: { industry }, urlPath: 'tag/byIndustry' }),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	protected createTagViewModel = (accountTag: Api.IAccountTag) => {
		return new TagViewModel(this.mUserSession, accountTag);
	};

	public deleteUnused = () => {
		return new Promise<void>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<void>) => {
				if (opResult.success) {
					resolve();
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<void>(
				this.composeApiUrl({ urlPath: 'tag/deleteUnused' }),
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public reevaluateTaggingGame = () => {
		return new Promise<Api.IOperationResult<number>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<number>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<number>(
				this.composeApiUrl({ urlPath: 'contact/reevaluateTaggingGame' }),
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
	};
}

export class TagAlertViewModel extends BaseResourceViewModel<Partial<Api.ITagAlert>> {
	private mLastReportSendDate: Date;
	constructor(userSession: UserSessionContext, tagAlert?: Partial<Api.ITagAlert>) {
		super(userSession, tagAlert);

		makeObservable<TagAlertViewModel, 'mLastReportSendDate'>(this, {
			mLastReportSendDate: observable.ref,
			isLoaded: override,
			interactionFilter: computed,
			contactCount: computed,
			interactionInterval: computed,
			lastReportSendDate: computed,
			id: computed,
			tagValue: computed,
			tagNotifyOptions: computed,
			requireValidEmailAddress: computed,
			sendToDescription: computed,
		});

		this.apiBasePath = 'tag/alert';
	}

	public get isLoaded() {
		return this.mResource && this.mResource.id && !!this.mResource.tag;
	}

	public get interactionFilter() {
		return this.mResource.interactionFilter;
	}

	public get contactCount() {
		// FOLLOWUP: Resolve

		// @ts-ignore
		return this.mResource.contactCount;
	}

	/** Time in days */
	public get interactionInterval() {
		return this.mResource.interactionIntervalInDays;
	}

	public get lastReportSendDate() {
		return this.mLastReportSendDate;
	}

	public get id() {
		return this.mResource.id;
	}

	public get tagValue() {
		return this.mResource.tag;
	}

	public get tagNotifyOptions() {
		return this.mResource.sendAlertsTo;
	}

	public get requireValidEmailAddress() {
		return this.mResource.requireValidEmailAddress;
	}

	public get sendToDescription() {
		const sendAlertsTo = this.tagNotifyOptions;
		if (!sendAlertsTo) {
			return undefined;
		}

		return sendAlertsTo.resourceOwner ? 'Contact Owner' : getDisplayName(sendAlertsTo.user as Api.IPrincipal, false);
	}

	protected mSetResource(tagAlert?: Api.ITagAlert) {
		super.mSetResource(
			tagAlert
				? {
						...tagAlert,
						interactionFilter: {
							user: null, // needed for mobx
							...tagAlert.interactionFilter,
						},
						requireValidEmailAddress: tagAlert.requireValidEmailAddress,
						sendAlertsTo: {
							user: null, // needed for mobx
							...tagAlert.sendAlertsTo,
						},
					}
				: null
		);
		if (tagAlert) {
			if (tagAlert.id) {
				this.setApiPathForMethod('GET', `tag/alert/${tagAlert.id}`);
			} else {
				this.setApiPathForMethod('GET', `tag/alert/bytag?tag=${tagAlert.tag}`);
			}
			this.mLastReportSendDate = tagAlert.lastReportSendDate ? new Date(tagAlert.lastReportSendDate) : null;
		} else {
			this.mLastReportSendDate = null;
		}
	}
}

export enum KeepInTouchCommitmentInterval {
	Never = 0,
	Daily,
	MultipleTimesAWeek,
	Weekly,
}

export enum KeepInTouchCommitmentIntervalError {
	None = 0,
	SelectAtLeastOneDay,
	SelectAtLeasTwoDays,
	NeverNotSatisfied,
}

export class KeepInTouchCommitmentPreferencesViewModel {
	private mKitCommitmentPreferences: Api.IKeepInTouchCommitmentPreferences;
	protected busy: boolean;
	protected mInterval: KeepInTouchCommitmentInterval;
	protected mUser: Api.IUser;
	protected mUserSession: UserSessionContext;

	constructor(userSession: UserSessionContext, user: Api.IUser) {
		makeObservable<KeepInTouchCommitmentPreferencesViewModel, 'mKitCommitmentPreferences' | 'busy' | 'mInterval'>(
			this,
			{
				mKitCommitmentPreferences: observable,
				busy: observable,
				mInterval: observable,
				weeklyDays: computed,
				setWeeklyDays: action,
				calendarBlockBusy: computed,
				setCalendarBlockBusy: action,
				calendarBlockInMinutes: computed,
				setCalendarBlockInMinutes: action,
				isDaily: computed,
				setIsDaily: action,
				isBusy: computed,
				error: computed,
				interval: computed,
				setInterval: action,
				reset: action,
			}
		);

		this.mUser = user;
		this.mUserSession = userSession;
		this.reset();
	}

	public get weeklyDays() {
		return this.mKitCommitmentPreferences.weeklyDays;
	}

	public setWeeklyDays = (weeklyDays: Api.DayOfWeek[]) => {
		this.mKitCommitmentPreferences.weeklyDays = weeklyDays || [];
	};

	public get calendarBlockBusy() {
		return this.mKitCommitmentPreferences.calendarBlockBusy;
	}

	public setCalendarBlockBusy = (value: boolean) => {
		this.mKitCommitmentPreferences.calendarBlockBusy = value;
	};

	public get calendarBlockInMinutes() {
		return this.mKitCommitmentPreferences.calendarBlockInMinutes;
	}

	public setCalendarBlockInMinutes = (value: number) => {
		this.mKitCommitmentPreferences.calendarBlockInMinutes = value;
	};

	public get isDaily() {
		return this.mKitCommitmentPreferences.daily;
	}

	public setIsDaily = (value: boolean) => {
		this.mKitCommitmentPreferences.daily = value;
	};

	public get isBusy() {
		return this.busy;
	}

	public get error() {
		switch (this.mInterval) {
			case KeepInTouchCommitmentInterval.Daily: {
				this.mKitCommitmentPreferences.weeklyDays = [];
				break;
			}
			case KeepInTouchCommitmentInterval.MultipleTimesAWeek: {
				if ((this.mKitCommitmentPreferences.weeklyDays || []).length < 2) {
					return KeepInTouchCommitmentIntervalError.SelectAtLeasTwoDays;
				}
				break;
			}
			case KeepInTouchCommitmentInterval.Weekly: {
				if ((this.mKitCommitmentPreferences.weeklyDays || []).length < 1) {
					return KeepInTouchCommitmentIntervalError.SelectAtLeastOneDay;
				}
				break;
			}
			default: {
				if (
					this.mKitCommitmentPreferences.calendarBlockBusy !== false ||
					this.mKitCommitmentPreferences.daily !== false ||
					this.mKitCommitmentPreferences.weeklyDays !== null ||
					this.mKitCommitmentPreferences.weeklyDays.length > 0
				) {
					return KeepInTouchCommitmentIntervalError.NeverNotSatisfied;
				}
				break;
			}
		}

		return KeepInTouchCommitmentIntervalError.None;
	}

	public get interval() {
		return this.mInterval;
	}

	public setInterval = (value: KeepInTouchCommitmentInterval) => {
		this.mInterval = value;
		if (value === KeepInTouchCommitmentInterval.Daily) {
			this.mKitCommitmentPreferences.daily = true;
			this.mKitCommitmentPreferences.weeklyDays = [];
		} else if (value === KeepInTouchCommitmentInterval.Never) {
			this.mKitCommitmentPreferences.calendarBlockBusy = false;
			this.mKitCommitmentPreferences.daily = false;
			this.mKitCommitmentPreferences.weeklyDays = [];
		} else {
			this.mKitCommitmentPreferences.daily = false;
			const weekdayCount = (this.mKitCommitmentPreferences.weeklyDays || []).length;
			if (value === KeepInTouchCommitmentInterval.Weekly && weekdayCount > 1) {
				this.mKitCommitmentPreferences.weeklyDays = [];
			}
		}
	};

	public reset = () => {
		this.mKitCommitmentPreferences = {
			calendarBlockBusy: false,
			daily: false,
			weeklyDays: [Api.DayOfWeek.Monday],
			...(this.mUser.userPreferences.keepInTouchCommitmentPreferences || {}),
		};

		if (this.mKitCommitmentPreferences.daily) {
			this.mKitCommitmentPreferences.weeklyDays = [];
			this.mInterval = KeepInTouchCommitmentInterval.Daily;
		} else {
			if (!this.mKitCommitmentPreferences.weeklyDays || this.mKitCommitmentPreferences.weeklyDays.length === 0) {
				this.mInterval = KeepInTouchCommitmentInterval.Never;
			} else {
				this.mInterval =
					(this.mKitCommitmentPreferences.weeklyDays || []).length > 1
						? KeepInTouchCommitmentInterval.MultipleTimesAWeek
						: KeepInTouchCommitmentInterval.Weekly;
			}
		}
	};

	public save = () => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IKeepInTouchCommitmentPreferences>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IKeepInTouchCommitmentPreferences>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mUser.userPreferences.keepInTouchCommitmentPreferences = result.value;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IKeepInTouchCommitmentPreferences>(
					`user/${this.mUser.id}/keepInTouchCommitmentPreferences`,
					'PUT',
					this.toJs(),
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public toJs = () => {
		return { ...this.mKitCommitmentPreferences };
	};
}

export class EntityRichContentViewModel<
	TEntity extends Api.IEntity = Api.IEntity,
	TEntityViewModel extends EntityViewModel<TEntity> = EntityViewModel<TEntity>,
> {
	private mRichContentPageCollectionController: ObservablePageCollectionController<
		Api.IRichContent,
		RichContentViewModel
	>;
	private mEntity: TEntityViewModel;

	constructor(entity: TEntityViewModel) {
		makeObservable<EntityRichContentViewModel<TEntity, TEntityViewModel>, 'mRichContentPageCollectionController'>(
			this,
			{
				mRichContentPageCollectionController: observable.ref,
				controller: computed,
			}
		);

		this.mEntity = entity;

		this.mRichContentPageCollectionController = new ObservablePageCollectionController<
			Api.IRichContent,
			RichContentViewModel
		>({
			apiPath: `richcontent/by${entity instanceof CompanyViewModel ? 'Company' : 'Contact'}/${this.mEntity.id}`,
			client: this.mEntity.userSession.webServiceHelper,
			transformer: this.mCreateRichContentVm,
		});
	}

	public get controller() {
		return this.mRichContentPageCollectionController;
	}

	private mCreateRichContentVm = (richContentModel: Api.IRichContent) => {
		if (richContentModel && richContentModel._type === 'ActionItem') {
			return new ActionItemViewModel(this.mEntity.userSession, richContentModel as Api.IActionItem);
		}
		return new NoteViewModel(this.mEntity.userSession, richContentModel as Api.INote);
	};
}

export class EntityTimelineViewModel<
	TEntity extends Api.IEntity = Api.IEntity,
	TEntityViewModel extends EntityViewModel<TEntity> = EntityViewModel<TEntity>,
> {
	private mTimelinePageCollectionController: ObservablePageCollectionController<
		Api.ITimelineEvent,
		TimelineEventViewModel
	>;
	private mEntity: TEntityViewModel;

	constructor(entity: TEntityViewModel) {
		makeObservable<EntityTimelineViewModel<TEntity, TEntityViewModel>, 'mTimelinePageCollectionController'>(this, {
			mTimelinePageCollectionController: observable,
			controller: computed,
		});

		this.mEntity = entity;

		this.mTimelinePageCollectionController = new ObservablePageCollectionController<
			Api.ITimelineEvent,
			TimelineEventViewModel
		>({
			apiPath: `timeline/${entity instanceof CompanyViewModel ? 'Company' : 'Contact'}/${this.mEntity.id}`,
			client: this.mEntity.userSession.webServiceHelper,
			httpMethod: 'GET',
			transformer: x => this.mCreateEventVm(x),
		});
	}

	public get controller() {
		return this.mTimelinePageCollectionController;
	}

	protected mCreateEventVm = (event: Api.ITimelineEvent) => {
		switch (event?._type) {
			case 'ActionItemEvent': {
				return new ActionItemEventViewModel(this.mEntity.userSession, event as Api.IActionItemEvent);
			}
			case 'ConversationThreadEvent': {
				return new ConversationThreadEventViewModel(this.mEntity.userSession, event as Api.IConversationThreadEvent);
			}
			case 'SentEmailEvent': {
				return new SentEmailEventViewModel(this.mEntity.userSession, event as Api.ISentEmailEvent);
			}
			case 'HtmlNewsletterEvent': {
				return new HtmlNewsletterEventViewModel(this.mEntity.userSession, event as Api.IHtmlNewsletterEvent);
			}
			case 'NoteEvent': {
				return new NoteEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'PhoneCallCompletedEvent': {
				return new PhoneCallCompletedEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'UntrackedPhoneCallEvent': {
				return new UntrackedPhoneCallEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'DealCreatedEvent': {
				return new DealCreatedEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'DealUpdatedEvent': {
				return new DealUpdatedEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'SkipLeadEvent': {
				return new SkipLeadEventViewModel(this.mEntity.userSession, event as Api.INoteEvent);
			}
			case 'PhoneCallEvent': {
				return new PhoneCallEventViewModel(this.mEntity.userSession, event);
			}
			case 'MeetingEvent': {
				return new MeetingEventViewModel(this.mEntity.userSession, event);
			}
			case 'FollowUpEvent': {
				return new FollowUpEventViewModel(this.mEntity.userSession, event);
			}
			case 'CancelledFollowUpEvent': {
				return new CancelledFollowUpEventViewModel(this.mEntity.userSession, event);
			}
			case 'RescheduledFollowUpEvent': {
				return new RescheduledFollowUpEventViewModel(this.mEntity.userSession, event);
			}
			case 'SurveyResponseEvent':
			case 'CustomSurveyResponseEvent':
			case 'DonationSurveyResponseEvent':
			case 'EventRegistrationSurveyResponseEvent':
			case 'SatisfactionSurveyResponseEvent': {
				return new SurveyResponseEventViewModel(this.mEntity.userSession, event as Api.ISurveyResponseEvent);
			}
			case 'HandwrittenCardOrderEvent': {
				return new HandwrittenCardOrderEventViewModel(this.mEntity.userSession, event);
			}
			case 'FileDownloadEvent': {
				return new FileDownloadEventViewModel(this.mEntity.userSession, event as Api.IFileDownloadEvent);
			}
			case 'FileDeleteEvent': {
				return new FileDeleteEventViewModel(this.mEntity.userSession, event as Api.IFileDeleteEvent);
			}
			case 'FileSendEvent': {
				return new FileSendEventViewModel(this.mEntity.userSession, event as Api.IFileSendEvent);
			}
			default: {
				break;
			}
		}

		return new TimelineEventViewModel(this.mEntity.userSession, event);
	};
}

export enum RichContentComposerResult {
	None = 0,
	Edit,
	Delete,
	Create,
}

export type RichContentComposerCompletion<
	TModel extends Api.IRichContent = Api.IRichContent,
	TViewModel extends RichContentViewModel<TModel> = RichContentViewModel<TModel>,
> = (richContent?: TViewModel, composerResult?: RichContentComposerResult) => void;

export class RichContentComposerViewModel<
	TModel extends Api.IRichContent = Api.IRichContent,
	TViewModel extends RichContentViewModel<TModel> = RichContentViewModel<TModel>,
> {
	protected mRichContentViewModel: TViewModel;
	protected mOnCompleteCallback: RichContentComposerCompletion<TModel, TViewModel>;
	protected mUserSession: UserSessionContext;

	constructor() {
		makeObservable<RichContentComposerViewModel<TModel, TViewModel>, 'mRichContentViewModel'>(this, {
			mRichContentViewModel: observable.ref,
			richContent: computed,
			show: action,
			showWithReferencedEntities: action,
			reset: action,
		});
		this.mCreateRichContentViewModel = this.mCreateRichContentViewModel.bind(this);
		this.mShowWithReferencedEntities = this.mShowWithReferencedEntities.bind(this);
		this.showWithReferencedEntities = this.showWithReferencedEntities.bind(this);
	}

	public get richContent() {
		return this.mRichContentViewModel;
	}

	public show = (richContent: TViewModel, onComplete?: RichContentComposerCompletion<TModel, TViewModel>) => {
		this.mRichContentViewModel = richContent;
		this.mOnCompleteCallback = onComplete;
	};

	public showWithReferencedEntities(
		referencedEntities: EntityViewModel[],
		contactIds?: string[],
		companyIds?: string[],
		onComplete?: RichContentComposerCompletion<TModel, TViewModel>
	) {
		return this.mShowWithReferencedEntities(referencedEntities, contactIds, companyIds, onComplete);
	}

	public reset = () => {
		this.mRichContentViewModel = null;
		this.mOnCompleteCallback = null;
	};

	public setUserSession = (userSession: UserSessionContext) => {
		this.mUserSession = userSession;
	};

	public onComplete = (richContent?: TViewModel, composerResult?: RichContentComposerResult) => {
		if (this.mOnCompleteCallback) {
			this.mOnCompleteCallback(richContent, composerResult);
		}

		this.reset();
	};

	/** Subclasses should implement this */
	protected mCreateRichContentViewModel(_: TModel): TViewModel;
	protected mCreateRichContentViewModel(): TViewModel {
		throw new Error('Not Implemented');
	}

	private getAllCompaniesById(companyIds: string[]) {
		const promise = new Bluebird<CompanyViewModel[]>((resolve, _, onCancel) => {
			let canceled = false;
			if (onCancel) {
				onCancel(() => {
					canceled = true;
				});
			}

			const companies: CompanyViewModel[] = [];
			if (companyIds && companyIds.length > 0) {
				const promises = companyIds.map(x => {
					const company = new CompanyViewModel(this.mUserSession, { id: x });
					return { company, promise: company.load() };
				});
				promises.forEach(x => {
					const onFinish = (success: boolean) => {
						if (success) {
							companies.push(x.company);
						}
						const index = promises.findIndex(y => y.company === x.company);
						promises.splice(index, 1);

						if (!canceled && promises.length === 0) {
							resolve(companies);
						}
					};

					x.promise
						.then(() => {
							onFinish(true);
						})
						.catch(() => {
							onFinish(false);
						});
				});
			} else {
				resolve(companies);
			}
		});
		return promise;
	}

	protected mShowWithReferencedEntities(
		referencedEntities: EntityViewModel[],
		contactIds?: string[],
		companyIds?: string[],
		onComplete?: RichContentComposerCompletion<TModel, TViewModel>
	) {
		const entities: EntityViewModel[] = [...(referencedEntities || [])];
		const loadPromise = new Bluebird<EntityViewModel[]>((resolve, _, onCancel) => {
			let canceled = false;
			if (onCancel) {
				onCancel(() => {
					canceled = true;
				});
			}

			const count = (contactIds || []).length + (companyIds || []).length;
			if (count === 0) {
				if (!canceled) {
					resolve(entities);
				}
				return;
			}

			const promises = [
				ContactsViewModel.getAllByIds(this.mUserSession, contactIds),
				this.getAllCompaniesById(companyIds),
			];
			const promise = Promise.all<EntityViewModel[]>(promises);
			promise.then(loadedEntityCollections => {
				if (!canceled) {
					loadedEntityCollections.forEach(x => x.forEach(y => entities.push(y)));
					resolve(entities);
				}
			});
		});

		loadPromise.then(allEntities => {
			if (!this.mRichContentViewModel) {
				const richContentModel = Api.createRichContentWithReferencedEntities<TModel>(
					(allEntities || []).map(x => x.toJs())
				);
				const richContentViewModel = this.mCreateRichContentViewModel(richContentModel);
				this.show(richContentViewModel, onComplete);
			}
		});
		return loadPromise;
	}
}

export class NoteComposerViewModel extends RichContentComposerViewModel<Api.INote, NoteViewModel> {
	protected mCreateRichContentViewModel(noteModel: Api.INote) {
		return new NoteViewModel(this.mUserSession, noteModel);
	}
}

export class ActionItemComposerViewModel extends RichContentComposerViewModel<Api.IActionItem, ActionItemViewModel> {
	protected mCreateRichContentViewModel(actionItemModel: Api.IActionItem) {
		return new ActionItemViewModel(this.mUserSession, actionItemModel);
	}
}

export interface IDropdownOption<T> {
	title: string;
	value?: T;
}

export const campaignDateRangeOptions: IDropdownOption<number>[] = [
	{
		title: 'Last 30 days',
		value: 30,
	},
	{
		title: 'Last 90 days',
		value: 90,
	},
	{
		title: 'Last 180 days',
		value: 180,
	},
];

export class CampaignsReportingViewModel<
	TViewModel extends CampaignViewModel = CampaignViewModel,
> extends PagedViewModel<Api.ICampaign, TViewModel, Api.ICampaignReportRequest> {
	private mCampaignsCategoryFilters: Api.EmailReportingCategory[];
	private mCampaignsReportType: Api.BulkEmailReportType.Campaigns | Api.BulkEmailReportType.IndividualEmails =
		Api.BulkEmailReportType.Campaigns;
	protected mSelectedUserId: string = undefined;
	public mSelectedCampaignStatus: Api.EmailSendStatus;
	public pageSize: number;
	protected mSelectedDateRangeOption: IDropdownOption<number> = campaignDateRangeOptions[0];
	public dateRange: { startDate?: Date; endDate?: Date };
	public resourceSelectorId: string;

	constructor(userSession: UserSessionContext, user?: Partial<Api.IUser>) {
		super(userSession);

		makeObservable<
			CampaignsReportingViewModel<TViewModel>,
			'mCampaignsCategoryFilters' | 'mCampaignsReportType' | 'mSelectedUserId' | 'mSelectedDateRangeOption'
		>(this, {
			mCampaignsCategoryFilters: observable.ref,
			mCampaignsReportType: observable,
			mSelectedUserId: observable,
			mSelectedCampaignStatus: observable,
			pageSize: observable,
			mSelectedDateRangeOption: observable.ref,
			dateRange: observable.ref,
			resourceSelectorId: observable.ref,
			campaignsReportType: computed,
			campaignsCategoryFilters: computed,
			request: computed,
			selectedDateRangeOption: computed,
			setSelectedDateRangeOption: action,
			load: override,
		});

		this.reset = this.reset.bind(this);

		this.mSelectedUserId = user?.id || (this.isAdmin ? undefined : userSession.user.id);
		this.mCampaignTransformer = this.mCampaignTransformer.bind(this);
		this.pageSize = 100;

		this.mPagedCollectionController = new FilteredPageCollectionController<
			Api.ICampaign,
			TViewModel,
			Api.ICampaignReportRequest
		>({
			apiPath: this.apiPathBase,
			client: this.mUserSession.webServiceHelper,
			transformer: this.mCampaignTransformer,
		});
	}

	public get campaignsReportType() {
		return this.mCampaignsReportType;
	}

	public get campaignsCategoryFilters() {
		return this.mCampaignsCategoryFilters;
	}

	public get request(): Api.ICampaignReportRequest {
		const property =
			this.mCampaignsReportType === Api.BulkEmailReportType.Campaigns
				? Api.BulkEmailFilterProperty.OnlyCampaigns
				: Api.BulkEmailFilterProperty.IndividualSends;

		const filter: Api.IFilterCriteria<Api.BulkEmailFilterProperty> = {
			criteria: [{ property }],
			op: Api.FilterOperator.And,
		};

		if (this.mSelectedUserId) {
			filter.criteria.push({
				property: Api.BulkEmailFilterProperty.User,
				value: this.mSelectedUserId,
			});
		}

		if (this.mCampaignsCategoryFilters?.length > 0) {
			const categoryFilter: Api.IFilterCriteria<Api.BulkEmailFilterProperty> = {
				criteria: [],
				op: Api.FilterOperator.Or,
			};
			this.mCampaignsCategoryFilters.forEach(categoryOrProperty => {
				categoryFilter.criteria.push(
					categoryOrProperty === Api.BulkEmailFilterProperty.OtherIndividualSends
						? {
								property: Api.BulkEmailFilterProperty.OtherIndividualSends,
							}
						: {
								property: Api.BulkEmailFilterProperty.Category,
								value: categoryOrProperty,
							}
				);
			});

			filter.criteria.push(categoryFilter);
		}

		if (this.mSelectedCampaignStatus) {
			filter.criteria.push({
				property: Api.BulkEmailFilterProperty.Status,
				value: this.mSelectedCampaignStatus,
			});
		}

		if (this.resourceSelectorId) {
			filter.criteria.push({
				property: Api.BulkEmailFilterProperty.ResourceSelector,
				value: this.resourceSelectorId,
			});
		}

		const request = {
			endDate: this.dateRange?.endDate,
			filter,
			startDate: this.dateRange?.startDate,
			userId: this.mSelectedUserId,
		};

		return request;
	}

	public get selectedDateRangeOption() {
		return this.mSelectedDateRangeOption;
	}

	public setSelectedDateRangeOption(value: IDropdownOption<number>) {
		this.mSelectedDateRangeOption = value;
		this.dateRange = {
			startDate: moment().startOf('day').subtract(this.mSelectedDateRangeOption.value, 'days').toDate(),
		};
	}

	load(params?: Api.IDictionary): Promise<Api.IPageCollectionControllerFetchResult<Api.ICampaign[]>> {
		return this.fetch(params) as any;
	}

	public set campaignReportType(type: Api.BulkEmailReportType.Campaigns | Api.BulkEmailReportType.IndividualEmails) {
		this.mCampaignsReportType = type;
	}

	public set categoryFilters(categories: Api.EmailReportingCategory[]) {
		this.mCampaignsCategoryFilters = categories;
	}

	protected mCampaignTransformer(campaign: Api.ICampaign) {
		return new CampaignViewModel(this.userSession, campaign).impersonate(this.mImpersonationContext);
	}

	public readonly apiPathBase = 'reports/campaign';

	public get dateRangeOptions() {
		return campaignDateRangeOptions;
	}

	public set selectedUserId(userId: string | undefined) {
		this.mSelectedUserId = userId;
	}

	public get selectedCampaignStatus() {
		return this.mSelectedCampaignStatus;
	}

	public set selectedCampaignStatus(status: Api.EmailSendStatus) {
		this.mSelectedCampaignStatus = status;
	}

	public impersonate(impersonationContext?: Api.IImpersonationContext) {
		super.impersonate(impersonationContext);
		this.mPagedCollectionController.impersonate(impersonationContext);
		return this;
	}

	public reset(): void {
		super.reset();
		this.mCampaignsCategoryFilters = [];
		this.mCampaignsReportType = null;
		this.mSelectedDateRangeOption = null;
		this.mSelectedUserId = null;
	}
}

export interface ICampaignEmailRecipient {
	contact: ContactViewModel;
	emailAddress: string;
	hasReplied?: boolean;
	displayName?: string;
}

/** One email in a larger campaign */
export class CampaignEmailViewModel extends ViewModel {
	private mLoadingPromise: Promise<Api.ICampaignEmailContent>;
	private mModel: Partial<Api.ICampaignEmailContent>;
	private mRecipients: ICampaignEmailRecipient[];

	constructor(userSession: UserSessionContext, model: Partial<Api.ICampaignEmailContent>) {
		super(userSession);

		makeObservable<CampaignEmailViewModel, 'mModel' | 'mRecipients'>(this, {
			mModel: observable.ref,
			mRecipients: observable.ref,
			hasContent: computed,
			sender: computed,
			subject: computed,
			status: computed,
			senderName: computed,
			senderEmail: computed,
			tags: computed,
			htmlContent: computed,
			contactViewModel: computed,
			displayName: computed,
			recipients: computed,
			clickDate: computed,
			clickTargets: computed,
			load: override,
		});

		this.mSetModel = this.mSetModel.bind(this);
		this.mSetModel(model);
	}

	public get hasContent() {
		return !!this.mModel.htmlContent;
	}

	public get sender() {
		return this.mModel.creator;
	}

	public get subject() {
		return this.mModel.subject;
	}

	public get status() {
		return this.mModel.status;
	}

	public get senderName() {
		if (!this.sender) {
			return undefined;
		}

		return getDisplayName(this.sender);
	}

	public get senderEmail() {
		const sender = this.sender;
		return sender && sender.primaryEmail ? sender.primaryEmail.value : undefined;
	}

	public get tags() {
		return this.mModel.tags;
	}

	public get htmlContent() {
		return this.mModel.htmlContent;
	}

	// TODO: remove contactViewModel when the api stops returning it
	public get contactViewModel() {
		return this.recipients && this.recipients.length > 0 ? this.recipients[0].contact : null;
	}

	public get displayName() {
		return this.recipients && this.recipients.length > 0
			? (this.recipients[0].displayName ?? this.recipients[0].contact.name)
			: null;
	}

	public get recipients() {
		return this.mRecipients;
	}

	public get clickDate() {
		return this.mModel?.clickDate ? new Date(this.mModel.clickDate) : null;
	}

	public get clickTargets() {
		return this.mModel?.clickTargets;
	}

	public load(): Promise<Api.ICampaignEmailContent> {
		if (this.mLoadingPromise) {
			return this.mLoadingPromise;
		}

		this.loaded = false;
		this.busy = true;
		this.loading = true;

		this.mLoadingPromise = new Promise((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ICampaignEmailContent>) => {
				runInAction(() => {
					this.loading = false;
					this.mLoadingPromise = null;
					this.busy = false;
					if (opResult.success) {
						this.mSetModel(opResult.value);
						this.loaded = true;
						resolve(opResult.value);
					} else {
						this.loaded = false;
						reject(opResult);
					}
				});
			};

			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICampaignEmailContent>(
				`reports/campaign/transactions/${this.id}`,
				'GET', // Method
				null, // Body (null if it's a GET operation)
				onFinish,
				onFinish
			);
		});

		return this.mLoadingPromise;
	}

	public get id() {
		return this.mModel.id;
	}

	public get sentDate() {
		return this.mModel.sentDate;
	}

	public get viewedDate() {
		return this.mModel.openDate;
	}

	public get repliedDate() {
		return this.mModel.repliedDate;
	}

	public get unsubscribedDate() {
		return this.mModel.unsubscribedDate;
	}

	public get isCustom() {
		return this.mModel.isCustom;
	}

	public toJs() {
		return this.mModel;
	}

	public applyUpdate = (update: Api.IEmailUpdate) => {
		if (update.id === this.mModel?.id) {
			this.mModel = {
				...this.mModel,
				...Api.selectKeysOf(update, ['openDate', 'repliedDate', 'sentDate', 'status']),
			};
		}
	};

	private mSetModel(model: Partial<Api.ICampaignEmailContent>) {
		this.mModel = model;
		this.mRecipients = (model.toRecipients || []).map<ICampaignEmailRecipient>(x => {
			return {
				contact: new ContactViewModel(this.userSession, x.contact),
				emailAddress: x.sentToEmailAddress,
				hasReplied: x.hasReplied,
				displayName: x.displayName,
			};
		});
	}
}

export class CampaignEmailsByStatusViewModel extends PagedViewModel<
	Api.ICampaignEmail,
	CampaignEmailViewModel,
	Api.ICampaignTransactionsRequest
> {
	public readonly status: Api.EmailTransactionStatus;
	private mLastCampaignUpdate: Api.IBulkEmailUpdate;
	private mId: string;
	private mIsGroup: boolean;

	private mIndex: number;

	public fragment?: string;

	public pageSize = 25;

	public isClicked: boolean;

	public tagFilter?: readonly string[];

	constructor(
		userSession: UserSessionContext,
		campaignId: string,
		status: Api.EmailTransactionStatus,
		isGroup = false,
		isClicked = false
	) {
		super(userSession);

		makeObservable<CampaignEmailsByStatusViewModel, 'mLastCampaignUpdate' | 'mIndex'>(this, {
			status: observable,
			mLastCampaignUpdate: observable.ref,
			mIndex: observable,
			fragment: observable,
			tagFilter: observable.ref,
			setTagFilter: action,
			itemsCount: override,
			selectedItem: computed,
			selectedIndex: computed,
			applyUpdates: action,
			load: override,
			reset: override,
		});

		this.mId = campaignId;
		this.status = status;
		this.mIndex = 0;
		this.mIsGroup = isGroup;
		this.isClicked = isClicked;

		this.mPagedCollectionController = new FilteredPageCollectionController<
			Api.ICampaignEmail,
			CampaignEmailViewModel,
			Api.ICampaignTransactionsRequest
		>({
			apiPath: 'reports/campaign/Transactions',
			client: this.mUserSession.webServiceHelper,
			transformer: this.transformer,
		});
	}

	public setTagFilter(tags?: readonly string[]) {
		this.tagFilter = tags && tags.length > 0 ? tags : undefined;
		this.reset();
	}

	public get itemsCount() {
		if (this.mLastCampaignUpdate) {
			switch (this.status) {
				case Api.EmailTransactionStatus.Bounced:
				case Api.EmailTransactionStatus.Failed: {
					return this.mLastCampaignUpdate.failedCount;
				}
				case Api.EmailTransactionStatus.Sent: {
					return this.mLastCampaignUpdate.sentSuccessfullyCount;
				}
				case Api.EmailTransactionStatus.Opened: {
					return this.mLastCampaignUpdate.openCount;
				}
				case Api.EmailTransactionStatus.Queued: {
					return (
						this.mLastCampaignUpdate.totalEmails -
						this.mLastCampaignUpdate.failedCount -
						this.mLastCampaignUpdate.sentSuccessfullyCount
					);
				}
				case Api.EmailTransactionStatus.Replied: {
					return this.mLastCampaignUpdate.replyCount;
				}
				case Api.EmailTransactionStatus.Unsubscribed: {
					return this.mLastCampaignUpdate.unsubscribedCount || 0;
				}
				default: {
					break;
				}
			}
		}
		return this.mPagedCollectionController.totalCount;
	}

	public get selectedItem() {
		const index = this.selectedIndex;
		return index >= 0 ? this.items.getByIndex(index) : undefined;
	}

	public get selectedIndex() {
		if (!this.isLoaded || this.items.length < 1) {
			return -1;
		}

		if (this.mIndex >= this.items.length) {
			return this.items.length - 1;
		}

		return this.mIndex;
	}

	public set selectedIndex(index: number) {
		this.mIndex = index;
	}

	public get request(): Api.ICampaignTransactionsRequest {
		const request: Api.ICampaignTransactionsRequest = {
			expandGroup: this.mIsGroup,
			fragment: this.fragment,
			id: this.mId,
			status: this.status,
		};
		if (this.isClicked) {
			request.clickedTransactions = true;
			request.status = null;
		}
		if (this.status === Api.EmailTransactionStatus.Unsubscribed) {
			request.unsubscribedTransactions = true;
			request.status = null;
		}
		if (this.tagFilter && this.tagFilter.length > 0) {
			request.filter = {
				criteria: this.tagFilter.map(value => ({
					property: Api.ContactFilterCriteriaProperty.Tag,
					value,
				})),
			};
		}
		return request;
	}

	public applyUpdates = (campaignUpdate?: Api.IBulkEmailUpdate, emailUpdates?: Api.IEmailUpdate[]) => {
		if (campaignUpdate) {
			this.mLastCampaignUpdate = campaignUpdate;
		}
		emailUpdates?.forEach(x => this.mPagedCollectionController.fetchResults.getById(x.id)?.applyUpdate(x));
	};

	public async load(
		params?: Api.IDictionary<any>
	): Promise<Api.IPageCollectionControllerFetchResult<Api.ICampaignEmail[]>> {
		if (this.status === Api.EmailTransactionStatus.Unsubscribed && this.mIsGroup) {
			throw new Error("Can't get unsubscribed transactions for group, only individually");
		}
		return super.load(params);
	}

	private transformer = (model: Api.ICampaignEmail) => {
		return new CampaignEmailViewModel(this.mUserSession, model);
	};

	public reset() {
		super.reset();
		this.mLastCampaignUpdate = null;
	}
}

/** The individual campaign reporting page with tabs including queued, sent, replied */
export class CampaignViewModel extends ViewModel {
	protected mLoadingDefaultMessageContent: boolean;
	protected mModel?: Api.ICampaign;
	protected mDefaultMessageContent: Api.IRawRichTextContentState;
	protected mEmailBodyTemplate: Api.ITemplate;
	protected mLoadingPromise: Promise<Api.ICampaign>;

	constructor(userSession: UserSessionContext, model: Api.ICampaign) {
		super(userSession);

		makeObservable<
			CampaignViewModel,
			'mLoadingDefaultMessageContent' | 'mModel' | 'mDefaultMessageContent' | 'mEmailBodyTemplate' | 'mLoadingPromise'
		>(this, {
			mLoadingDefaultMessageContent: observable,
			mModel: observable.ref,
			mDefaultMessageContent: observable.ref,
			mEmailBodyTemplate: observable.ref,
			mLoadingPromise: observable.ref,
			isBusy: override,
			emailBodyTemplate: computed,
			isLoadingDefaultMessageContent: computed,
			id: computed,
			templateReference: computed,
			defaultMessageContent: computed,
			lastDetailedStatus: computed,
			subject: computed,
			name: computed,
			creator: computed,
			totalCount: computed,
			repliedCount: computed,
			openCount: computed,
			sentCount: computed,
			noEmailAddressCount: computed,
			queuedCount: computed,
			filterRequest: computed,
			failedCount: computed,
			bouncedCount: computed,
			isFinishedSending: computed,
			completedDate: computed,
			cancelDate: computed,
			creationDate: computed,
			distinctClickCount: computed,
			hasClickTargets: computed,
			lastModifiedDate: computed,
			lastCommErrorDate: computed,
			schedule: computed,
			isScheduledToSend: computed,
			expirationDate: computed,
			sendToHousehold: computed,
			canEditEmailContent: computed,
			categories: computed,
			cancelSchedule: action,
			actor: computed,
			approvalRequests: computed,
			status: computed,
			hasStarted: computed,
			groupId: computed,
			endResolutionDate: computed,
			hasResolvedTransactions: computed,
			isInFinalStatus: computed,
			machineOpenObserved: computed,
			load: override,
			setCampaign: action,
		});

		this.mSetModel(model);
	}

	public get isBusy() {
		return this.busy || this.loading || this.mLoadingDefaultMessageContent;
	}

	public get emailBodyTemplate() {
		return this.mEmailBodyTemplate;
	}

	public get isLoadingDefaultMessageContent() {
		return !!this.mLoadingDefaultMessageContent;
	}

	public get id() {
		return this.mModel?.id;
	}

	public get templateReference() {
		return this.mModel?.templateReference;
	}

	public get defaultMessageContent() {
		return this.mDefaultMessageContent;
	}

	public get lastDetailedStatus() {
		return this.mModel?.lastDetailedStatus;
	}

	public get subject() {
		return this.mModel?.subject;
	}

	public get name() {
		return this.mModel?.name;
	}

	public get creator() {
		return this.mModel?.creator;
	}

	public get totalCount() {
		return this.mModel?.totalEmails;
	}

	public get repliedCount() {
		return this.mModel?.replyCount;
	}

	public get openCount() {
		return this.mModel?.openCount;
	}

	public get sentCount() {
		return this.mModel?.sentSuccessfullyCount;
	}

	public get distinctClickCount() {
		return this.mModel?.distinctClickCount || 0;
	}

	get hasClickTargets() {
		return this.mModel?.hasClickTargets;
	}

	public get noEmailAddressCount() {
		return this.mModel?.noEmailAddressCount || 0;
	}

	public get queuedCount() {
		return this.totalCount - this.sentCount - this.failedCount - this.noEmailAddressCount;
	}

	public get filterRequest() {
		return this.mModel?.filterRequest;
	}

	public get failedCount() {
		return this.mModel?.failedCount;
	}

	public get bouncedCount() {
		return this.mModel?.bouncedCount;
	}

	public get isFinishedSending() {
		return !!this.mModel?.completedDate;
	}

	public get completedDate() {
		return this.mModel?.completedDate ? new Date(this.mModel.completedDate) : null;
	}

	public get cancelDate() {
		return this.mModel?.cancelledDate ? new Date(this.mModel.cancelledDate) : null;
	}

	public get creationDate() {
		return this.mModel?.creationDate ? new Date(this.mModel.creationDate) : null;
	}

	public get lastModifiedDate() {
		return this.mModel?.lastModifiedDate ? new Date(this.mModel.lastModifiedDate) : null;
	}

	public get lastCommErrorDate() {
		return this.mModel?.lastCommErrorDate;
	}

	public get schedule() {
		return this.mModel?.schedule;
	}

	public get isScheduledToSend() {
		return !this.mModel?.cancelledDate && !this.mModel?.completedDate && !!this.mModel?.schedule;
	}

	public get expirationDate() {
		return this.mModel?.schedule?.expirationDate;
	}

	public get sendToHousehold() {
		return this.mModel?.sendToHousehold;
	}

	public get canEditEmailContent() {
		const readyOrQueued = this.status === Api.EmailSendStatus.Ready || this.status === Api.EmailSendStatus.Queued;
		return (
			readyOrQueued &&
			this.creator?.id === this.mUserSession?.user?.id &&
			// editing newsletter content from here is disabled for now
			!this.categories?.some(x => x === Api.EmailCategories.HtmlNewsletter)
		);
	}

	public get categories() {
		return this.mModel?.categories;
	}

	public cancelSchedule = () => {
		return this.mUpdateSchedule('DELETE');
	};

	public get actor() {
		return this.mModel?.actor;
	}

	public get approvalRequests() {
		return this.mModel?.approvalRequests;
	}

	public get status() {
		return this.mModel?.status;
	}

	public get hasStarted() {
		if (this.isScheduledToSend) {
			return new Date(this.mModel.schedule.startDate) < new Date();
		}
		return false;
	}

	public get groupId() {
		return this.mModel?.groupId;
	}

	public get endResolutionDate() {
		return this.mModel?.endResolutionDate ? new Date(this.mModel.endResolutionDate) : null;
	}

	public get hasResolvedTransactions() {
		return this.mModel?.hasResolvedTransactions;
	}

	public get isInFinalStatus() {
		return (
			this.status === Api.EmailSendStatus.Complete ||
			this.status === Api.EmailSendStatus.Cancelled ||
			this.status === Api.EmailSendStatus.Rejected
		);
	}

	public get machineOpenObserved() {
		return this.mModel?.machineOpenObserved;
	}

	public toJs() {
		return this.mModel;
	}

	public load(): Promise<Api.ICampaign> {
		if (!this.mLoadingPromise) {
			this.loaded = false;
			this.busy = true;
			this.loading = true;

			this.mLoadingPromise = new Promise((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.ICampaign>) => {
					runInAction(() => {
						this.mLoadingPromise = null;
						this.loading = false;
						this.busy = false;
						if (opResult.success) {
							this.mSetModel(opResult.value);
							this.loaded = true;
							resolve(opResult.value);
						} else {
							this.loaded = false;
							reject(opResult);
						}
					});
				};

				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICampaign>(
					this.composeApiUrl({ urlPath: `email/${this.mModel.id}` }),
					'GET', // Method
					null, // Body (null if it's a GET operation)
					onFinish,
					onFinish
				);
			});
		}

		return this.mLoadingPromise;
	}

	public setCampaign = (campaign: Api.ICampaign) => {
		this.mSetModel(campaign);
	};

	public loadEmailBodyTemplate = async () => {
		if (!this.isBusy && this.mModel?.templateReference?.templateId) {
			this.busy = true;
			try {
				const opResult = await this.mLoadEmailBodyTemplate();
				this.busy = false;
				return opResult;
			} catch (err) {
				this.busy = false;
				throw Api.asApiError(err);
			}
		}
	};

	public loadDefaultMessageContent = (forceReload?: boolean) => {
		if (this.mDefaultMessageContent && !forceReload) {
			return Promise.resolve<Api.IOperationResult<Api.IRawRichTextContentState>>({
				success: true,
				systemCode: 200,
				value: this.mDefaultMessageContent,
			});
		}

		if (!this.isBusy) {
			this.mLoadingDefaultMessageContent = true;
			const promise = new Promise<Api.IOperationResult<Api.IRawRichTextContentState>>((resolve, reject) => {
				this.userSession.webServiceHelper
					.callWebServiceAsync<Api.IRawRichTextContentState>(
						this.composeApiUrl({ urlPath: `reports/campaign/${this.mModel.id}/content` }),
						'GET'
					)
					.then(opResult => {
						runInAction(() => {
							this.mLoadingDefaultMessageContent = false;
							this.mDefaultMessageContent = opResult.value;
							resolve(opResult);
						});
					})
					.catch(e => {
						this.mLoadingDefaultMessageContent = false;
						const err = Api.asApiError(e);
						reject(err);
					});
			});
			return promise;
		}
	};

	private mLoadEmailBodyTemplate = async (): Promise<Api.IOperationResult<Api.ITemplate>> => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplate>(
			this.composeApiUrl({ urlPath: `template/${this.mModel?.templateReference?.templateId}` }),
			'GET'
		);

		if (!opResult.success) {
			throw opResult;
		}

		this.mEmailBodyTemplate = opResult.value;
		return opResult;
	};

	private mUpdateSchedule = (method: 'PUT' | 'DELETE', schedule?: Api.IScheduledSend, timeZone?: string) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IOperationResult<Api.ICampaign>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.ICampaign>) => {
					if (opResult.success) {
						this.busy = false;
						this.mSetModel(opResult.value);
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICampaign>(
					this.composeApiUrl({
						queryParams: {
							timeZone,
						},
						urlPath: `email/${this.mModel.id}/scheduledsend`,
					}),
					method,
					method === 'PUT' ? schedule : null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	private mSetModel = (model: Api.ICampaign) => {
		this.mModel = model;
	};
}

export class EmailWorkloadViewModel extends Api.ImpersonationBroker {
	protected mUserSession: UserSessionContext;

	constructor(userSession: UserSessionContext) {
		super();
		this.mUserSession = userSession;
	}

	public getScheduledSendEstimate = (schedule: Api.IScheduledSend, numberOfEmails: number, timeZone?: string) => {
		return new Promise<Api.IOperationResult<Api.IEmailSendEstimate>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IEmailSendEstimate>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEmailSendEstimate>(
				this.composeApiUrl({
					queryParams: {
						numberOfEmails,
						timeZone,
					},
					urlPath: 'email/scheduledsend/estimate',
				}),
				'POST',
				schedule,
				onFinish,
				onFinish
			);
		});
	};

	/**
	 * Estimates the send window for a saved campaign.
	 *
	 * Prefer this over getScheduledSendEstimate: because the backend loads the campaign, the estimator can leave it
	 * out of its own forecast, respect its send-interval blackouts and use its effective send limits. Estimating
	 * from a bare schedule lets an in-flight campaign's whole contact count land in the forecast it is measured
	 * against.
	 *
	 * @param numberOfEmails Omit for the whole campaign; pass the remaining queued count to ask when the rest of
	 * the queue drains.
	 */
	public getCampaignSendEstimate = (campaignId: string, numberOfEmails?: number, timeZone?: string) => {
		return new Promise<Api.IOperationResult<Api.IEmailSendEstimate>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IEmailSendEstimate>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEmailSendEstimate>(
				this.composeApiUrl({
					queryParams: {
						numberOfEmails,
						timeZone,
					},
					urlPath: `email/${encodeURIComponent(campaignId)}/scheduledsend/estimate`,
				}),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public getBulkScheduledSendEstimate = (
		email: Api.IEmailMessageCompose<Api.IFollowUpOptions>,
		numberOfEmails: number,
		timeZone?: string
	) => {
		return new Promise<Api.IOperationResult<Api.IEmailSendEstimate>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IEmailSendEstimate>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEmailSendEstimate>(
				this.composeApiUrl({
					queryParams: {
						numberOfEmails,
						timeZone,
						forUserId: email.options?.sendEmailFromUserId,
					},
					urlPath: 'email/scheduledsend/bulkEmailEstimate',
				}),
				'POST',
				email,
				onFinish,
				onFinish
			);
		});
	};

	public getWorkload = (timeZone?: string) => {
		return new Promise<Api.IOperationResult<Api.IEmailWorkload>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IEmailWorkload>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEmailWorkload>(
				this.composeApiUrl({ queryParams: { timeZone }, urlPath: 'email/user/workload' }),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};
}

export class ImportContactsViewModel<TFile extends Blob = Blob> extends ViewModel {
	protected mFile: TFile;
	private mOptions: ImportContactsOptionsViewModel;
	private mPreview: Api.IImportContactPreview;
	private mSystemJob: SystemJobViewModel;
	private mAccountId?: string;
	private mUserId?: string;

	constructor(userSession: UserSessionContext, accountId?: string, userId?: string) {
		super(userSession);

		makeObservable<ImportContactsViewModel, 'mFile' | 'mOptions' | 'mPreview' | 'mSystemJob'>(this, {
			mFile: observable.ref,
			mOptions: observable.ref,
			mPreview: observable.ref,
			mSystemJob: observable.ref,
			options: computed,
			preview: computed,
			systemJob: computed,
			file: computed,
		});

		this.importFromFile = this.importFromFile.bind(this);
		this.mOptions = new ImportContactsOptionsViewModel(this.mUserSession);

		this.mAccountId = accountId;
		this.mUserId = userId;
	}

	public get options() {
		return this.mOptions;
	}

	public get preview() {
		return this.mPreview;
	}

	public get systemJob() {
		return this.mSystemJob;
	}

	public get file() {
		return this.mFile;
	}

	public importFromFile(
		file: TFile,
		contentType?: Api.ImportContactsContentType,
		deleteRenewalKeyFacts?: boolean
	): Promise<Api.IOperationResult<Api.IImportContactPreview>> {
		if (this.isBusy) {
			return Promise.resolve(undefined);
		}

		this.busy = true;
		if (contentType) {
			this.mOptions.setContentType(contentType);
		}
		if (deleteRenewalKeyFacts) {
			this.mOptions.setDeleteRenewalKeyFacts(deleteRenewalKeyFacts);
		}
		const promise = new Promise<Api.IOperationResult<Api.IImportContactPreview>>((resolve, reject) => {
			const data = new FormData();
			data.append('file', file);

			const onFinish = action((opResult: Api.IOperationResult<Api.IImportContactPreview>) => {
				this.busy = false;
				if (opResult.success) {
					this.mFile = file;
					if (this.mSetPreview(opResult.value)) {
						resolve(opResult);
					} else {
						reject({
							success: false,
							systemCode: 500,
							systemMessage:
								'Please make sure the spreadsheet you uploaded has a header row. If it does, please make sure at least one header is something we can recognize, like "Name" or "Email".',
						});
					}
				} else {
					reject(opResult);
				}
			});

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IImportContactPreview>(
				this.getUrl(),
				'POST',
				data,
				onFinish,
				onFinish
			);
		});

		return promise;
	}

	public executeImport = (mergeByName = true) => {
		if (!this.isBusy && this.mPreview && this.mPreview.id) {
			this.mOptions.setAllowMatchingByName(mergeByName);
			this.busy = true;
			const promise = new Promise<Api.IOperationResult<Api.IImportContactsJob>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IImportContactsJob>) => {
					this.busy = false;
					if (opResult.success) {
						// copy over the options from the system job...
						// polling will wipe these values out if we don't
						this.mOptions = new ImportContactsOptionsViewModel(
							this.mUserSession,
							opResult.value.options || this.mOptions.toJs()
						);
						this.mSetSystemJob(opResult.value);
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IImportContactsJob>(
					this.getUrl(this.mPreview.id),
					'PUT',
					this.mOptions.toJs(),
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	protected getUrl(jobId?: string) {
		const url = this.mAccountId
			? `admin/contactOperations/${this.mAccountId}/${this.mUserId}/importFromFile`
			: 'contact/importFromFile';
		return !jobId ? url : `${url}/${jobId}`;
	}

	protected mSetPreview = (preview: Api.IImportContactPreview) => {
		this.mPreview = preview;
		this.loaded = true;

		// create suggested mapping
		const headerNameToField: Api.IDictionary<Api.IImportContactFieldConfig> = {};
		(this.mPreview.header || []).forEach(headerName => {
			let cleanedHeaderName = headerName || '';
			if (headerName.includes('[[Tag]]')) {
				cleanedHeaderName = 'Tags';
			}
			const lowerHeaderName = cleanedHeaderName.toLocaleLowerCase().trim();
			if (lowerHeaderName) {
				const matchingField = (this.mPreview.fields || []).find(field => {
					const matchParams = new Set([
						(field.propertyName || '').toLocaleLowerCase(),
						(field.name || '').toLocaleLowerCase(),
						...(field.aliases || []).map(a => a.toLocaleLowerCase()),
					]);
					return matchParams.has(lowerHeaderName);
				});
				if (matchingField) {
					headerNameToField[headerName] = matchingField;
				}
			}
		});
		this.mOptions.setHeaderToFieldMapping(headerNameToField);
		return Object.keys(headerNameToField).length > 0;
	};

	private mSetSystemJob = (systemJob: Api.IImportContactsJob) => {
		this.mSystemJob = new SystemJobViewModel(this.mUserSession, systemJob);
	};
}

export class ImportContactsOptionsViewModel implements Api.IImportContactsOptions {
	private mOptions: Api.IImportContactsOptions;
	private mHeaderNameToFieldMap: Api.IDictionary<Api.IImportContactFieldConfig>;
	private mUserSession: UserSessionContext;

	constructor(userSession: UserSessionContext, options?: Api.IImportContactsOptions) {
		makeObservable<ImportContactsOptionsViewModel, 'mOptions' | 'mHeaderNameToFieldMap'>(this, {
			mOptions: observable,
			mHeaderNameToFieldMap: observable.ref,
			tags: computed,
			setTags: action,
			setContentType: action,
			contentType: computed,
			headerNameToFieldMap: computed,
			setHeaderToFieldMapping: action,
			fieldMapping: computed,
			setUseEmailAddressAsAlternativeKey: action,
			useEmailAddressAsAlternativeKey: computed,
			setVisibility: action,
			visibility: computed,
			setPreserveExistingFieldValues: action,
			preserveExistingFieldValues: computed,
			setAllowMatchingByName: action,
			deleteRenewalKeyFacts: computed,
			setDeleteRenewalKeyFacts: action,
			allowMatchingByName: computed,
			setExcludeBaseTag: action,
			excludeBaseTag: computed,
			reset: action,
		});

		this.mUserSession = userSession;
		this.mReset(options);
	}

	public get tags() {
		return this.mOptions.tags || [];
	}

	public setTags = (tags: string[]) => {
		this.mOptions.tags = tags;
	};

	public setContentType(contentType: Api.ImportContactsContentType) {
		this.mOptions.contentType = contentType;
	}

	public get contentType() {
		return this.mOptions.contentType;
	}

	public get headerNameToFieldMap() {
		return this.mHeaderNameToFieldMap;
	}

	public setHeaderToFieldMapping(headerNameToFieldMap: Api.IDictionary<Api.IImportContactFieldConfig>) {
		this.mHeaderNameToFieldMap = headerNameToFieldMap || {};
		this.mOptions.fieldMapping = Object.keys(this.mHeaderNameToFieldMap).reduce<Api.IDictionary<string>>(
			(result, headerName) => {
				if (this.mHeaderNameToFieldMap[headerName]) {
					result[headerName] = this.mHeaderNameToFieldMap[headerName].propertyName;
				}
				return result;
			},
			{}
		);
	}

	public get fieldMapping() {
		return this.mOptions.fieldMapping;
	}

	public setUseEmailAddressAsAlternativeKey(useEmailAddressAsAlternativeKey: boolean) {
		this.mOptions.useEmailAddressAsAlternativeKey = useEmailAddressAsAlternativeKey;
	}

	public get useEmailAddressAsAlternativeKey() {
		return this.mOptions.useEmailAddressAsAlternativeKey;
	}

	public setVisibility(visibility: string) {
		this.mOptions.visibility = visibility;
	}

	public get visibility() {
		return this.mOptions.visibility;
	}

	public setPreserveExistingFieldValues(values: Api.IPreservableFieldValues[]) {
		this.mOptions.preserveExistingFieldValues = values;
	}

	public get preserveExistingFieldValues() {
		return this.mOptions.preserveExistingFieldValues;
	}

	public setAllowMatchingByName(allowMatchingByName: boolean) {
		this.mOptions.allowMatchingByName = allowMatchingByName;
	}

	public get deleteRenewalKeyFacts() {
		return this.mOptions.deleteRenewalKeyFacts;
	}
	public setDeleteRenewalKeyFacts(deleteRenewalKeyFacts: boolean) {
		this.mOptions.deleteRenewalKeyFacts = deleteRenewalKeyFacts;
	}

	public get allowMatchingByName() {
		return this.mOptions.allowMatchingByName;
	}

	public setExcludeBaseTag(value: boolean) {
		this.mOptions.excludeBaseTag = value;
	}

	public get excludeBaseTag() {
		return this.mOptions.excludeBaseTag;
	}

	public reset = () => {
		this.mReset();
	};

	public toJs = () => {
		return this.mOptions;
	};

	private mReset = (options?: Api.IImportContactsOptions) => {
		this.mOptions = {
			allowMatchingByName: true,
			contentType: Api.ImportContactsContentType.csv,
			excludeBaseTag: false,
			fieldMapping: {},
			preserveExistingFieldValues: [],
			tags: [],
			useEmailAddressAsAlternativeKey: false,
			visibility: VmUtils.getDefaultVisibility(this.mUserSession.user),
			...(options || {}),
		};
		this.mHeaderNameToFieldMap = {};
	};
}

export class TagOwnerReportSystemJobViewModel extends SystemJobViewModel<Api.ISystemJob> {
	private mReportPdfDownloadUrl: string;
	private accountId?: string;
	private baseRoute: string;
	private query: string;

	public static createWithOwnerReportId = (userSession: UserSessionContext, ownerReportId: string) => {
		return new TagOwnerReportSystemJobViewModel(userSession, {
			additionalFields: {
				OwnerReportId: ownerReportId,
			},
		});
	};

	/**
	 * @param {string} accountId - Optional parameter used for Levitate system admins to run report for a specific
	 *   account.
	 */
	constructor(userSession: UserSessionContext, systemJob?: Api.ISystemJob, accountId?: string) {
		super(userSession, systemJob);

		makeObservable<TagOwnerReportSystemJobViewModel, 'mReportPdfDownloadUrl'>(this, {
			mReportPdfDownloadUrl: observable,
			ownerReportId: computed,
			isLoaded: override,
			ownerReportPdfDownloadUrl: computed,
			runReport: action,
			getReportPdfDownloadUrl: action,
		});

		this.accountId = accountId;
		this.baseRoute = this.accountId ? `admin/tagAlertsOperations/report` : `reports/tagOwner`;
		this.query = this.accountId ? `?accountId=${accountId}` : ``;
	}

	public get ownerReportId() {
		return this.mSystemJob ? (this.mSystemJob.additionalFields || {})?.OwnerReportId : null;
	}

	public get isLoaded() {
		return (this.mSystemJob && this.mSystemJob.id && !!this.ownerReportId) || this.loaded;
	}

	public get ownerReportPdfDownloadUrl() {
		return this.mReportPdfDownloadUrl;
	}

	public runReport = () => {
		if (!this.mSystemJob && !this.isBusy) {
			this.loading = true;
			return new Promise<Api.IOperationResult<Api.ISystemJob>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.ISystemJob>) => {
					this.loading = false;
					if (opResult.success) {
						this.setSystemJob(opResult.value);
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});

				const path = `${this.baseRoute}/run${this.query}`;

				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
					path,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}
	};

	public getReportPdfDownloadUrl = () => {
		if (this.ownerReportId && !this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<string>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<string>) => {
					this.busy = false;
					if (opResult.success) {
						this.mReportPdfDownloadUrl = opResult.value;
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});

				const path = `${this.baseRoute}/${this.ownerReportId}/pdf${this.query}`;
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<string>(
					path,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
	};
}

export class UnclassifiedContactsViewModel extends ViewModel {
	private mPageCollectionController: FilteredPageCollectionController<Api.IClassifyContact, ClassifyContactViewModel>;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			isBusy: override,
			totalCount: computed,
			fetchUnclassifiedContacts: action,
			unclassifiedContacts: computed,
			resetUnclassifiedContacts: action,
		});

		this.mPageCollectionController = new FilteredPageCollectionController<
			Api.IClassifyContact,
			ClassifyContactViewModel
		>({
			apiPath: 'contact/classification/suggestions/filter',
			client: this.userSession.webServiceHelper,
			transformer: this.createClassifyContactViewModel,
		});
	}

	public get isBusy() {
		return this.busy || this.loading || this.mPageCollectionController.isFetching;
	}

	public get totalCount() {
		return this.mPageCollectionController.totalCount;
	}

	public fetchUnclassifiedContacts(
		excludedIds: { excludeContactIds: string[] },
		pageSize?: number,
		params?: Api.IDictionary
	) {
		return this.mPageCollectionController.getNext(excludedIds, pageSize, params);
	}

	public get unclassifiedContacts() {
		return this.mPageCollectionController.fetchResults;
	}

	public resetUnclassifiedContacts = () => {
		this.mPageCollectionController.reset();
	};

	private createClassifyContactViewModel = (item: Api.IClassifyContact) => {
		return new ClassifyContactViewModel(this.mUserSession, item);
	};
}

export class EmailScannersViewModel extends ViewModel {
	protected mAccount: Api.IAccount;
	protected mEmailScanners: Api.IEmailScanner[];
	protected mCustomLeadParsers: Api.ICustomLeadParser[];

	constructor(userSession: UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<EmailScannersViewModel, 'mAccount' | 'mEmailScanners' | 'mCustomLeadParsers'>(this, {
			mAccount: observable.ref,
			mEmailScanners: observable.ref,
			mCustomLeadParsers: observable.ref,
			emailScanners: computed,
			customLeadParsers: computed,
			loadStats: action,
		});

		this.mAccount = account;
		this.mEmailScanners = this.getEmailScanners();
		this.mCustomLeadParsers = this.getCustomLeadParsers();
	}

	public get emailScanners() {
		return this.mEmailScanners;
	}

	public get customLeadParsers() {
		return this.mCustomLeadParsers;
	}

	protected baseApiRoute() {
		return 'account/features/emailscan';
	}

	protected callAsync = async (path: string, method: Api.HTTPMethod, body?: any) => {
		this.busy = true;
		try {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IEmailScanFeatures>(
				this.composeApiUrl({ urlPath: `${this.baseApiRoute()}${path}` }),
				method,
				body
			);
			runInAction(() => {
				this.mAccount.features.emailScan = {
					...this.mAccount.features.emailScan,
					...value,
				};
				this.mEmailScanners = this.getEmailScanners();
				this.mCustomLeadParsers = this.getCustomLeadParsers();
			});
		} finally {
			runInAction(() => {
				this.busy = false;
			});
		}
	};

	public createCustomLeadParser = (parserData: Api.ICustomLeadParser) =>
		this.callAsync('/customLeadParser', 'POST', parserData);
	public deleteCustomLeadParser = (id: string) => this.callAsync(`/customLeadParser/${id}`, 'DELETE');
	public enableCustomLeadParser = (id: string) => this.callAsync(`/customLeadParser/${id}/toggleEnable`, 'PATCH');
	public enableEmailScanner = (id: string) => this.callAsync(`/${id}/toggleEnable`, 'PATCH');
	public updateCustomLeadParser = (parserData: Api.ICustomLeadParser) =>
		this.callAsync(`/customLeadParser/${parserData.id}`, 'PUT', parserData);

	protected getCustomLeadParsers() {
		return this.mAccount?.features?.emailScan?.customLeadParsers || [];
	}

	protected getEmailScanners() {
		const scanners = this.getDefaultScannersByIndustry().concat(this.getEnabledScanners());
		return Array.from(new Set(scanners)).map(s => ({
			...s,
			isDisabled: !VmUtils.isIntegrationEnabledForAccount(s.id, this.mAccount),
		}));
	}

	protected getEnabledScanners() {
		const enabledScanners =
			this.mAccount.features?.emailScan?.enabledScanners?.filter(
				x => !Api.EmailScannersBlockedFromConfiguration.has(x)
			) ?? [];

		if (enabledScanners.length === 0) {
			return [];
		}

		return Api.AllEmailScanners.filter(
			scanner => enabledScanners.findIndex(enabledScanner => enabledScanner === scanner.id) >= 0
		);
	}

	protected getDefaultScannersByIndustry() {
		switch (this.mAccount.additionalInfo?.industry) {
			case Api.Industry.Accounting:
				return Api.AllEmailScanners.filter(x => {
					return x.id === Api.EmailScannerId.EndorsedLocalProviders;
				});
			case Api.Industry.RealEstate:
			case Api.Industry.Mortgage:
				return Api.AllEmailScanners.filter(x => {
					return (
						x.id !== Api.EmailScannerId.EndorsedLocalProviders &&
						x.id !== Api.EmailScannerId.TrustedChoice &&
						x.id !== Api.EmailScannerId.WizardCalls &&
						x.id !== Api.EmailScannerId.Everquote &&
						x.id !== Api.EmailScannerId.Datalot &&
						x.id !== Api.EmailScannerId.SmartVestor
					);
				});
			case Api.Industry.Insurance:
				return Api.AllEmailScanners.filter(x => {
					return (
						x.id === Api.EmailScannerId.EndorsedLocalProviders ||
						x.id === Api.EmailScannerId.TrustedChoice ||
						x.id === Api.EmailScannerId.WizardCalls ||
						x.id === Api.EmailScannerId.Everquote ||
						x.id === Api.EmailScannerId.Datalot ||
						x.id === Api.EmailScannerId.Forge3
					);
				});
			case Api.Industry.Financial:
				return Api.AllEmailScanners.filter(
					x => x.id === Api.EmailScannerId.SmartVestor || x.id === Api.EmailScannerId.EndorsedLocalProviders
				);
			case Api.Industry.Legal:
				return Api.AllEmailScanners.filter(x => x.id === Api.EmailScannerId.NOLO);
			default:
				return [];
		}
	}

	public loadStats = async () => {
		this.busy = true;
		try {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IEmailScannerStats[]>(
				this.composeApiUrl({ urlPath: `${this.baseApiRoute()}/stats` }),
				'GET'
			);
			runInAction(() => {
				try {
					this.mEmailScanners = this.mEmailScanners.map(scanner => {
						const stats = value.find(stat => {
							return stat.emailScanner === scanner.id;
						});
						return stats ? { ...scanner, stats } : scanner;
					});

					this.mCustomLeadParsers = this.mCustomLeadParsers.map(parser => {
						const stats = value.find(stat => stat.id === parser.id);
						return stats ? { ...parser, stats } : parser;
					});
				} catch (error) {
					// FOLLOWUP: Resolve

					// @ts-ignore
					throw Api.asApiError(new Error(`Failed to map stats: ${error.message}`));
				}
			});
		} finally {
			this.busy = false;
		}
	};
}
export class AccountIntegrationsViewModel extends ViewModel {
	protected mAccount: Api.IAccount;
	protected mAms360Vm: ConfigurableIntegrationViewModel;
	protected mHawksoftVm: ConfigurableIntegrationViewModel;
	protected mRedtailVm: ConfigurableIntegrationViewModel;
	protected mEclipseVm: ConfigurableIntegrationViewModel;
	protected mClioVm: ConfigurableIntegrationViewModel;
	protected mEzLynxVm: ConfigurableIntegrationViewModel;
	protected mXanatekVm: ConfigurableIntegrationViewModel;
	protected mEpicVm: ConfigurableIntegrationViewModel;
	protected mQQVm: ConfigurableIntegrationViewModel;
	protected mHubspotVm: ConfigurableIntegrationViewModel;
	protected mDonorPerfectVm: ConfigurableIntegrationViewModel;
	public readonly allIntegrations: ConfigurableIntegrationViewModel[];

	constructor(userSession: UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<
			AccountIntegrationsViewModel,
			| 'mAccount'
			| 'mAms360Vm'
			| 'mHawksoftVm'
			| 'mRedtailVm'
			| 'mEclipseVm'
			| 'mClioVm'
			| 'mEzLynxVm'
			| 'mXanatekVm'
			| 'mEpicVm'
			| 'mQQVm'
			| 'mHubspotVm'
			| 'mDonorPerfectVm'
		>(this, {
			mAccount: observable.ref,
			mAms360Vm: observable.ref,
			mHawksoftVm: observable.ref,
			mRedtailVm: observable.ref,
			mEclipseVm: observable.ref,
			mClioVm: observable.ref,
			mEzLynxVm: observable.ref,
			mXanatekVm: observable.ref,
			mEpicVm: observable.ref,
			mQQVm: observable.ref,
			mHubspotVm: observable.ref,
			mDonorPerfectVm: observable.ref,
			ams360: computed,
			hawksoft: computed,
			eclipse: computed,
			redtail: computed,
			clio: computed,
			ezlynx: computed,
			xanatek: computed,
			epic: computed,
			qq: computed,
			hubspot: computed,
			donorPerfect: computed,
			account: computed,
		});

		this.mAccount = account;
		/** Note: if adding a new one, don't forget to add it to the readonly this.allIntegrations collection! */
		this.mAms360Vm = new ConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.Ams360);
		this.mHawksoftVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.HawkSoft
		);
		this.mRedtailVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Redtail
		);
		this.mEclipseVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Eclipse
		);
		this.mClioVm = new ConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.Clio);
		this.mEzLynxVm = new ConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.EzLynx);
		this.mXanatekVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.GeneralCsv
		);
		this.mEpicVm = new ConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.Epic);
		this.mQQVm = new ConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.QQ);
		this.mHubspotVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.HubSpot
		);

		this.mDonorPerfectVm = new ConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.DonorPerfect
		);
		this.allIntegrations = [
			this.mAms360Vm,
			this.mHawksoftVm,
			this.mRedtailVm,
			this.mEclipseVm,
			this.mClioVm,
			this.mEzLynxVm,
			this.mXanatekVm,
			this.mEpicVm,
			this.mQQVm,
			this.mHubspotVm,
			this.mDonorPerfectVm,
		];
	}

	public getByType(type: Api.ConfigurableIntegrationType) {
		return this.allIntegrations.find(x => x.type === type);
	}

	public get ams360() {
		return this.mAms360Vm;
	}

	public get hawksoft() {
		return this.mHawksoftVm;
	}

	public get eclipse() {
		return this.mEclipseVm;
	}

	public get redtail() {
		return this.mRedtailVm;
	}

	public get clio() {
		return this.mClioVm;
	}

	public get ezlynx() {
		return this.mEzLynxVm;
	}

	public get xanatek() {
		return this.mXanatekVm;
	}

	public get epic() {
		return this.mEpicVm;
	}

	public get qq() {
		return this.mQQVm;
	}

	public get hubspot() {
		return this.mHubspotVm;
	}

	public get donorPerfect() {
		return this.mDonorPerfectVm;
	}

	public get account() {
		return this.mAccount;
	}

	protected getLoadRoute() {
		return 'account/integrations';
	}

	public async load() {
		if (!this.isBusy) {
			this.loading = true;
			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccountIntegrations>(
					this.getLoadRoute(),
					'GET'
				);
				runInAction(() => {
					if (value?.ams360) {
						this.ams360.setIntegration(value.ams360);
					}
					if (value?.hawkSoft) {
						this.hawksoft.setIntegration(value.hawkSoft);
					}
					if (value?.redtail) {
						this.redtail.setIntegration(value.redtail);
					}
					if (value?.eclipse) {
						this.eclipse.setIntegration(value.eclipse);
					}
					if (value?.clio) {
						this.clio.setIntegration(value.clio);
					}
					if (value?.ezLynxCsv) {
						this.ezlynx.setIntegration(value.ezLynxCsv);
					}
					if (value?.epicCsv) {
						this.epic.setIntegration(value.epicCsv);
					}
					if (value?.qqCatalyst) {
						this.mQQVm.setIntegration(value.qqCatalyst);
					}
					if (value?.hubSpot) {
						this.mHubspotVm.setIntegration(value.hubSpot);
					}
					if (value?.generalCsv) {
						if (value?.generalCsv?.generalCsvProvider === Api.GeneralCsvProvider.Xanatek) {
							this.mXanatekVm.setIntegration(value.generalCsv);
						}
					}
					if (value?.donorPerfect) {
						this.mDonorPerfectVm.setIntegration(value.donorPerfect);
					}
					return value;
				});
			} finally {
				runInAction(() => {
					this.busy = false;
				});
			}
		}
	}
}

export class ConfigurableIntegrationViewModel extends ViewModel {
	protected levitateUser: UserViewModel;
	protected mIntegration: Api.IConfigurableIntegration;
	protected mLoadingPromise: Promise<[Api.IOperationResult<Api.IAccount>, Api.IConfigurableIntegration]>;
	protected mAccount: Api.IAccount;
	/*
	 * This is only used for FE decisions. Any API interactions should rely on apiType
	 */
	public readonly type: Api.ConfigurableIntegrationType;
	protected readonly apiType: Api.ConfigurableIntegrationType;
	protected mUsers: ObservableCollection<ConfigurableIntegrationUserViewModel>;
	public availableSegmentingTags: string[];

	constructor(
		userSession: UserSessionContext,
		account: Api.IAccount,
		type: Api.ConfigurableIntegrationType,
		integration?: Api.IConfigurableIntegration
	) {
		super(userSession);

		makeObservable<ConfigurableIntegrationViewModel, 'levitateUser' | 'mIntegration' | 'mLoadingPromise' | 'mAccount'>(
			this,
			{
				levitateUser: observable.ref,
				mIntegration: observable.ref,
				mLoadingPromise: observable.ref,
				mAccount: observable.ref,
				availableSegmentingTags: observable.ref,
				account: computed,
				isLoaded: override,
				users: computed,
				enabled: computed,
				configured: computed,
				invalidOwnerUsers: computed,
				reset: action,
				availableTags: computed,
				getTags: action,
				setIntegration: action,
			}
		);

		this.mAccount = account;
		this.mUsers = new ObservableCollection([], 'id');
		this.type = type;
		this.apiType = Api.resolveIntegrationProviderToApiType(type);

		if (integration) {
			this.setIntegration(integration);
		}
	}

	public get account() {
		return this.mAccount;
	}

	public get isLoaded() {
		return this.mAccount?.id && this.mAccount?.companyName && !!this.mIntegration.agencyId;
	}

	public get users() {
		return this.mUsers;
	}

	public get enabled() {
		return this.mIntegration?.enabled;
	}

	public get configured() {
		return this.mIntegration?.configured;
	}

	public get invalidOwnerUsers() {
		const users = this.mUsers.filter(x => {
			return this.mIntegration.invalidOwnerships.map(y => y.integrationOwnerId).includes(x.id);
		});
		return VmUtils.dedupeById(users);
	}

	public reset = () => {
		this.mUsers = new ObservableCollection([], 'id');
	};

	public toJs = () => {
		return this.mIntegration;
	};
	public get availableTags(): ObservableCollection<Api.IConfigurableIntegration> {
		return this.availableTags;
	}

	public async getTags() {
		if (!this.isBusy) {
			this.loading = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<string[]>(
				this.getAvailableIntegrationTags(),
				'GET'
			);
			this.loading = false;
			if (!opResult.success) {
				throw opResult;
			}
			this.availableSegmentingTags = opResult.value;
			return opResult;
		}
		return null;
	}

	public getUserMap = () => {
		return this.mUsers.map<Api.IIntegrationUserMapping>(x => {
			return {
				integrationId: x.id,
				levitateUser: x.levitateUser?.id ? { id: x.levitateUser.id } : null,
			};
		});
	};

	protected getConfigureRoute(): string {
		return `account/integrations/configure/${this.apiType}`;
	}

	public async configure(body: Partial<Api.IConfigurableIntegration>) {
		if (!this.isBusy) {
			this.loading = true;
			let generalBody: Api.IGeneralCsvIntegration = null;
			if (Api.isGeneralCsvIntegration(this.apiType)) {
				generalBody = {
					generalCsvProvider: this.type as unknown as Api.GeneralCsvProvider, // same underlying value
					...body,
				};
			}
			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<Api.IConfigurableIntegration>(
					this.getConfigureRoute(),
					'POST',
					generalBody ?? body
				);
				runInAction(() => {
					this.setIntegration(value);
				});
				return value;
			} finally {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	protected getLoadAccountRoute() {
		return `account/${this.account.id}`;
	}

	protected getLoadIntegrationRoute() {
		return `account/integrations/${this.apiType}`;
	}

	public async load() {
		if (!this.isBusy && this.account.id) {
			this.loading = true;
			const accountPromise = this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				this.getLoadAccountRoute(),
				'GET'
			);

			const integrationPromise = this.mUserSession.webServiceHelper.callAsync<Api.IConfigurableIntegration>(
				this.getLoadIntegrationRoute(),
				'GET'
			);

			try {
				const values = await Promise.all([accountPromise, integrationPromise]);
				runInAction(() => {
					this.loading = false;
					this.mLoadingPromise = null;

					this.mAccount = values[0];
					this.setIntegration(values[1]);
				});
				return values;
			} finally {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	public setIntegration(integration: Api.IConfigurableIntegration) {
		this.mUsers.clear();

		if (integration) {
			this.mUsers.addAll(
				(integration.userMap || []).map(x => {
					return new ConfigurableIntegrationUserViewModel(this.mUserSession, this.mAccount, x);
				})
			);
		}
		this.mIntegration = integration;
	}

	protected getSetUserMapRoute() {
		return `account/integrations/${this.apiType}/userMap`;
	}

	public setUserMap = async (userMap: Api.IIntegrationUserMapping[] = this.getUserMap()) => {
		if (!this.isBusy) {
			this.busy = true;
			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<Api.IIntegrationUserMapping[]>(
					this.getSetUserMapRoute(),
					'PUT',
					userMap
				);
				runInAction(() => {
					this.mIntegration = {
						...this.mIntegration,
						userMap,
					};
					return value;
				});
			} finally {
				runInAction(() => {
					this.busy = false;
				});
			}
		}
	};

	protected getAvailableIntegrationTags() {
		return `impersonate/${this.account.id}/account/integrations/availableTags`;
	}
}

export class ConfigurableIntegrationUserViewModel<T extends UserViewModel = UserViewModel> extends ViewModel {
	protected mIntegrationUser: Api.IIntegrationUserMapping;
	public levitateUser: T;
	public readonly account: Api.IAccount;
	constructor(userSession: UserSessionContext, account: Api.IAccount, integrationUser: Api.IIntegrationUserMapping) {
		super(userSession);

		makeObservable<ConfigurableIntegrationUserViewModel, 'mIntegrationUser'>(this, {
			mIntegrationUser: observable.ref,
			levitateUser: observable.ref,
			account: observable.ref,
			id: computed,
			status: computed,
			statusDescription: computed,
		});

		this.account = account;
		this.mSetUser(integrationUser);
	}

	public get id() {
		return this.mIntegrationUser?.integrationId;
	}

	public get status(): Api.ActivationStatus {
		return this.levitateUser?.activationStatus;
	}

	public get statusDescription(): 'Connected' | 'Not Connected' | 'Invited' | 'Deactivated' {
		switch (this.status) {
			case Api.ActivationStatus.ACTIVE: {
				return 'Connected';
			}
			case Api.ActivationStatus.CANCELED:
			case Api.ActivationStatus.DEACTIVATED: {
				return 'Deactivated';
			}
			case Api.ActivationStatus.INIT: {
				return 'Invited';
			}
			default: {
				break;
			}
		}

		return 'Not Connected';
	}

	protected mSetUser(integrationUser: Api.IIntegrationUserMapping) {
		this.mIntegrationUser = integrationUser;
		this.levitateUser =
			integrationUser.levitateUser && integrationUser.levitateUser.id
				? (new UserViewModel(this.mUserSession, integrationUser.levitateUser) as T)
				: undefined;
	}
}

export class ContentCalenderSuggestionViewModel extends ViewModel {
	protected mSuggestion: Api.IContentCalendarSuggestion;
	protected mTemplate: Api.ITemplate;
	protected rejected: boolean;
	protected mMatchingAccountTags: Api.IAccountTag[];
	public userSelectedNewTags: boolean;

	constructor(userSession: UserSessionContext, suggestion?: Api.IContentCalendarSuggestion) {
		super(userSession);

		makeObservable<
			ContentCalenderSuggestionViewModel,
			'mSuggestion' | 'mTemplate' | 'rejected' | 'mMatchingAccountTags'
		>(this, {
			mSuggestion: observable.ref,
			mTemplate: observable.ref,
			rejected: observable.ref,
			mMatchingAccountTags: observable.ref,
			userSelectedNewTags: observable.ref,
			id: computed,
			template: computed,
			accountAges: computed,
			archivedDate: computed,
			isArchived: computed,
			filter: computed,
			industries: computed,
			minimumDurationInDays: computed,
			subverticals: computed,
			schedule: computed,
			targets: computed,
			isLoaded: override,
			isRejected: computed,
			canEdit: computed,
			filterCriteriaTags: computed,
			accountTagsMatchingFilterCriteriaTags: computed,
			load: override,
			reject: action,
		});

		this.mSuggestion = suggestion;
	}

	public get id() {
		return this.mSuggestion?.id;
	}

	public get template() {
		return this.mTemplate;
	}

	public get accountAges() {
		return this.mSuggestion?.accountAges;
	}

	public get archivedDate() {
		return this.mSuggestion?.archivedDate;
	}

	public get isArchived() {
		return this.mSuggestion?.archived;
	}

	public get filter() {
		return this.mSuggestion?.filter;
	}

	public get industries() {
		return this.mSuggestion?.industries;
	}

	public get minimumDurationInDays() {
		return this.mSuggestion?.minimumDurationInDays;
	}

	public get subverticals() {
		return this.mSuggestion?.subverticals;
	}

	public get schedule() {
		return this.mSuggestion?.schedule;
	}

	public get targets() {
		return this.mSuggestion?.targets;
	}

	public get isLoaded() {
		return !!this.mTemplate;
	}

	public get isRejected() {
		return this.rejected;
	}

	public get canEdit() {
		return (
			!this.impersonationContext?.account ||
			(this.impersonationContext?.account &&
				(this.userSession?.user?.role === 'admin' || this.userSession?.user?.role === 'superAdmin'))
		);
	}

	public get filterCriteriaTags() {
		const sorted = VmUtils.sortContactFilterCriteria(this.mSuggestion?.filter.criteria);
		return sorted.searches
			.filter(x => x.property === Api.ContactFilterCriteriaProperty.Tag && x.value)
			.map(x => x.value);
	}

	public get accountTagsMatchingFilterCriteriaTags() {
		return this.mMatchingAccountTags;
	}

	public toJs = () => {
		return this.mSuggestion;
	};

	public async load() {
		const templates = new TemplatesViewModel(this.userSession).impersonate(this.impersonationContext);
		await templates.getById(this.mSuggestion.templateReference.templateId).then(response => {
			this.mTemplate = response;
		});

		// load and match account tags
		if (this.filterCriteriaTags.length) {
			const tagsToSearchFor = this.filterCriteriaTags;
			const searchResults = await Promise.all(
				tagsToSearchFor.map(x => {
					const params: Api.IDictionary<string> = {
						query: x,
					};
					return this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccountTag[]>(
						this.composeApiUrl({ queryParams: params, urlPath: 'tag/search' }),
						'GET'
					);
				})
			);
			this.mMatchingAccountTags = searchResults
				.map((x, i) => {
					if (x.success) {
						const match = x.value.find(y => y.name.toLocaleLowerCase() === tagsToSearchFor[i].toLocaleLowerCase());
						if (match) {
							return match;
						}
					}
				})
				.filter(x => x);
		} else {
			this.mMatchingAccountTags = [];
		}
	}

	public reject() {
		this.busy = true;
		const promise = new Promise<Api.IOperationResult<Api.IContentCalendarSuggestion>>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<Api.IContentCalendarSuggestion>) => {
				this.busy = false;
				if (opResult.success) {
					resolve(opResult);
					this.rejected = true;
				} else {
					reject(opResult);
					this.rejected = false;
				}
			});

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContentCalendarSuggestion>(
				this.composeApiUrl({ urlPath: `ContentCalendarSuggestion/${this.mSuggestion.id}/reject` }),
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	}
}

export interface IRestoreKeyFactsRequest {
	startDate?: Date;
	endDate?: Date;
	keyDateKind?: Api.KeyDateKind;
	suppressReason?: string;
}
